import {
  CLAUDE_DEFAULT_MODEL,
  DEFAULT_ANALYSIS_MODEL,
  DEFAULT_BLOG_MODEL,
  DEFAULT_IMAGE_MODEL,
  DEFAULT_POST_COPY_MODEL,
  DEFAULT_VARIANT_MODEL,
  DEFAULT_VIDEO_MODEL,
  GEMINI_VISION_MODEL,
  OPENAI_FAST_MODEL,
  OPENAI_IMAGE_MODEL,
  OPENAI_LATEST_MODEL,
} from "./modelCatalog";
import { getPromptBlock } from "./promptStore";
import {
  buildAnalyzeKey,
  CachedAnalyzeFailureError,
  getAnalyzeFailure,
  recordAnalyzeFailure,
} from "./aiFailureCache";
import {
  TASK_KIND,
  finishTask,
  startTask,
} from "../stores/statusStore";
import {
  getCachedAnalysis,
  hashFile,
  PROMPT_VERSION,
  putCachedAnalysis,
  saveCreativeScore,
} from "./analysisCache";

const MAX_VARIANT_QUANTITY = 20;
const GEMINI_TEXT_FALLBACK_ORDER = [
  "gemini-2.5-pro",
  "gemini-2.5-flash",
  "gemini-2.5-flash-preview",
  "gemini-3-pro-preview",
  "gemini-3-flash-preview",
  "gemini-3-flash",
];
const GEMINI_IMAGE_FALLBACK_ORDER = [
  "gemini-2.5-flash-image-preview",
  "gemini-2.5-flash-image",
  "gemini-3-pro-image-preview",
];
const GEMINI_MODEL_ALIAS = {
  "gemini-3-pro": "gemini-3-pro-preview",
  "gemini-3-pro-image": "gemini-3-pro-image-preview",
};
const MAX_VIDEO_IMPORT_SEGMENTS = 12;
const MAX_VIDEO_FRAME_DIMENSION = 640;
const MANMADE_BOOK_BRAIN_ADDON = {
  knowledge_layer_addon: {
    version: "1.0",
    name: "manmade_book_brain_addon",
    purpose:
      "Additional strategic context for generating high-converting ManMade creatives using principles extracted from supplied books, PDFs, transcripts, and internal ad analysis.",
    core_doctrine: {
      primary_rule:
        "One creative should communicate one core idea, one belief shift, and one obvious visual proof.",
      market_view:
        "Do not create desire from scratch. Identify and channel desire, fear, frustration, embarrassment, aspiration, and identity that already exist in the market.",
      creative_view:
        "The best ads feel like compressed thoughts, realizations, truths, jokes, or observations rather than obvious ads.",
      conversion_view:
        "Creative should increase perceived value, emotional relevance, and clarity faster than it increases explanation.",
      brand_view:
        "The goal is not to look like a dropshipping ad. The goal is to look like brand advertising that converts.",
    },
    book_derived_principles: {
      breakthrough_advertising: {
        principles: [
          "People do not fundamentally change; only the direction and expression of their desires change.",
          "Advertising is strongest when it channels existing mass desire into a specific product.",
          "The role of the marketer is to intensify the market's awareness of the problem and attach the solution to the product.",
          "Strong creative should not invent demand where none exists; it should redirect and sharpen demand already present.",
        ],
        application_to_manmade: [
          "Assume the customer already wants to feel clean, safe, confident, and sexually acceptable.",
          "Assume the customer already fears irritation, odor, visible bumps, cuts, and embarrassment.",
          "Creative should surface and sharpen those feelings, then visually connect ManMade as the elegant resolution.",
        ],
      },
      ogilvy: {
        principles: [
          "Strong ads are built around a single idea, not multiple scattered claims.",
          "Visuals should do the heavy lifting whenever possible.",
          "Clarity beats clutter.",
          "The ad should be understandable quickly and sell through structure, not noise.",
        ],
        application_to_manmade: [
          "Do not stack five benefits into one static unless one is clearly dominant.",
          "Use product, skin texture, contrast, and composition as proof.",
          "Treat headlines as interpreters of the visual, not essays.",
        ],
      },
      sixteen_word_sales_letter: {
        principles: [
          "Each piece of copy should move one belief.",
          "Proof is essential.",
          "The prospect should be guided into a logical internal conclusion, not beaten over the head with explanation.",
        ],
        application_to_manmade: [
          "Every ad should answer one belief question such as 'Is this safer?', 'Is this for me?', 'Does this solve the embarrassing part?', or 'Is this smarter than my current method?'",
          "Use proof through tactile product realism, visible irritation contrast, clean outcomes, ratings, packshot legitimacy, and obvious use-case symbolism.",
        ],
      },
      hormozi_offers_and_leads: {
        principles: [
          "Business rewards bold experimentation and repeated testing.",
          "Skill is the difference between gambling and becoming the house.",
          "The offer is where the transaction begins.",
          "Advertising exists to make the offer known to the right people and produce more opportunities to sell.",
        ],
        application_to_manmade: [
          "Creative generation should produce multiple testable angles, not single precious ideas.",
          "Ads should strengthen the offer by making the product feel more specific, more valuable, safer, smarter, or more identity-aligned.",
          "Every concept should have angle expansion potential into variants and campaigns.",
        ],
      },
      sell_like_crazy: {
        principles: [
          "Revenue problems are usually persuasion and marketing problems.",
          "Attention must be captured before selling can happen.",
          "A strong system is fast, direct, scalable, and based on tested persuasion rather than hacks.",
        ],
        application_to_manmade: [
          "Open with the thing that grabs the buyer by the throat emotionally: itch, bumps, redness, odor anxiety, sexual self-consciousness, or fear of getting cut again.",
          "Then simplify the path to belief and action.",
        ],
      },
      social_engineering: {
        principles: [
          "People respond strongly when they participate in meaning-making.",
          "Framing, tension, curiosity, and guided conclusions increase compliance and engagement.",
        ],
        application_to_manmade: [
          "Ads should often leave one small gap for the viewer to complete.",
          "Use visual setups where the viewer 'gets it' a half-second after seeing it.",
        ],
      },
      forty_eight_laws_of_power: {
        principles: [
          "Indirect persuasion is often stronger than direct force.",
          "Saying less can increase perceived control and power.",
          "Mystique, contrast, and suggestion can outperform overt explanation.",
        ],
        application_to_manmade: [
          "Do not oversell with loud desperation.",
          "Let some concepts pull rather than push.",
        ],
      },
    },
    customer_psychology_overlay: {
      primary_emotional_drivers: [
        "confidence",
        "masculinity",
        "control",
        "cleanliness",
        "partner approval",
        "sexual readiness",
        "relief",
        "embarrassment avoidance",
        "pain avoidance",
      ],
      primary_fears: [
        "cutting sensitive skin again",
        "visible razor burn or bumps",
        "odor and bacteria buildup",
        "looking bad during intimacy",
        "feeling out of control with grooming",
        "buying another overhyped product that fails",
      ],
      primary_desires: [
        "look put together without looking overgroomed",
        "feel safe grooming sensitive areas",
        "remove bacne, buildup, odor, and irritation",
        "feel presentable and desirable",
        "use a tool that feels purpose-built instead of improvised",
      ],
      core_identity_translation: [
        "A better tool is not enough. It must feel like the smarter male choice.",
        "The customer wants competence, not complication.",
        "The customer wants relief without feeling less masculine.",
      ],
    },
    creative_generation_rules: {
      mandatory: [
        "One creative = one dominant thought.",
        "Use contrast in every concept.",
        "Image carries proof, text carries interpretation.",
        "Make the idea understandable in 1 to 2 seconds.",
        "Show more than you tell.",
        "Do not create generic ecommerce ads.",
        "Do not rely on feature dumps.",
        "Avoid ad-looking ads whenever possible.",
      ],
      preferred_mechanics: [
        "pattern interruption",
        "common_phrase_flip",
        "visual metaphor",
        "social tension",
        "disgust to relief transition",
        "identity-coded truth",
        "participation gap",
        "cause and effect compression",
        "inside-joke familiarity",
        "unspoken truth made visual",
      ],
      shareability_rules: [
        "The ad should ideally feel sendable to a friend.",
        "The viewer should feel clever for understanding it.",
        "Humor, truth, or sharp recognition should be possible without destroying clarity.",
      ],
      resistance_reduction_rules: [
        "Do not always lead with CTA language.",
        "Do not overexplain the product if the visual already proves the point.",
        "Allow some creatives to operate as attraction pieces rather than hard sell pieces.",
      ],
    },
    static_ad_frameworks: [
      {
        name: "common_phrase_flip",
        formula:
          "Take a phrase the audience already knows, reinterpret it through the product, and use the visual as the payoff.",
      },
      {
        name: "pain_vs_relief",
        formula:
          "Show the cringe-inducing problem and the clean resolution in a highly compressed contrast.",
      },
      {
        name: "truth_they_already_feel",
        formula:
          "State an internal thought the customer already has but rarely says out loud.",
      },
      {
        name: "symbolic_product",
        formula:
          "Turn the product into a symbol of control, barrier, safety, relief, or readiness.",
      },
      {
        name: "participation_gap",
        formula:
          "Create a visual or verbal setup that makes the viewer complete one step of the persuasion themselves.",
      },
      {
        name: "identity_lock",
        formula:
          "Tie the product to being prepared, in control, sharp, clean, or sexually confident.",
      },
    ],
    headline_rules: {
      preferred_length: "2 to 6 words",
      style: [
        "compressed",
        "punchy",
        "interpretable instantly",
        "truthful or intriguingly indirect",
        "masculine without trying too hard",
      ],
      avoid: [
        "generic benefit vomit",
        "fake hype",
        "obvious dropship phrasing",
        "overwritten cleverness",
        "long educational headlines unless specifically requested",
      ],
    },
    visual_rules: {
      product_rendering: [
        "premium",
        "tactile",
        "materially believable",
        "realistic texture",
        "clear silhouette",
        "not plastic-looking",
      ],
      skin_rendering: [
        "realistic",
        "controlled texture",
        "believable redness or irritation when shown",
        "not over-gross unless explicitly pursuing disgust-based interruption",
      ],
      composition: [
        "clear focal hierarchy",
        "minimal clutter",
        "symmetry or intentional imbalance only",
        "obvious eye path",
        "mobile-first readability",
      ],
      lighting_logic: {
        pain: "harder, harsher, more directional",
        clinical: "clean, high clarity, neutral premium",
        relief: "brighter, softer, cleaner",
        identity: "premium contrast, deliberate, controlled",
      },
    },
    offer_and_merchandising_integration: {
      principles: [
        "Creative should support AOV, bundle logic, and perceived value.",
        "If proof cues are used, they should strengthen trust quickly.",
        "If price appears, it should be structurally subordinate to the emotional and visual hook unless the format is intentionally price-led.",
      ],
      preferred_proof_elements: [
        "rating bars",
        "derm-tested or trust cues when relevant",
        "packshot legitimacy",
        "logo repetition",
        "visible use-case proof",
        "before-and-after implication",
      ],
    },
    meta_platform_fit: {
      rules: [
        "Assume 1 to 3 second attention windows.",
        "Build vertical visual hierarchy.",
        "Hook before explanation.",
        "Readable on mobile without zoom.",
        "Test multiple variants aggressively.",
      ],
    },
    idea_quality_filter: [
      "Would this still make sense if the user sees it for 1 second?",
      "Is the concept built around one dominant tension?",
      "Would the customer recognize themselves in it?",
      "Does the visual prove something, not just decorate?",
      "Is this specific to ManMade, not usable by any random brand?",
      "Does it feel like a thought, not a template?",
    ],
    do_not_generate: [
      "generic blue-arrow dropshipping graphics",
      "feature-stack statics with weak hierarchy",
      "cheap fake urgency visuals unless explicitly requested",
      "concepts that require long reading to understand",
      "safe but forgettable brand filler",
      "ads that prioritize prettiness over interruption or clarity",
    ],
    success_definition: {
      winning_creative:
        "A creative that compresses one emotionally relevant truth into a visually obvious, instantly processable, brand-specific idea that feels native enough to stop scroll and strong enough to test into scale.",
      elite_creative:
        "A creative that looks like brand advertising, carries a direct-response skeleton underneath, and creates enough recognition, tension, and proof to drive both clicks and memory.",
    },
  },
};

export default class AIService {
  constructor(apiKeys = {}) {
    this.aiProfile = apiKeys.aiProfile || null;
    this.tagProfile = apiKeys.tagProfile || null;
    this.analysisKnowledge = apiKeys.analysisKnowledge || null;
    this.adCopyKnowledge = apiKeys.adCopyKnowledge || null;
    this.blogKnowledge = apiKeys.blogKnowledge || null;
    this.blogSeo = apiKeys.blogSeo || null;
    this.blogModelOverride = apiKeys.blogModelOverride || null;
    this.generationAspectRatio = apiKeys.generationAspectRatio || null;
    this.qcModelOverride = apiKeys.qcModel || null;
    this.activityPreferences = apiKeys.activityPreferences || null;
    this.geminiImageAspectConfigSupport = null;
    this.geminiFileCache = new Map();
    this.workspaceId = apiKeys.workspaceId || null;
    this.atlascloudApiKey = apiKeys.atlascloud || null;
    this.klingApiKey = apiKeys.kling || null;
    this.serverOnly = true;
  }

  getServerBaseUrl() {
    const env = import.meta?.env || {};
    const raw = String(env.VITE_API_BASE_URL || "/api").trim();
    if (!raw) return "/api";
    return raw.replace(/\/+$/, "");
  }

  isServerAiEnabled() {
    const env = import.meta?.env || {};
    const flag = String(env.VITE_USE_SERVER_AI || "true").toLowerCase();
    return flag !== "false";
  }

  requireWorkspace() {
    if (!this.workspaceId) {
      throw new Error("Active workspace is required for AI requests.");
    }
  }

  isRetryableServerFetchError(error) {
    const message = String(error?.message || "").toLowerCase();
    return (
      error instanceof TypeError ||
      message.includes("failed to fetch") ||
      message.includes("networkerror") ||
      message.includes("network request failed") ||
      message.includes("load failed")
    );
  }

  async _refreshSessionSilently() {
    try {
      await fetch(`${this.getServerBaseUrl()}/api/auth/session`, {
        method: "GET",
        credentials: "include",
      });
    } catch {
      // best-effort only
    }
  }

  async callAiServer(action, payload = {}, { signal, suppressAuthEvent = false } = {}) {
    if (!this.isServerAiEnabled()) {
      throw new Error("Server AI is disabled.");
    }
    this.requireWorkspace();
    const requestUrl = `${this.getServerBaseUrl()}/ai/generate`;
    const requestBody = JSON.stringify({
      action,
      payload,
      workspace_id: this.workspaceId,
    });

    const doFetch = () =>
      fetch(requestUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: requestBody,
        signal,
      });

    let response = null;
    const maxAttempts = 3;
    for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
      try {
        response = await doFetch();
        break;
      } catch (error) {
        if (signal?.aborted || error?.name === "AbortError") {
          throw error;
        }
        if (attempt < maxAttempts && this.isRetryableServerFetchError(error)) {
          const waitMs = 220 * attempt + Math.floor(Math.random() * 180);
          await this.wait(waitMs, signal);
          continue;
        }
        const message = String(error?.message || "Failed to fetch");
        const wrapped = new Error(
          `Network error calling ${action} via ${requestUrl}: ${message}`,
        );
        wrapped.action = action;
        wrapped.url = requestUrl;
        wrapped.cause = error;
        throw wrapped;
      }
    }

    // On 401, try a silent session refresh and retry once before logging out.
    if (response?.status === 401 && !suppressAuthEvent) {
      await this._refreshSessionSilently();
      try {
        response = await doFetch();
      } catch {
        // If the retry fetch itself fails, fall through to the error handler below.
      }
    }

    if (!response.ok) {
      const text = await response.text();
      let parsed = null;
      try {
        parsed = text ? JSON.parse(text) : null;
      } catch (error) {
        parsed = null;
      }
      if (response.status === 401 && typeof window !== "undefined" && !suppressAuthEvent) {
        window.dispatchEvent(new CustomEvent("auth:unauthorized"));
      }
      const rawMessage =
        parsed?.error?.message ||
        parsed?.error ||
        parsed?.message ||
        parsed?.detail ||
        text ||
        "AI request failed";
      const message =
        response.status === 401
          ? "Session expired. Please sign in again."
          : rawMessage;
      const err = new Error(message);
      err.status = response.status;
      err.statusText = response.statusText;
      err.url = requestUrl;
      err.action = action;
      err.payload = parsed || text;
      throw err;
    }
    const text = await response.text();
    return text ? JSON.parse(text) : {};
  }

  async callOpenAIChat(payload, signal) {
    return this.callAiServer("openai.chat", payload, { signal });
  }

  pushTextCandidate(out, value, maxLength = 30000) {
    if (!Array.isArray(out) || value == null) return;
    let text = "";
    if (typeof value === "string") {
      text = value;
    } else if (typeof value === "number" || typeof value === "boolean") {
      text = String(value);
    } else if (value && typeof value === "object") {
      try {
        text = JSON.stringify(value);
      } catch (error) {
        text = "";
      }
    }
    const trimmed = String(text || "").trim();
    if (trimmed) {
      out.push(trimmed.slice(0, maxLength));
    }
  }

  collectTextCandidates(value, out, depth = 0) {
    if (value == null || !Array.isArray(out) || depth > 5) return;
    if (typeof value === "string") {
      this.pushTextCandidate(out, value);
      return;
    }
    if (Array.isArray(value)) {
      value.forEach((entry) =>
        this.collectTextCandidates(entry, out, depth + 1),
      );
      return;
    }
    if (typeof value !== "object") {
      return;
    }

    if (typeof value.text === "string") {
      this.pushTextCandidate(out, value.text);
    } else if (value.text && typeof value.text === "object") {
      this.collectTextCandidates(value.text, out, depth + 1);
    }

    if (typeof value.content === "string") {
      this.pushTextCandidate(out, value.content);
    } else if (value.content && typeof value.content === "object") {
      this.collectTextCandidates(value.content, out, depth + 1);
    }

    if (typeof value.value === "string") {
      this.pushTextCandidate(out, value.value);
    }

    if (typeof value.output_text === "string") {
      this.pushTextCandidate(out, value.output_text);
    }

    if (Array.isArray(value.parts)) {
      value.parts.forEach((entry) =>
        this.collectTextCandidates(entry, out, depth + 1),
      );
    }
  }

  getOpenAIChatContentCandidatesOrThrow(response) {
    const candidates = [];
    const choices = Array.isArray(response?.choices) ? response.choices : [];
    let refusal = "";
    let finishReason = "";

    choices.forEach((choice) => {
      if (!finishReason && choice?.finish_reason) {
        finishReason = String(choice.finish_reason).trim();
      }
      const message = choice?.message || {};
      if (
        !refusal &&
        typeof message?.refusal === "string" &&
        message.refusal.trim()
      ) {
        refusal = message.refusal.trim();
      }
      if (message?.parsed && typeof message.parsed === "object") {
        this.pushTextCandidate(candidates, message.parsed);
      }
      this.collectTextCandidates(message?.content, candidates);

      const functionArgs = message?.function_call?.arguments;
      if (
        typeof functionArgs === "string" ||
        (functionArgs && typeof functionArgs === "object")
      ) {
        this.pushTextCandidate(candidates, functionArgs);
      }

      const toolCalls = Array.isArray(message?.tool_calls)
        ? message.tool_calls
        : [];
      toolCalls.forEach((toolCall) => {
        const toolArgs = toolCall?.function?.arguments;
        if (
          typeof toolArgs === "string" ||
          (toolArgs && typeof toolArgs === "object")
        ) {
          this.pushTextCandidate(candidates, toolArgs);
        }
      });
    });

    const unique = [];
    const seen = new Set();
    candidates.forEach((candidate) => {
      const key = String(candidate || "").trim();
      if (!key || seen.has(key)) return;
      seen.add(key);
      unique.push(key);
    });

    if (unique.length) {
      return unique;
    }

    if (refusal) {
      const error = new Error(`Model refused request: ${refusal}`);
      error.modelContent = refusal;
      error.finishReason = finishReason;
      throw error;
    }

    if (finishReason === "length") {
      const error = new Error("Incomplete JSON in model response");
      error.finishReason = finishReason;
      throw error;
    }

    if (finishReason === "content_filter") {
      const error = new Error(
        "Model response was blocked by content filtering",
      );
      error.finishReason = finishReason;
      throw error;
    }

    const error = new Error("Empty response from model");
    error.finishReason = finishReason;
    throw error;
  }

  extractJSONFromCandidates(contentCandidates) {
    const candidates = Array.isArray(contentCandidates)
      ? contentCandidates
      : [contentCandidates];
    let lastError = null;

    for (const candidate of candidates) {
      try {
        return this.extractJSON(candidate);
      } catch (error) {
        error.modelContent = candidate;
        lastError = error;
      }
    }

    if (lastError) {
      throw lastError;
    }

    const error = new Error("Empty response from model");
    error.modelContent = "";
    throw error;
  }

  async callOpenAIImages(payload, signal, { suppressAuthEvent = false } = {}) {
    return this.callAiServer("openai.images", payload, { signal, suppressAuthEvent });
  }

  // Regular chat, one brand-new conversation per call (the server forces
  // store:false and strips previous_response_id, so nothing carries over
  // between generations).
  async callOpenAIResponses(payload, signal) {
    return this.callAiServer("openai.responses", payload, { signal });
  }

  async callOpenAIVideosCreate(payload, signal) {
    return this.callAiServer("openai.videos.create", payload, { signal });
  }

  async callOpenAIVideosStatus(payload, signal) {
    return this.callAiServer("openai.videos.status", payload, { signal });
  }

  async callOpenAIVideosContent(payload, signal) {
    return this.callAiServer("openai.videos.content", payload, { signal });
  }

  async callGeminiGenerate(model, body, signal) {
    const requestedModel = this.normalizeGeminiModelName(model);
    const fallbackChain = this.buildGeminiFallbackChain(requestedModel, body);
    let lastError = null;
    for (let index = 0; index < fallbackChain.length; index += 1) {
      const candidateModel = fallbackChain[index];
      let candidateError = null;
      if (index > 0) {
        console.warn(
          `[AIService] Gemini fallback: ${requestedModel} -> ${candidateModel}`,
        );
      }
      const maxAttempts = 3;
      for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
        try {
          return await this.callAiServer(
            "gemini.generateContent",
            { model: candidateModel, body },
            { signal },
          );
        } catch (error) {
          candidateError = error;
          const retryableSameModel = this.isGeminiTransientError(error);
          if (attempt < maxAttempts && retryableSameModel) {
            const jitterMs = Math.floor(Math.random() * 180);
            const waitMs = 220 * attempt + jitterMs;
            await this.wait(waitMs, signal);
            continue;
          }
          break;
        }
      }
      lastError = candidateError || lastError;
      const canTryNext = index < fallbackChain.length - 1;
      const shouldFallback =
        this.isGeminiTransientError(candidateError) ||
        this.isGeminiModelUnavailableError(candidateError);
      if (canTryNext && shouldFallback) {
        continue;
      }
      throw candidateError || new Error("Gemini request failed");
    }
    throw lastError || new Error("Gemini request failed");
  }

  async callGeminiPredictLongRunning(model, body, signal) {
    return this.callAiServer(
      "gemini.predictLongRunning",
      { model, body },
      { signal },
    );
  }

  async callGeminiOperation(name, signal) {
    return this.callAiServer("gemini.operation", { name }, { signal });
  }

  async callGeminiUpload(payload, signal) {
    return this.callAiServer("gemini.upload", payload, { signal });
  }

  async callGeminiFile(payload, signal) {
    return this.callAiServer("gemini.file", payload, { signal });
  }

  async uploadGeminiUrl(url, mimeType, name, signal) {
    if (!url) {
      throw new Error("Gemini upload URL missing");
    }
    const result = await this.callGeminiUpload(
      {
        name: name || "reference-image",
        mimeType: mimeType || "application/octet-stream",
        url,
      },
      signal,
    );
    const fileInfo = result?.file || result;
    return {
      name: fileInfo?.name || name || "reference-image",
      uri: fileInfo?.uri || "",
      mimeType: fileInfo?.mimeType || mimeType || "application/octet-stream",
    };
  }

  async uploadWorkspaceAssetForGemini(file, mimeType, signal) {
    this.requireWorkspace();
    const base = this.getServerBaseUrl();
    const id = `gemini-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    const resolvedMimeType =
      mimeType || file?.type || "application/octet-stream";
    const readErrorText = async (response) => {
      try {
        return String((await response.text()) || "").slice(0, 400);
      } catch (error) {
        return "";
      }
    };
    const response = await fetch(`${base}/assets/signed-upload`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({
        workspace_id: this.workspaceId,
        category: "analysis_uploads",
        id,
        name: file?.name || "upload",
        mimeType: resolvedMimeType,
      }),
      signal,
    });
    if (!response.ok) {
      const text = await readErrorText(response);
      const message = text || "Failed to request signed upload";
      const err = new Error(message);
      err.status = response.status;
      throw err;
    }
    const payload = await response.json();
    const signedUrl = payload?.signedUrl || "";
    if (!signedUrl) {
      throw new Error("Signed upload URL missing");
    }
    let uploadResponse;
    try {
      uploadResponse = await fetch(signedUrl, {
        method: "PUT",
        headers: {
          "Content-Type": resolvedMimeType,
        },
        body: file,
        signal,
      });
    } catch (error) {
      const uploadError = new Error(
        `Failed to upload file (network: ${String(
          error?.message || error,
        ).slice(0, 200)})`,
      );
      uploadError.status = 0;
      throw uploadError;
    }
    if (!uploadResponse.ok) {
      const uploadText = await readErrorText(uploadResponse);
      const detail = uploadText ? ` ${uploadText}` : "";
      const err = new Error(
        `Failed to upload file (${uploadResponse.status} ${uploadResponse.statusText} -${detail})`,
      );
      err.status = uploadResponse.status;
      throw err;
    }
    const signedRead = await fetch(
      `${base}/assets/signed?path=${encodeURIComponent(payload?.path || "")}`,
      { credentials: "include", signal },
    );
    if (!signedRead.ok) {
      const text = await readErrorText(signedRead);
      const message = text || "Failed to fetch signed url";
      const err = new Error(message);
      err.status = signedRead.status;
      throw err;
    }
    const signedPayload = await signedRead.json();
    return {
      path: payload?.path || "",
      url: signedPayload?.url || "",
    };
  }

  async callClaudeMessages(payload, signal) {
    return this.callAiServer("anthropic.messages", payload, { signal });
  }

  resolveBlogModel() {
    return this.blogModelOverride || DEFAULT_BLOG_MODEL;
  }

  async generateTextWithModel(
    modelName,
    prompt,
    { temperature = 0.3, maxTokens = 800, signal } = {},
  ) {
    const model = String(modelName || "").trim() || DEFAULT_BLOG_MODEL;
    if (model.startsWith("gpt")) {
      const response = await this.callOpenAIChat(
        {
          model,
          messages: [{ role: "user", content: prompt }],
          temperature,
          max_completion_tokens: maxTokens,
        },
        signal,
      );
      const contentCandidates =
        this.getOpenAIChatContentCandidatesOrThrow(response);
      return contentCandidates[0] || "";
    }
    if (model.startsWith("claude")) {
      return this.callClaude({
        model,
        max_tokens: maxTokens,
        temperature,
        messages: [
          {
            role: "user",
            content: [{ type: "text", text: prompt }],
          },
        ],
      });
    }
    const data = await this.callGeminiGenerate(
      model,
      {
        contents: [{ parts: [{ text: prompt }] }],
        generationConfig: {
          temperature,
          maxOutputTokens: maxTokens,
        },
      },
      signal,
    );
    return (
      data?.candidates?.[0]?.content?.parts
        ?.map((part) => part.text)
        .filter(Boolean)
        .join("\n") || ""
    );
  }

  async base64ToBlob(base64, mimeType = "application/octet-stream") {
    const binary = atob(base64 || "");
    const length = binary.length;
    const bytes = new Uint8Array(length);
    for (let i = 0; i < length; i += 1) {
      bytes[i] = binary.charCodeAt(i);
    }
    return new Blob([bytes], { type: mimeType || "application/octet-stream" });
  }

  parseDataUrl(dataUrl) {
    const match = String(dataUrl || "").match(/^data:([^;]+);base64,(.*)$/);
    if (!match) return null;
    return { mimeType: match[1], base64: match[2] };
  }

  async fetchMediaBlob(mediaUrl) {
    if (!mediaUrl) {
      throw new Error("Media URL missing");
    }
    const parsed = this.parseDataUrl(mediaUrl);
    if (parsed?.base64) {
      const blob = await this.base64ToBlob(parsed.base64, parsed.mimeType);
      return { blob, mimeType: parsed.mimeType || blob.type };
    }
    let response = null;
    try {
      response = await fetch(mediaUrl);
    } catch (error) {
      if (String(mediaUrl).startsWith("blob:")) {
        throw new Error(
          "Generated media is no longer available in browser memory. Regenerate it or analyze it before refreshing.",
        );
      }
      throw error;
    }
    if (!response.ok) {
      throw new Error("Failed to load media");
    }
    const blob = await response.blob();
    return { blob, mimeType: blob.type };
  }

  shouldUseServerAI() {
    return this.isServerAiEnabled();
  }

  async parseOpenAIError(response, fallback) {
    try {
      const text = await response.text();
      if (!text) return fallback;
      try {
        const parsed = JSON.parse(text);
        const message =
          parsed?.error?.message || parsed?.message || parsed?.error || text;
        return typeof message === "string" ? message : JSON.stringify(message);
      } catch (error) {
        return text;
      }
    } catch (error) {
      return fallback;
    }
  }

  async analyzeCreative(file, isVideo, onProgress, signal, options = {}) {
    const { model, creative_id: creativeId, ad_id: adId } = options;
    const dedupeKey = buildAnalyzeKey(file, model);
    const cachedFailure = getAnalyzeFailure(dedupeKey);
    if (cachedFailure) {
      throw new CachedAnalyzeFailureError(cachedFailure);
    }
    const taskId = `analyze:${dedupeKey}:${Date.now()}`;
    startTask(taskId, {
      kind: isVideo ? TASK_KIND.ANALYSIS : TASK_KIND.ANALYSIS,
      label: file?.name || (isVideo ? "video creative" : "image creative"),
    });

    // DB-backed cache: short-circuit if we've already analyzed this exact
    // file with this exact model. Keyed on sha256(file bytes) so the cache
    // is stable across sessions and across users in the same workspace.
    let contentHash = null;
    if (this.workspaceId && model) {
      contentHash = await hashFile(file);
      if (contentHash) {
        try {
          const cached = await getCachedAnalysis({
            workspaceId: this.workspaceId,
            contentHash,
            model,
            creativeId,
            adId,
          });
          if (cached?.analysis) {
            finishTask(taskId, "done");
            return cached.analysis;
          }
        } catch (error) {
          // Cache miss is non-fatal; fall through to live analysis.
        }
      }
    }

    // Lazy-load the workspace's KB chunks so buildAiGuidance can cite them.
    // Topic hint = file name; better topic data lands once tags are persisted.
    this.lastAnalyzeTopic = file?.name || "";
    if (this.workspaceId) {
      await this.loadActiveKnowledge();
    }

    try {
      let result;
      if (isVideo) {
        result = await this.analyzeVideoFrames(file, onProgress, signal);
      } else if (model && model.startsWith("gemini")) {
        const isRetryableGeminiFileError = (error) => {
          const message = String(error?.message || "").toLowerCase();
          return (
            message.includes("failed to create file") ||
            message.includes("gemini upload failed after retries")
          );
        };
        const maxAttempts = 2;
        let lastError = null;
        for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
          try {
            result = await this.analyzeImageWithGemini(file, signal, model);
            lastError = null;
            break;
          } catch (error) {
            lastError = error;
            if (!isRetryableGeminiFileError(error) || attempt >= maxAttempts) {
              break;
            }
            await this.wait(450 * attempt, signal);
          }
        }
        if (lastError) throw lastError;
      } else if (model && model.startsWith("claude")) {
        result = await this.analyzeImageWithClaude(file, signal, model);
      } else {
        result = await this.analyzeImageWithGPT(file, signal, model);
      }
      finishTask(taskId, "done");

      // Persist the successful analysis so subsequent loads short-circuit.
      if (this.workspaceId && model && contentHash) {
        const conceptLabel =
          result?.concept_label ||
          result?.combined_context?.concept_label ||
          null;
        const confidence =
          typeof result?.confidence === "number" ? result.confidence : null;
        const citations = Array.isArray(result?.citations)
          ? result.citations
          : Array.isArray(result?.combined_context?.citations)
            ? result.combined_context.citations
            : [];
        const transcript =
          result?.transcript ||
          result?.combined_context?.transcript ||
          null;
        try {
          await putCachedAnalysis({
            workspaceId: this.workspaceId,
            contentHash,
            model,
            mediaKind: isVideo ? "video" : "image",
            analysis: result,
            creativeId,
            adId,
            promptVersion: PROMPT_VERSION,
            conceptLabel,
            confidence,
            citations,
            transcript,
          });
        } catch (error) {
          // Persisting is best-effort; the analysis still returns to the caller.
        }
      }

      return result;
    } catch (error) {
      if (signal?.aborted || error?.name === "AbortError") {
        finishTask(taskId, "done");
        throw error;
      }
      recordAnalyzeFailure(dedupeKey, error);
      finishTask(taskId, "error", { error: String(error?.message || error) });
      throw error;
    }
  }

  getAiProfile() {
    if (this.aiProfile) return this.aiProfile;
    return {
      instructions: "",
      personality: "",
      mustDo: "",
      avoid: "",
      referenceUrls: [],
    };
  }

  getTagProfile() {
    if (Array.isArray(this.tagProfile)) {
      return this.tagProfile
        .map((tag) => ({
          name: tag.name || "",
          description: tag.description || "",
        }))
        .filter((tag) => tag.name);
    }
    return [];
  }

  async loadActiveKnowledge() {
    if (!this.workspaceId) {
      this.kbChunks = [];
      return [];
    }
    if (Array.isArray(this.kbChunks) && this.kbChunks.length) {
      return this.kbChunks;
    }
    try {
      const url = `${this.getServerBaseUrl()}/knowledge/chunks?workspace_id=${encodeURIComponent(this.workspaceId)}`;
      const response = await fetch(url, {
        method: "GET",
        credentials: "include",
      });
      if (!response.ok) {
        this.kbChunks = [];
        return [];
      }
      const text = await response.text();
      const data = text ? JSON.parse(text) : {};
      this.kbChunks = Array.isArray(data?.rows) ? data.rows : [];
      return this.kbChunks;
    } catch (error) {
      this.kbChunks = [];
      return [];
    }
  }

  buildKnowledgeContext({ topic = "", maxTokens = 6000 } = {}) {
    const chunks = Array.isArray(this.kbChunks) ? this.kbChunks : [];
    if (!chunks.length) return "";
    const term = String(topic || "").toLowerCase();
    const scored = chunks.map((chunk) => {
      const haystack = `${chunk.source_title || ""} ${chunk.body || ""}`.toLowerCase();
      let score = 0;
      if (term) {
        term
          .split(/\s+/)
          .filter(Boolean)
          .forEach((token) => {
            if (token.length < 3) return;
            if (haystack.includes(token)) score += 1;
          });
      }
      return { chunk, score };
    });
    scored.sort((a, b) => b.score - a.score);
    const selected = [];
    let total = 0;
    for (const { chunk } of scored) {
      const tokens = Number(chunk.tokens || 0) || Math.ceil((chunk.body || "").length / 4);
      if (total + tokens > maxTokens) continue;
      selected.push(chunk);
      total += tokens;
    }
    if (!selected.length) return "";
    const lines = selected.map((c, idx) => {
      const head = `[${idx + 1}] ${c.source_title}${c.source_locator ? `, ${c.source_locator}` : ""}`;
      return `${head}\n"${c.body.replace(/\s+/g, " ").trim()}"`;
    });
    return [
      "",
      "Knowledge sources available — cite inline as [KB: <source_title>, <source_locator>].",
      "Return a `citations` array on the JSON response with {source, locator, claim} entries when you use them.",
      "<<SOURCES>>",
      lines.join("\n"),
      "<<END>>",
    ].join("\n");
  }

  buildAiGuidance() {
    const profile = this.getAiProfile() || {};
    const lines = [];
    // Only inject things the user has explicitly configured. No hardcoded
    // reading-level or token-efficiency rules — those should live in the
    // Prompt Library if the user wants them.
    if (profile.instructions) {
      lines.push(
        `Custom instructions: ${this.compactText(profile.instructions, 220)}`,
      );
    }
    if (profile.personality) {
      lines.push(
        `Personality & tone: ${this.compactText(profile.personality, 140)}`,
      );
    }
    if (profile.mustDo) {
      lines.push(`Must-do: ${this.compactText(profile.mustDo, 160)}`);
    }
    if (profile.avoid) {
      lines.push(`Avoid: ${this.compactText(profile.avoid, 160)}`);
    }
    const kbBlock = this.buildKnowledgeContext({
      topic: this.lastAnalyzeTopic || "",
    });
    if (lines.length === 0 && !kbBlock) return "";
    const userPrefs = lines.length ? `\n\nUser preferences:\n${lines.join("\n")}\n` : "";
    return `${userPrefs}${kbBlock}`;
  }

  buildActivityGuidance(type) {
    const prefs = Array.isArray(this.activityPreferences)
      ? this.activityPreferences
      : [];
    if (!prefs.length) return "";
    const filtered = prefs.filter((item) => item?.type === type);
    if (!filtered.length) return "";
    const summarizeCopy = (copy) => {
      if (!copy || typeof copy !== "object") return "";
      if (
        copy.caption ||
        Array.isArray(copy.hashtagBundles) ||
        Array.isArray(copy.hashtags)
      ) {
        return this.compactText(
          [
            String(copy.caption || "").trim(),
            ...(Array.isArray(copy.hashtagBundles)
              ? copy.hashtagBundles
              : Array.isArray(copy.hashtags)
                ? copy.hashtags
                : []),
          ]
            .map((val) => String(val || "").trim())
            .filter(Boolean)
            .join(" | "),
          140,
        );
      }
      if (Array.isArray(copy.headlines) || Array.isArray(copy.primaryTexts)) {
        const headline = Array.isArray(copy.headlines)
          ? String(copy.headlines[0] || "").trim()
          : "";
        const primary = Array.isArray(copy.primaryTexts)
          ? String(copy.primaryTexts[0] || "").trim()
          : "";
        return this.compactText(
          [headline, primary].filter(Boolean).join(" | "),
          140,
        );
      }
      if (copy.title || copy.subtitle || copy.body) {
        return this.compactText(
          [copy.title, copy.subtitle, copy.cta]
            .map((val) => String(val || "").trim())
            .filter(Boolean)
            .join(" | "),
          160,
        );
      }
      if (copy.headline || copy.story || copy.deck) {
        return this.compactText(
          [copy.headline, copy.deck, copy.cta]
            .map((val) => String(val || "").trim())
            .filter(Boolean)
            .join(" | "),
          160,
        );
      }
      return "";
    };
    const formatItem = (item) => {
      const channel = item?.channel ? `Channel: ${item.channel}` : "";
      const postType = item?.postType?.name
        ? `Post type: ${item.postType.name}`
        : "";
      const snippet = summarizeCopy(item?.copy || item?.output || {});
      return [channel, postType, snippet].filter(Boolean).join(" — ");
    };
    const likes = filtered
      .filter((item) => item.sentiment === "like")
      .slice(0, 2)
      .map(formatItem)
      .filter(Boolean);
    const dislikes = filtered
      .filter((item) => item.sentiment === "dislike")
      .slice(0, 2)
      .map(formatItem)
      .filter(Boolean);
    const lines = [];
    if (likes.length) {
      lines.push(`Liked examples:\n- ${likes.join("\n- ")}`);
    }
    if (dislikes.length) {
      lines.push(`Disliked examples:\n- ${dislikes.join("\n- ")}`);
    }
    if (!lines.length) return "";
    return `\n\nUser feedback (condensed):\n${lines.join("\n")}\n`;
  }

  getAnalysisKnowledge() {
    const source = this.analysisKnowledge || {};
    return {
      notes: String(source?.notes || ""),
      docs: Array.isArray(source?.docs) ? source.docs : [],
    };
  }

  buildAnalysisGuidance() {
    const { notes, docs } = this.getAnalysisKnowledge();
    const lines = [];
    const trimmedNotes = String(notes || "").trim();
    if (trimmedNotes) {
      lines.push(`Notes: ${this.compactText(trimmedNotes, 1200)}`);
    }
    const docLines = (Array.isArray(docs) ? docs : [])
      .filter((doc) => doc?.text)
      .slice(0, 4)
      .map((doc) => {
        const name = doc?.name ? String(doc.name) : "reference";
        const text = this.compactText(String(doc.text || ""), 800);
        return text ? `Doc (${name}): ${text}` : "";
      })
      .filter(Boolean);
    if (docLines.length > 0) {
      lines.push(...docLines);
    }
    if (lines.length === 0) {
      return "";
    }
    return `\n\nGeneral knowledge (key points only):\n${lines.join("\n")}\n`;
  }

  getAdCopyKnowledge() {
    const source = this.adCopyKnowledge || {};
    return {
      notes: String(source?.notes || ""),
      avoid: String(source?.avoid || ""),
      docs: Array.isArray(source?.docs) ? source.docs : [],
      headlines: Array.isArray(source?.headlines) ? source.headlines : [],
      primaryTexts: Array.isArray(source?.primaryTexts)
        ? source.primaryTexts
        : [],
    };
  }

  normalizeMetaAdsCopyPayload(parsed) {
    const source =
      parsed && typeof parsed === "object" && !Array.isArray(parsed)
        ? parsed
        : {};
    const nested =
      source?.copy && typeof source.copy === "object" ? source.copy : {};
    const toList = (value) =>
      (Array.isArray(value) ? value : value ? [value] : [])
        .map((entry) => String(entry || "").trim())
        .filter(Boolean);
    const scrub = (value) =>
      String(value || "")
        .replace(
          /^(headline|primary text|body copy|cta|implied|implicit)\s*:\s*/i,
          "",
        )
        .replace(/\b(implied|implicit)\b[:.]?\s*/gi, "")
        .replace(/\s+/g, " ")
        .trim();
    const clipPlain = (value, maxLen = 240) => {
      const text = String(value || "").trim();
      if (!text) return "";
      if (!maxLen || text.length <= maxLen) return text;
      return text.slice(0, Math.max(0, maxLen)).trim();
    };
    const simplifyVocabulary = (value) => {
      let text = String(value || "");
      const replacements = [
        [/\bsophisticated\b/gi, "simple"],
        [/\bpremium\b/gi, "top"],
        [/\bprecision-engineered\b/gi, "well-made"],
        [/\bprecision\b/gi, "exact"],
        [/\bperformance-minded\b/gi, "made to work"],
        [/\bconfidence\b/gi, "self-belief"],
        [/\boptimi[sz]e\b/gi, "improve"],
        [/\bmaximize\b/gi, "get more"],
        [/\bleverage\b/gi, "use"],
        [/\binnovative\b/gi, "new"],
      ];
      for (const [pattern, replacement] of replacements) {
        text = text.replace(pattern, replacement);
      }
      return text.replace(/\s+/g, " ").trim();
    };
    const hasTooManyLongWords = (value, maxLen = 9, limit = 2) => {
      const count = String(value || "")
        .split(/\s+/)
        .map((word) => word.replace(/[^a-z]/gi, ""))
        .filter(Boolean)
        .filter((word) => word.length > maxLen).length;
      return count > limit;
    };
    const countWords = (value) =>
      String(value || "")
        .trim()
        .split(/\s+/)
        .filter(Boolean).length;
    const sentenceSplit = (value) =>
      String(value || "")
        .split(/(?<=[.!?])\s+/)
        .map((entry) => entry.trim())
        .filter(Boolean);
    const normalizeLineByType = (value, type = "primary") => {
      let text = scrub(value);
      if (!text) return "";
      text = text
        .replace(/\.{3,}|…/g, " ")
        .replace(/\s+/g, " ")
        .trim();
      text = simplifyVocabulary(text);
      if (type === "primary") {
        const sentences = sentenceSplit(text);
        text = (sentences.length ? sentences.slice(0, 2) : [text])
          .join(" ")
          .trim();
        if (text && !/[.!?]$/.test(text)) {
          text = `${text}.`;
        }
      }
      return text.replace(/\s+/g, " ").trim();
    };
    const looksLikeImageCommentary = (value, type = "primary") => {
      const text = String(value || "").trim();
      if (!text) return true;
      const lower = text.toLowerCase();
      const blocked = [
        "this ad",
        "the ad",
        "visual",
        "image",
        "photo",
        "composition",
        "camera",
        "lighting",
        "upper left",
        "upper right",
        "lower left",
        "lower right",
        "corner",
        "background",
        "foreground",
        "the word",
        "text reads",
        "headline reads",
        "psychological hook",
        "pattern interruption",
        "juxtaposition",
        "metaphor",
        "why it works",
        "scroll-stopping",
        "masterclass",
        "analysis",
        "implied",
        "implicit",
        "this creative",
        "the creative",
        "copy reads",
        "the clip",
        "clip starts",
        "scene",
        "actor",
        "creator",
        "dialogue",
        "voiceover",
        "on-screen",
        "stitch",
        "shot",
        "segment",
        "timing",
        "continuity",
        "below",
        "above",
      ];
      if (blocked.some((token) => lower.includes(token))) return true;
      if (/^\s*[{\[]/.test(text)) return true;
      if (/"[^"]+"\s*:/.test(text) || /'[^']+'\s*:/.test(text)) return true;
      if (/^(headline|primary text|body copy|cta)\s*:/i.test(text)) return true;
      const words = countWords(text);
      if (type === "headline" && (words < 2 || words > 6)) return true;
      if (type === "primary" && (words < 8 || words > 30)) return true;
      if (type === "primary" && sentenceSplit(text).length > 2) return true;
      if (type === "headline" && hasTooManyLongWords(text, 10, 0)) return true;
      if (type === "primary" && hasTooManyLongWords(text, 11, 1)) return true;
      return false;
    };
    const sanitizeList = (value, { maxLen = 180, type = "primary" } = {}) =>
      toList(value)
        .map((entry) => normalizeLineByType(entry, type))
        .map((entry) => clipPlain(entry, type === "primary" ? 0 : maxLen))
        .filter((entry) => entry && !looksLikeImageCommentary(entry, type));
    const pickFirstList = (...lists) => {
      for (const list of lists) {
        if (Array.isArray(list) && list.length) return list;
      }
      return [];
    };
    const extractArrayField = (obj, keys) =>
      keys.flatMap((key) => {
        const value = obj?.[key];
        if (Array.isArray(value)) return value;
        if (typeof value === "string") return [value];
        return [];
      });
    const headlineCandidates = pickFirstList(
      sanitizeList(
        extractArrayField(source, [
          "headlines",
          "headline_options",
          "headlineOptions",
          "headline_variants",
          "headlineVariants",
        ]),
        { maxLen: 90, type: "headline" },
      ),
      sanitizeList(
        extractArrayField(nested, [
          "headlines",
          "headline_options",
          "headlineOptions",
          "headline_variants",
          "headlineVariants",
        ]),
        { maxLen: 90, type: "headline" },
      ),
    );
    const primaryCandidates = pickFirstList(
      sanitizeList(
        extractArrayField(source, [
          "primaryTexts",
          "primary_texts",
          "primaryText",
          "primary",
          "primary_options",
          "primaryOptions",
          "body",
          "body_options",
          "bodyOptions",
        ]),
        { maxLen: 220, type: "primary" },
      ),
      sanitizeList(
        extractArrayField(nested, [
          "primaryTexts",
          "primary_texts",
          "primaryText",
          "primary",
          "primary_options",
          "primaryOptions",
          "body",
          "body_options",
          "bodyOptions",
        ]),
        { maxLen: 220, type: "primary" },
      ),
    );
    const dedupe = (list) => {
      const seen = new Set();
      const out = [];
      for (const item of list) {
        const text = String(item || "").trim();
        if (!text) continue;
        const key = text.toLowerCase();
        if (seen.has(key)) continue;
        seen.add(key);
        out.push(text);
      }
      return out;
    };
    return {
      headlines: dedupe(headlineCandidates).slice(0, 5),
      primaryTexts: dedupe(primaryCandidates).slice(0, 5),
    };
  }

  async generateAdCopyFromAnalysis(analysis, options = {}) {
    const {
      modelName = DEFAULT_POST_COPY_MODEL,
      channel = "Direct Response",
      tone = "Performance",
      creativity = 0.7,
      enforceLimits = true,
      postType = null,
      postExamples = [],
      _previewOnly = false,
      promptOverride = "",
    } = options || {};
    const payload = analysis || {};
    const analysisJson = JSON.stringify(payload).slice(0, 12000);
    const normalizePostFormat = (value) => {
      const raw = String(value || "")
        .trim()
        .toLowerCase();
      if (
        raw === "blog" ||
        raw === "native_story" ||
        raw === "meta_ads" ||
        raw === "organic"
      ) {
        return raw;
      }
      return "meta_ads";
    };
    const postFormat = normalizePostFormat(postType?.format);
    const postTypeName =
      String(postType?.name || "").trim() ||
      (postFormat === "blog"
        ? "Blog Post"
        : postFormat === "native_story"
          ? "Native Story"
          : postFormat === "organic"
            ? "Organic"
            : "Meta Ads");
    const postTypeGuidance = String(postType?.description || "").trim();
    const postTypeExtra = String(postType?.extraInfo || "").trim();
    const postTypeLines = [
      `Post type: ${postTypeName}.`,
      postTypeGuidance ? `Post type guidance: ${postTypeGuidance}` : "",
      postTypeExtra ? `Post type notes: ${postTypeExtra}` : "",
    ]
      .filter(Boolean)
      .join("\n");
    const resolveExampleMedia = (example) => {
      if (Array.isArray(example?.media)) return example.media;
      if (Array.isArray(example?.images)) return example.images;
      return [];
    };
    const exampleNotes = (Array.isArray(postExamples) ? postExamples : [])
      .slice(0, 3)
      .map((example) => {
        const name = example?.name ? String(example.name) : "Example";
        const notes = String(example?.notes || "").trim();
        const mediaList = resolveExampleMedia(example);
        const mediaNames = mediaList
          .map((item) => item?.name)
          .filter(Boolean)
          .slice(0, 4)
          .join(", ");
        const mediaLine = mediaNames ? `Media: ${mediaNames}` : "";
        const contextLines = mediaList
          .filter((item) => item?.context)
          .slice(0, 2)
          .map((item, index) => {
            const typeHint =
              item?.mediaType ||
              (String(item?.mimeType || "").startsWith("video")
                ? "video"
                : "image");
            const rawJson = JSON.stringify(item.context || {});
            const trimmed = rawJson.slice(0, 2500);
            const suffix = rawJson.length > 2500 ? "…(truncated)" : "";
            return `Context ${index + 1} (${typeHint}): ${trimmed}${suffix}`;
          });
        return [
          name,
          notes ? `Notes: ${notes}` : "",
          mediaLine,
          ...contextLines,
        ]
          .filter(Boolean)
          .join("\n  ");
      })
      .filter(Boolean);
    const examplesBlock = exampleNotes.length
      ? `Reference examples:\n- ${exampleNotes.join("\n- ")}`
      : "";
    const feedbackBlock = this.buildActivityGuidance("post_copy");

    const rolePrompt =
      postFormat === "organic"
        ? getPromptBlock("ad_copy_role_organic")
        : getPromptBlock("ad_copy_role_ads");
    const organicBrandContext =
      postFormat === "organic"
        ? `Context about the brand:
- Use the creative analysis and copy library to infer the product, audience, and tone.
- Start from a relatable real-world moment, use-case, problem, or payoff.
- Keep the voice natural, specific, and on-brand.
- Avoid generic filler, forced slang, and broad unsupported claims.`
        : "";
    const organicOutputBrief =
      postFormat === "organic"
        ? `Write:
1) A short video description (1-2 sentences)
2) 3 hashtag bundles (4-6 hashtags each)

Description rules:
- Start with a relatable moment, need state, payoff, or curiosity hook
- Make the product or offer feel useful and easy to understand
- Sound like a real person, not a corporate brief

Hashtag rules:
- Mix broader discovery hashtags with niche product/use-case hashtags
- Keep hashtags relevant to the product, use case, or audience
- Avoid generic filler tags like #Cool or #ForYou
- Avoid spammy hashtags`
        : "";

    const schemaBlock =
      postFormat === "blog"
        ? `Return ONLY JSON with this schema:
{
  "title": "",
  "subtitle": "",
  "outline": ["", "", "", ""],
  "body": "",
  "cta": ""
}`
        : postFormat === "native_story"
          ? `Return ONLY JSON with this schema:
{
  "headline": "",
  "deck": "",
  "story": "",
  "cta": ""
}`
          : postFormat === "organic"
            ? `Return ONLY JSON with this schema:
{
  "caption": "",
  "hashtagBundles": ["", "", ""]
}`
            : `Return ONLY JSON with this schema:
{
  "headlines": ["", "", "", "", ""],
  "primaryTexts": ["", "", "", "", ""]
}`;

    const formatRules =
      postFormat === "blog"
        ? "Write a concise blog post: title <= 80 chars, subtitle <= 140 chars, outline 3-6 bullets, body 3-5 short paragraphs, CTA 1 sentence."
        : postFormat === "native_story"
          ? "Write an editorial-style native story: headline <= 60 chars, deck 1-2 sentences, story 3-5 short paragraphs, CTA 1 sentence."
          : postFormat === "organic"
            ? getPromptBlock("ad_copy_format_organic")
            : getPromptBlock("ad_copy_format_meta");
    const formatSpecificGuidance =
      postFormat === "meta_ads"
        ? `NEVER describe the image, layout, or analysis. Each primaryTexts entry must be final ad copy ready to publish. Each primaryTexts entry must be 1-2 short sentences and 8-30 words total. Each headline must be 2-6 words (hard limit). Keep every line at or below US 5th-grade reading level. No strategy explanation, no critique, no references to the creative itself.`
        : postFormat === "organic"
          ? `For Organic output:
- caption must be 1-2 sentences
- sentence 1 should open on a relatable moment, use-case, pain point, or curiosity hook
- keep the voice natural, specific, and lightly social, not generic hype
- make the product feel useful, tangible, and easy to understand
- avoid bland filler, empty praise, and corporate phrasing
- avoid captions like "This cool product makes everything better"
- return exactly 3 hashtag bundle options
- each hashtagBundles entry must be one line containing 4-6 hashtags separated by spaces
- every hashtag must start with #
- each bundle should mix broader discovery tags with niche product/use-case tags
- avoid generic filler tags like #Cool, #ForYou, #Video, or spammy trend tags
- bundle 2 and 3 should be slight variants of bundle 1, not random unrelated sets
- avoid duplicate full bundles
- do not include numbering, bullets, or extra commentary inside JSON values`
          : `Do not include markdown, labels, strategy notes, or analysis commentary in the JSON values.`;

    const isRetryableJsonError = (error) => {
      const message = String(error?.message || "");
      return (
        message.includes("Incomplete JSON") ||
        message.includes("No JSON") ||
        message.includes("Empty response")
      );
    };
    const schemaExample =
      postFormat === "blog"
        ? `{"title":"","subtitle":"","outline":["","","",""],"body":"","cta":""}`
        : postFormat === "native_story"
          ? `{"headline":"","deck":"","story":"","cta":""}`
          : postFormat === "organic"
            ? `{"caption":"That tiny daily annoyance adds up fast. This makes the routine feel easier without overcomplicating it.","hashtagBundles":["#DailyRoutine #ProductDemo #EverydayUse #RealResults #BrandStory","#HowItWorks #UsefulFind #LifestyleContent #SimpleUpgrade #WorthTrying","#CreatorPick #ProductMoment #DailyFavorite #FreshLook #EverydayUse"]}`
            : `{"headlines":["","","","",""],"primaryTexts":["","","","",""]}`;
    const parsePostCopyResponse = (parsed) => {
      if (postFormat === "blog") {
        return {
          title: parsed?.title || "",
          subtitle: parsed?.subtitle || "",
          outline: Array.isArray(parsed?.outline) ? parsed.outline : [],
          body: parsed?.body || "",
          cta: parsed?.cta || "",
        };
      }
      if (postFormat === "native_story") {
        return {
          headline: parsed?.headline || "",
          deck: parsed?.deck || "",
          story: parsed?.story || "",
          cta: parsed?.cta || "",
        };
      }
      if (postFormat === "organic") {
        return {
          caption:
            parsed?.caption || parsed?.description || parsed?.sentence || "",
          hashtagBundles: Array.isArray(parsed?.hashtagBundles)
            ? parsed.hashtagBundles
            : Array.isArray(parsed?.hashtag_sets)
              ? parsed.hashtag_sets
              : Array.isArray(parsed?.hashtagSets)
                ? parsed.hashtagSets
                : Array.isArray(parsed?.hashtags)
                  ? [parsed.hashtags.join(" ")]
                  : Array.isArray(parsed?.tags)
                    ? [parsed.tags.join(" ")]
                    : [],
        };
      }
      return this.normalizeMetaAdsCopyPayload(parsed);
    };
    const ORGANIC_BANNED_CAPTION_PATTERNS = [
      /cool product/i,
      /awesome product/i,
      /^this\s+(video|cool|awesome|great|nice)\b/i,
      /\bchanges everything\b/i,
    ];
    const ORGANIC_BANNED_HASHTAGS = new Set([
      "#cool",
      "#foryou",
      "#fyp",
      "#video",
      "#viral",
      "#trend",
      "#trending",
      "#coolproduct",
      "#bestproduct",
    ]);
    const getOrganicBundleTags = (value) =>
      String(value || "").match(/#[A-Za-z0-9_]+/g) || [];
    const countWords = (value) =>
      String(value || "")
        .trim()
        .split(/\s+/)
        .filter(Boolean).length;
    const sentenceSplit = (value) =>
      String(value || "")
        .split(/(?<=[.!?])\s+/)
        .map((entry) => entry.trim())
        .filter(Boolean);
    const hasTooManyLongWords = (value, maxLen = 10, limit = 1) => {
      const count = String(value || "")
        .split(/\s+/)
        .map((word) => word.replace(/[^a-z]/gi, ""))
        .filter(Boolean)
        .filter((word) => word.length > maxLen).length;
      return count > limit;
    };
    const averageWordLength = (value) => {
      const words = String(value || "")
        .split(/\s+/)
        .map((word) => word.replace(/[^a-z]/gi, ""))
        .filter(Boolean);
      if (!words.length) return 0;
      return words.reduce((sum, word) => sum + word.length, 0) / words.length;
    };
    const validatePostCopyResponse = (result, rawContent = "") => {
      if (postFormat === "organic") {
        const caption = String(result?.caption || "").trim();
        const sentences = caption
          .split(/[.!?]+/)
          .map((entry) => entry.trim())
          .filter(Boolean);
        const bundles = Array.isArray(result?.hashtagBundles)
          ? result.hashtagBundles
              .map((entry) => String(entry || "").trim())
              .filter(Boolean)
          : [];
        const hasBadCaption =
          !caption ||
          sentences.length < 1 ||
          sentences.length > 2 ||
          ORGANIC_BANNED_CAPTION_PATTERNS.some((pattern) =>
            pattern.test(caption),
          );
        const hasBadBundle =
          bundles.length < 3 ||
          bundles.some((bundle) => {
            const tags = getOrganicBundleTags(bundle);
            return (
              tags.length < 4 ||
              tags.length > 6 ||
              tags.some((tag) => ORGANIC_BANNED_HASHTAGS.has(tag.toLowerCase()))
            );
          }) ||
          new Set(bundles.map((bundle) => bundle.toLowerCase())).size !==
            bundles.length;
        if (!hasBadCaption && !hasBadBundle) {
          return result;
        }
        const error = new Error("Organic copy quality check failed");
        error.modelContent = rawContent;
        throw error;
      }
      if (postFormat !== "meta_ads") {
        return result;
      }
      const headlines = Array.isArray(result?.headlines)
        ? result.headlines
            .map((entry) => String(entry || "").trim())
            .filter(Boolean)
        : [];
      const primaryTexts = Array.isArray(result?.primaryTexts)
        ? result.primaryTexts
            .map((entry) => String(entry || "").trim())
            .filter(Boolean)
        : [];
      const hasBadHeadlines =
        headlines.length !== 5 ||
        new Set(headlines.map((entry) => entry.toLowerCase())).size !== 5 ||
        headlines.some((headline) => {
          const words = countWords(headline);
          return (
            words < 2 ||
            words > 6 ||
            hasTooManyLongWords(headline, 10, 0) ||
            averageWordLength(headline) > 6.2
          );
        });
      const hasBadPrimaryTexts =
        primaryTexts.length !== 5 ||
        new Set(primaryTexts.map((entry) => entry.toLowerCase())).size !== 5 ||
        primaryTexts.some((text) => {
          const words = countWords(text);
          return (
            words < 8 ||
            words > 30 ||
            sentenceSplit(text).length > 2 ||
            hasTooManyLongWords(text, 11, 1) ||
            averageWordLength(text) > 5.8
          );
        });
      if (!hasBadHeadlines && !hasBadPrimaryTexts) {
        return result;
      }
      const error = new Error("Meta ads copy quality check failed");
      error.modelContent = rawContent;
      throw error;
    };
    const collectJsonishStrings = (value, out, depth = 0) => {
      if (depth > 6 || out.length >= 60 || value == null) return;
      if (typeof value === "string") {
        const trimmed = value.trim();
        if (
          trimmed &&
          trimmed.length >= 2 &&
          (trimmed.includes("{") || trimmed.includes("["))
        ) {
          out.push(trimmed.slice(0, 20000));
        }
        return;
      }
      if (Array.isArray(value)) {
        value.forEach((item) => collectJsonishStrings(item, out, depth + 1));
        return;
      }
      if (typeof value === "object") {
        Object.values(value).forEach((item) =>
          collectJsonishStrings(item, out, depth + 1),
        );
      }
    };
    const extractContentCandidates = (data) => {
      const candidates = [];
      const modelCandidates = Array.isArray(data?.candidates)
        ? data.candidates
        : [];
      modelCandidates.forEach((candidate) => {
        const parts = Array.isArray(candidate?.content?.parts)
          ? candidate.content.parts
          : [];
        parts.forEach((part) => {
          const directText = String(part?.text || "").trim();
          if (directText) {
            candidates.push(directText.slice(0, 20000));
          }
          const functionArgs =
            part?.functionCall?.args || part?.function_call?.args;
          if (typeof functionArgs === "string" && functionArgs.trim()) {
            candidates.push(functionArgs.trim().slice(0, 20000));
          } else if (functionArgs && typeof functionArgs === "object") {
            candidates.push(JSON.stringify(functionArgs).slice(0, 20000));
          }
        });
        collectJsonishStrings(candidate?.metadata, candidates);
        collectJsonishStrings(candidate?.groundingMetadata, candidates);
      });
      collectJsonishStrings(data?.metadata, candidates);
      collectJsonishStrings(data?.promptFeedback, candidates);
      const seen = new Set();
      return candidates.filter((entry) => {
        const key = String(entry || "").trim();
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    };
    // Extract the on-image headline so we can anchor copy to it
    const imageHeadline = (() => {
      try { return String(JSON.parse(analysisJson)?.visual_elements?.text_overlays || "").trim(); }
      catch { return ""; }
    })();
    const buildPrompt = (strict) => `${rolePrompt}
${organicBrandContext}
${organicOutputBrief}
Your job is to generate copy BASED ON AND CONSISTENT WITH the ad creative in the analysis below.
## IMAGE HEADLINE (MOST IMPORTANT)
${imageHeadline
  ? `The actual headline text visible on the image is: "${imageHeadline}".
Your generated headlines MUST be variations or amplifications of this same idea and angle.
Do NOT generate headlines that contradict or ignore this core message.`
  : "Derive the core message and angle directly from the analysis — especially visual_elements.text_overlays and messaging fields."}
${postTypeLines}
${examplesBlock}
${feedbackBlock}
Channel: ${channel}. Tone: ${tone}.
${formatRules}
${formatSpecificGuidance}
Use the Copy library guidance below for every post type.
Use the Copy Library phrasing and claims whenever possible. Do NOT invent claims beyond the library or analysis.
${enforceLimits ? "Enforce length limits and avoid duplicates." : "Allow creative variation."}
${schemaBlock}
${strict ? "Return strictly valid JSON only. Do not include markdown, commentary, or trailing text." : ""}

Analysis JSON:
${analysisJson}
${this.buildAdCopyGuidance()}
${this.buildAnalysisGuidance()}
${this.buildAiGuidance()}`;
    // When a promptOverride exists, use it as the role/instructions but always
    // append the per-image analysis data so each creative generates unique copy.
    const buildEffectivePrompt = (strict) => {
      if (!promptOverride) return buildPrompt(strict);
      return `${promptOverride}

## IMAGE DATA (use this to make copy specific to this creative)
${imageHeadline ? `Image headline: "${imageHeadline}"` : ""}
Channel: ${channel}. Tone: ${tone}.
${schemaBlock}
${strict ? "Return strictly valid JSON only. Do not include markdown, commentary, or trailing text." : ""}

Analysis JSON:
${analysisJson}
${this.buildAdCopyGuidance()}`;
    };
    if (_previewOnly) return { prompt: buildEffectivePrompt(false) };
    const buildRepairPrompt = (
      brokenContent,
    ) => `You repair invalid or incomplete JSON outputs.
Convert the broken response below into VALID JSON matching this schema exactly:
${schemaExample}
Rules:
- Return ONLY valid JSON.
- Keep intent and wording where possible.
- For Meta Ads output, return exactly 5 unique headlines and exactly 5 unique primaryTexts. Create missing items if needed.
- Keep Meta Ads copy at or below US 5th-grade reading level.
- If a field is missing, use an empty string or empty array.

Broken response:
${String(brokenContent || "").slice(0, 12000)}`;

    const resolveMaxOutputTokens = (strict) =>
      postFormat === "blog"
        ? strict
          ? 2200
          : 1700
        : postFormat === "native_story"
          ? strict
            ? 1500
            : 1200
          : postFormat === "organic"
            ? strict
              ? 700
              : 600
            : strict
              ? 1000
              : 900;
    const parseFromContentCandidates = (contentCandidates) => {
      let lastParseError = null;
      for (const content of contentCandidates || []) {
        try {
          const parsed = this.extractJSON(content);
          const result = parsePostCopyResponse(parsed);
          return validatePostCopyResponse(result, content);
        } catch (parseError) {
          parseError.modelContent = content;
          lastParseError = parseError;
        }
      }
      if (lastParseError) {
        throw lastParseError;
      }
      const emptyError = new Error("Empty response from model");
      emptyError.modelContent = "";
      throw emptyError;
    };
    const runWithGemini = async (model, strict, brokenContent = "") => {
      const maxOutputTokens = resolveMaxOutputTokens(strict);
      const prompt =
        brokenContent && strict
          ? buildRepairPrompt(brokenContent)
          : buildEffectivePrompt(strict);
      const data = await this.callGeminiGenerate(model, {
        contents: [{ parts: [{ text: prompt }] }],
        generationConfig: {
          temperature: strict ? Math.min(0.4, creativity) : creativity,
          maxOutputTokens,
          responseMimeType: "application/json",
        },
      });
      const contentCandidates = extractContentCandidates(data);
      return parseFromContentCandidates(contentCandidates);
    };
    const runWithOpenAI = async (model, strict, brokenContent = "") => {
      const maxOutputTokens = resolveMaxOutputTokens(strict);
      const prompt =
        brokenContent && strict
          ? buildRepairPrompt(brokenContent)
          : buildEffectivePrompt(strict);
      // OpenAI requires the word "json" in the prompt when using json_object mode
      const promptHasJson = /json/i.test(prompt);
      const response = await this.callOpenAIChat({
        model,
        messages: [{ role: "user", content: promptHasJson ? prompt : `${prompt}\n\nReturn your response as valid JSON.` }],
        temperature: strict ? Math.min(0.4, creativity) : creativity,
        max_completion_tokens: maxOutputTokens + 300,
        response_format: { type: "json_object" },
      });
      const contentCandidates =
        this.getOpenAIChatContentCandidatesOrThrow(response);
      return parseFromContentCandidates(contentCandidates);
    };
    const runWithClaude = async (model, strict, brokenContent = "") => {
      const maxOutputTokens = resolveMaxOutputTokens(strict);
      const prompt =
        brokenContent && strict
          ? buildRepairPrompt(brokenContent)
          : buildEffectivePrompt(strict);
      const text = await this.callClaude({
        model,
        max_tokens: maxOutputTokens + 300,
        temperature: strict ? Math.min(0.4, creativity) : creativity,
        messages: [
          {
            role: "user",
            content: [{ type: "text", text: prompt }],
          },
        ],
      });
      const content = String(text || "").trim();
      return parseFromContentCandidates(content ? [content] : []);
    };
    const buildModelFallbackOrder = (preferredModel) => {
      const requested = String(preferredModel || "").trim();
      const lower = requested.toLowerCase();
      const ordered = [requested];
      if (lower.startsWith("gemini")) {
        ordered.push(
          "gemini-2.5-flash",
          "gemini-2.5-pro",
          OPENAI_LATEST_MODEL,
          CLAUDE_DEFAULT_MODEL,
        );
      } else if (lower.startsWith("gpt")) {
        ordered.push(
          OPENAI_LATEST_MODEL,
          CLAUDE_DEFAULT_MODEL,
          "gemini-2.5-flash",
          "gemini-2.5-pro",
        );
      } else if (lower.startsWith("claude")) {
        ordered.push(
          CLAUDE_DEFAULT_MODEL,
          "gemini-2.5-flash",
          "gemini-2.5-pro",
          OPENAI_LATEST_MODEL,
        );
      } else {
        ordered.push(
          CLAUDE_DEFAULT_MODEL,
          "gemini-2.5-flash",
          "gemini-2.5-pro",
          OPENAI_LATEST_MODEL,
        );
      }
      return Array.from(
        new Set(
          ordered.map((entry) => String(entry || "").trim()).filter(Boolean),
        ),
      );
    };
    const isRetryableModelError = (error) => {
      const message = String(error?.message || "").toLowerCase();
      return (
        isRetryableJsonError(error) ||
        this.isGeminiTransientError(error) ||
        this.isGeminiModelUnavailableError(error) ||
        message.includes("high demand") ||
        message.includes("try again later") ||
        message.includes("quality check failed") ||
        message.includes("temporarily unavailable") ||
        message.includes("service unavailable") ||
        message.includes("timeout")
      );
    };
    const runWithModel = (model, strict, brokenContent = "") => {
      const lower = String(model || "")
        .trim()
        .toLowerCase();
      if (lower.startsWith("gpt")) {
        return runWithOpenAI(model, strict, brokenContent);
      }
      if (lower.startsWith("claude")) {
        return runWithClaude(model, strict, brokenContent);
      }
      return runWithGemini(model, strict, brokenContent);
    };

    const fallbackModels = buildModelFallbackOrder(modelName);
    let lastError = null;
    for (const candidateModel of fallbackModels) {
      try {
        return await runWithModel(candidateModel, false);
      } catch (error) {
        lastError = error;
        if (!isRetryableModelError(error)) {
          continue;
        }
        try {
          return await runWithModel(candidateModel, true);
        } catch (strictError) {
          lastError = strictError;
          if (!isRetryableModelError(strictError)) {
            continue;
          }
          try {
            return await runWithModel(
              candidateModel,
              true,
              strictError?.modelContent || error?.modelContent || "",
            );
          } catch (repairError) {
            lastError = repairError;
          }
        }
      }
    }
    throw lastError || new Error("Post copy generation failed");
  }

  buildAdCopyGuidance() {
    const { notes, avoid, docs, headlines, primaryTexts } =
      this.getAdCopyKnowledge();
    const lines = [];
    const trimmedNotes = String(notes || "").trim();
    if (trimmedNotes) {
      lines.push(`Notes: ${this.compactText(trimmedNotes, 420)}`);
    }
    const trimmedAvoid = String(avoid || "").trim();
    if (trimmedAvoid) {
      lines.push(`Avoid: ${this.compactText(trimmedAvoid, 320)}`);
    }
    const cleanedHeadlines = (Array.isArray(headlines) ? headlines : [])
      .map((value) => String(value || "").trim())
      .filter(Boolean)
      .slice(0, 3)
      .map((value) => this.compactText(value, 80));
    if (cleanedHeadlines.length > 0) {
      lines.push(`Headlines: ${cleanedHeadlines.join(" | ")}`);
    }
    const cleanedPrimary = (Array.isArray(primaryTexts) ? primaryTexts : [])
      .map((value) => String(value || "").trim())
      .filter(Boolean)
      .slice(0, 3)
      .map((value) => this.compactText(value, 140));
    if (cleanedPrimary.length > 0) {
      lines.push(`Primary text: ${cleanedPrimary.join(" | ")}`);
    }
    const docLines = (Array.isArray(docs) ? docs : [])
      .filter((doc) => doc?.text)
      .slice(0, 1)
      .map((doc) => {
        const name = doc?.name ? String(doc.name) : "reference";
        const text = this.compactText(String(doc.text || ""), 420);
        return text ? `Doc (${name}): ${text}` : "";
      })
      .filter(Boolean);
    if (docLines.length > 0) {
      lines.push(...docLines);
    }
    if (lines.length === 0) {
      return "";
    }
    return `\n\nCopy library (condensed):\n${lines.join("\n")}\n`;
  }

  getBlogKnowledge() {
    const source = this.blogKnowledge || {};
    return {
      notes: String(source?.notes || ""),
      goals: String(source?.goals || ""),
      audience: String(source?.audience || ""),
      mustInclude: String(source?.mustInclude || ""),
      avoid: String(source?.avoid || ""),
      cta: String(source?.cta || ""),
      docs: Array.isArray(source?.docs) ? source.docs : [],
    };
  }

  getBlogSeo() {
    const source = this.blogSeo || {};
    return {
      metaTitle: String(source?.metaTitle || ""),
      metaDescription: String(source?.metaDescription || ""),
      keywords: Array.isArray(source?.keywords) ? source.keywords : [],
      slug: String(source?.slug || ""),
      canonicalUrl: String(source?.canonicalUrl || ""),
      schemaMarkup: String(source?.schemaMarkup || ""),
    };
  }

  buildBlogKnowledgeGuidance() {
    const { notes, goals, audience, mustInclude, avoid, cta, docs } =
      this.getBlogKnowledge();
    const lines = [];
    const trimmedNotes = String(notes || "").trim();
    if (trimmedNotes)
      lines.push(`Notes: ${this.compactText(trimmedNotes, 420)}`);
    if (goals) lines.push(`Goals: ${this.compactText(goals, 220)}`);
    if (audience) lines.push(`Audience: ${this.compactText(audience, 220)}`);
    if (mustInclude)
      lines.push(`Must include: ${this.compactText(mustInclude, 240)}`);
    if (avoid) lines.push(`Avoid: ${this.compactText(avoid, 200)}`);
    if (cta) lines.push(`CTA guidance: ${this.compactText(cta, 160)}`);
    const docLines = (Array.isArray(docs) ? docs : [])
      .filter((doc) => doc?.text)
      .slice(0, 2)
      .map((doc) => {
        const name = doc?.name ? String(doc.name) : "reference";
        const text = this.compactText(String(doc.text || ""), 420);
        return text ? `Doc (${name}): ${text}` : "";
      })
      .filter(Boolean);
    if (docLines.length > 0) lines.push(...docLines);
    if (lines.length === 0) return "";
    return `\n\nBlog knowledge (condensed):\n${lines.join("\n")}\n`;
  }

  buildBlogSeoGuidance(overrides) {
    const base = overrides || this.getBlogSeo();
    const keywords = Array.isArray(base?.keywords) ? base.keywords : [];
    const lines = [];
    if (base?.metaTitle)
      lines.push(`Meta title: ${this.compactText(base.metaTitle, 120)}`);
    if (base?.metaDescription)
      lines.push(
        `Meta description: ${this.compactText(base.metaDescription, 180)}`,
      );
    if (keywords.length)
      lines.push(`SEO keywords: ${keywords.slice(0, 6).join(", ")}`);
    if (base?.slug) lines.push(`Slug: ${this.compactText(base.slug, 80)}`);
    if (base?.canonicalUrl)
      lines.push(`Canonical: ${this.compactText(base.canonicalUrl, 120)}`);
    if (base?.schemaMarkup)
      lines.push(`Schema markup: ${this.compactText(base.schemaMarkup, 260)}`);
    if (!lines.length) return "";
    return `\n\nSEO defaults (condensed):\n${lines.join("\n")}\n`;
  }

  buildCreativeKnowledgeSummary(knowledgeDb = []) {
    const folders = Array.isArray(knowledgeDb) ? knowledgeDb : [];
    const lines = folders.slice(0, 6).map((folder) => {
      const name = folder?.name || "Knowledge";
      const notes = this.compactText(folder?.notes || "", 160);
      const imageNames = (folder?.images || [])
        .map((img) => img?.name)
        .filter(Boolean)
        .slice(0, 4)
        .join(", ");
      const imageCount = Number(folder?.images?.length || 0);
      return [
        name,
        notes,
        imageCount > 0 ? `Reference images: ${imageCount}` : "",
        imageNames ? `Image notes: ${imageNames}` : "",
      ]
        .filter(Boolean)
        .join(" — ");
    });
    if (!lines.length) return "";
    return `Creative knowledge base:\n- ${lines.join("\n- ")}`;
  }

  buildTagSummary(tags = [], { limit = 8 } = {}) {
    return (Array.isArray(tags) ? tags : [])
      .slice(0, limit)
      .map((tag) =>
        tag?.description ? `${tag.name}: ${tag.description}` : tag?.name,
      )
      .filter(Boolean)
      .join("; ");
  }

  matchesManmadeScratchContext(brandLibrary = {}, knowledgeFolders = []) {
    const haystack = [
      brandLibrary?.name,
      brandLibrary?.message,
      ...(Array.isArray(knowledgeFolders)
        ? knowledgeFolders.flatMap((folder) => [folder?.name, folder?.notes])
        : []),
    ]
      .map((value) =>
        String(value || "")
          .trim()
          .toLowerCase(),
      )
      .filter(Boolean)
      .join(" ");
    return /\bmanmade\b/.test(haystack) || /\bman\s+made\b/.test(haystack);
  }

  buildKnowledgeFolderContext(knowledgeFolders = []) {
    return (Array.isArray(knowledgeFolders) ? knowledgeFolders : [])
      .slice(0, 6)
      .map((folder) => ({
        name: String(folder?.name || "").trim(),
        notes: this.compactText(folder?.notes || "", 500),
        reference_image_count: Number(folder?.images?.length || 0),
        reference_images: (Array.isArray(folder?.images) ? folder.images : [])
          .slice(0, 6)
          .map((image) => ({
            name: String(image?.name || "").trim(),
            mime_type: String(image?.mimeType || "").trim(),
          }))
          .filter((image) => image.name || image.mime_type),
      }))
      .filter(
        (folder) => folder.name || folder.notes || folder.reference_image_count,
      );
  }

  buildScratchGenerationContext(options = {}) {
    const {
      knowledgeFolders = [],
      tags = [],
      brandLibrary = {},
      focusArea = "Visual Hook",
      sceneMode = "mixed",
      creativity = 0.7,
      specialInstructions = "",
      promptChannel = "",
      aspectRatio = "",
    } = options || {};
    const aiProfile = this.getAiProfile() || {};
    const adCopy = this.getAdCopyKnowledge() || {};
    const normalizedSceneMode = this.normalizeSceneMode(sceneMode);
    const selectedProducts = this.buildKnowledgeFolderContext(knowledgeFolders);
    const normalizedTags = (Array.isArray(tags) ? tags : [])
      .slice(0, 12)
      .map((tag) => ({
        name: String(tag?.name || "").trim(),
        description: this.compactText(tag?.description || "", 220),
      }))
      .filter((tag) => tag.name);
    const contextJson = {
      source_of_truth: {
        mode: "knowledge_base_only",
        rule: "Product image truth, tags, settings, brand info, and creative directions must come from the selected knowledge folders and workspace settings only.",
      },
      brand_identity: {
        name: String(brandLibrary?.name || "").trim(),
        message: this.compactText(brandLibrary?.message || "", 600),
        colors: Array.isArray(brandLibrary?.colors) ? brandLibrary.colors : [],
        fonts: Array.isArray(brandLibrary?.fonts) ? brandLibrary.fonts : [],
        asset_names: (Array.isArray(brandLibrary?.assets)
          ? brandLibrary.assets
          : []
        )
          .slice(0, 6)
          .map((asset) => String(asset?.name || "").trim())
          .filter(Boolean),
      },
      visual_style: {
        aspect_ratio:
          String(aspectRatio || this.getMasterAspectRatio() || "1:1").trim() ||
          "1:1",
        scene_mode: normalizedSceneMode,
        focus_area: String(focusArea || "Visual Hook").trim(),
        prompt_channel: String(promptChannel || "").trim(),
        creativity: Number.isFinite(Number(creativity))
          ? Number(creativity)
          : 0.7,
      },
      customer_psychology: {
        ai_profile: {
          instructions: this.compactText(aiProfile?.instructions || "", 280),
          personality: this.compactText(aiProfile?.personality || "", 180),
          must_do: this.compactText(aiProfile?.mustDo || "", 220),
          avoid: this.compactText(aiProfile?.avoid || "", 220),
        },
        ad_copy_knowledge: {
          notes: this.compactText(adCopy?.notes || "", 320),
          avoid: this.compactText(adCopy?.avoid || "", 220),
          sample_headlines: (Array.isArray(adCopy?.headlines)
            ? adCopy.headlines
            : []
          )
            .slice(0, 4)
            .map((line) => this.compactText(line, 100))
            .filter(Boolean),
        },
      },
      product_details: {
        selected_products: selectedProducts,
        product_image_source_rule:
          "Match the product ONLY from selected knowledge-base folder notes and attached reference images. Do not invent new packaging, colors, textures, or product forms. The reference images are the single source of truth for what the product looks like.",
        headline_source_rule:
          "Headlines and copy should draw inspiration from ALL available knowledge: copy library (sample_headlines, notes), brand identity (message), product folder notes, AI profile instructions, and selected tags. Combine insights across all sources for compelling, specific headlines — not generic category claims.",
      },
      creative_strategy: {
        selected_tags: normalizedTags,
        special_instructions: this.compactText(specialInstructions || "", 700),
        performance_goal:
          "Generate sharp, testable static ad concepts that read in 1-2 seconds and feel brand-specific rather than generic ecommerce.",
      },
      tone: {
        desired_feel:
          "Performance-minded, compressed, emotionally resonant, visually obvious, identity-aware.",
      },
      formatting_preferences: {
        output_contract:
          "Return strict JSON matching the internal variants schema.",
        mobile_first: true,
      },
      ad_platform_constraints: {
        mobile_first: true,
        safe_margin_required: true,
        platform: "Paid social static ad creative",
      },
      performance_goals: {
        clarity: "Instantly understandable",
        emotion: "Emotionally resonant",
        proof: "Visual proof over feature dumping",
      },
    };
    if (this.matchesManmadeScratchContext(brandLibrary, knowledgeFolders)) {
      contextJson.knowledge_layer_addon =
        MANMADE_BOOK_BRAIN_ADDON.knowledge_layer_addon;
    }
    return contextJson;
  }

  normalizeCreativeStringList(values, limit = 6) {
    return (Array.isArray(values) ? values : [values])
      .map((value) => String(value || "").trim())
      .filter(Boolean)
      .slice(0, limit);
  }

  hasExplicitNoTextRequest(...values) {
    return values.some((value) =>
      /(no\s+text|text[-\s]?free|without\s+text|no\s+copy|without\s+copy|copy[-\s]?free)/i.test(
        String(value || ""),
      ),
    );
  }

  shouldForceCta(...values) {
    return values.some((value) =>
      /\b(cta|call to action|shop now|learn more|buy now|get yours|order now|offer|discount|sale|button)\b/i.test(
        String(value || ""),
      ),
    );
  }

  // Copy fields must hold only the words that appear on the ad. Concept models
  // sometimes prefix them with meta-labels ("Headline: ...", "Top headline —",
  // "CTA: ...") or wrap them in quotes; if that leaks through, the image model
  // renders the label itself onto the creative. Strip labels and quote wrappers
  // until the string stabilizes.
  cleanAdCopyText(value) {
    let text = String(value || "")
      .replace(/\s+/g, " ")
      .trim();
    for (let pass = 0; pass < 4; pass += 1) {
      const before = text;
      text = text
        .replace(
          /^(?:top|main|primary|bold|big|hero)?\s*(?:headline|header|title|hook|sub-?head(?:line)?|support(?:ing)?\s*(?:line|copy|text)?|value\s*prop(?:osition)?|body\s*copy|primary\s*text|tag\s*line|cta|call\s*to\s*action|badge|button|copy|text\s*overlays?)\s*[:\-–—=>]\s*/i,
          "",
        )
        .replace(/^["'“”‘’`]+/, "")
        .replace(/["'“”‘’`]+$/, "")
        .trim();
      if (text === before) break;
    }
    return text;
  }

  trimOverlayCopy(
    value,
    { maxWords = 12, maxChars = 90, forceSentence = false } = {},
  ) {
    const raw = String(value || "")
      .replace(/\s+/g, " ")
      .trim();
    if (!raw) return "";
    const words = raw.split(" ").filter(Boolean).slice(0, maxWords);
    let text = words.join(" ").trim();
    if (maxChars && text.length > maxChars) {
      text = text.slice(0, maxChars).trim();
    }
    text = text.replace(/[,\s]+$/, "").trim();
    if (forceSentence && text && !/[.!?]$/.test(text)) {
      text = `${text}.`;
    }
    return text;
  }

  withVariantCopyFallback(context = {}, options = {}) {
    const source =
      context && typeof context === "object" && !Array.isArray(context)
        ? context
        : {};
    const visual =
      source?.visual_elements &&
      typeof source.visual_elements === "object" &&
      !Array.isArray(source.visual_elements)
        ? source.visual_elements
        : {};
    const messaging =
      source?.messaging &&
      typeof source.messaging === "object" &&
      !Array.isArray(source.messaging)
        ? source.messaging
        : {};
    const specialInstructions = String(
      options?.specialInstructions || "",
    ).trim();
    const promptChannel = String(
      options?.promptChannel ||
        source?.promptChannel ||
        source?.prompt_channel ||
        "",
    ).trim();
    if (this.hasExplicitNoTextRequest(specialInstructions, promptChannel)) {
      return {
        ...source,
        visual_elements: {
          ...visual,
          text_overlays: "",
        },
        messaging: {
          ...messaging,
          value_proposition: "",
          cta: "",
        },
      };
    }

    const copySeeds = this.normalizeMetaAdsCopyPayload({});
    const headline = this.trimOverlayCopy(
      this.cleanAdCopyText(
        visual?.text_overlays ||
          copySeeds?.headlines?.[0] ||
          visual?.primary_hook ||
          "",
      ),
      { maxWords: 9, maxChars: 68 },
    );
    const valueProposition = this.trimOverlayCopy(
      this.cleanAdCopyText(
        messaging?.value_proposition ||
          copySeeds?.primaryTexts?.[0] ||
          `${source?.product_focus || options?.productFocus || "The product"} helps you get better results fast`,
      ),
      { maxWords: 14, maxChars: 120, forceSentence: true },
    );
    const shouldIncludeCta = this.shouldForceCta(
      messaging?.cta,
      options?.focusArea,
      specialInstructions,
      promptChannel,
    );
    const cta = shouldIncludeCta
      ? this.trimOverlayCopy(
          this.cleanAdCopyText(messaging?.cta || options?.defaultCta || "Shop now"),
          { maxWords: 4, maxChars: 24 },
        )
      : this.trimOverlayCopy(this.cleanAdCopyText(messaging?.cta || ""), {
          maxWords: 4,
          maxChars: 24,
        });

    return {
      ...source,
      visual_elements: {
        ...visual,
        text_overlays: headline,
      },
      messaging: {
        ...messaging,
        value_proposition: valueProposition,
        cta,
      },
    };
  }

  normalizeSceneMode(value) {
    const normalized = String(value || "")
      .trim()
      .toLowerCase();
    if (
      normalized === "ad" ||
      normalized === "product" ||
      normalized === "review" ||
      normalized === "custom"
    ) {
      return normalized;
    }
    // Migrate legacy values
    if (normalized === "general") return "product";
    if (normalized === "in_use") return "review";
    return "ad";
  }

  buildSceneModeRuleBlock(sceneMode, intent = "knowledge") {
    const mode = this.normalizeSceneMode(sceneMode);
    if (mode === "product") {
      if (intent === "image") {
        return getPromptBlock("scene_rule_product_image") || `[product image rule]`;
      }
      return getPromptBlock("scene_rule_product_variant") || `[product variant rule]`;
    }
    if (mode === "review") {
      if (intent === "image") {
        return getPromptBlock("scene_rule_review_image") || `[review image rule]`;
      }
      return getPromptBlock("scene_rule_review_variant") || `[review variant rule]`;
    }
    if (mode === "custom") {
      if (intent === "image") {
        return getPromptBlock("scene_rule_custom_image") || `[custom image rule]`;
      }
      return getPromptBlock("scene_rule_custom_variant") || `[custom variant rule]`;
    }
    // Default: ad mode
    if (intent === "image") {
      return getPromptBlock("scene_rule_ad_image") || `[ad image rule]`;
    }
    return getPromptBlock("scene_rule_ad_variant");
  }

  buildNativeAdSystemPrompt() {
    return getPromptBlock("native_ad_system");
  }

  getSceneModeChannelPool(sceneMode, mode = "knowledge", focusArea = "") {
    const scene = this.normalizeSceneMode(sceneMode);
    const pools =
      mode === "analysis"
        ? {
            ad: [
              "VS comparison chart - razor/competitor vs ManMade with x/check benefit rows and the product as the clear hero.",
              "Before/after split - skin results, razor bumps cleared, and a clear 'How It Started / How It's Going' transformation story.",
              "Guarantee hero - '30-Day Guarantee' or 'You Love It Or We'll Buy It Back' as dominant text with the product as the secondary anchor.",
              "Bundle stack - trimmer + aftershave + guards + travel bag + brush with crossed-out prices resolving to FREE.",
              "Mechanism reveal - macro close-up of safety guards, dual heads, ceramic blade, and labeled feature callouts.",
              "Stat/proof chart - bold proof graphics like '98% less cuts,' '4,600+ five-star reviews,' or 'Zero exposed blades' with the product.",
              "Bathroom-in-context - product staged on vanity, shower shelf, or counter with no person visible, used as a quiet social-proof signal.",
              "Text-dominant hero - bold headline does the selling while the product supports the message.",
              "Wife's text message overlay - screenshot-style text bubble plus the product as a visual anchor.",
              "Component isolation - single part hero (blade head, guard, or aftershave bottle) shown in extreme macro.",
            ],
            product: [
              "Hero shot + premium positioning (product propped on marble/concrete, 45-degree angle, warm studio light, aspirational)",
              "Multi-angle grid + completeness (front/side/back/detail in 2x2 layout, white or neutral background, e-commerce ready)",
              "Lifestyle use-case + context (product on bathroom counter / gym bag / nightstand, shows where it fits in life)",
              "Before/after comparison + transformation proof (split or side-by-side, clear visual difference, data overlay)",
              "Feature callout + education (product with subtle text labels pointing to key features, infographic-style)",
              "Ingredient/material close-up + quality signal (macro texture shot, premium feel, craftsmanship visible)",
              "Scale-in-hand + relatability (product held in hand, shows real size, human element without being a full lifestyle shot)",
              "Flat-lay bundle + value communication (product with accessories/packaging, overhead shot, premium surface)",
              "Unboxing moment + first impression (product emerging from box, tissue paper, the 'opening experience')",
              "Action detail + mechanism proof (close-up of product doing its job, the 'how it works' money shot)",
            ],
            review: [
              "Bathroom counter + just-received moment (product next to sink, water drops on counter, box/packaging nearby, harsh overhead light)",
              "Mirror selfie + genuine excitement (product held up, bathroom mirror slightly smudged, mixed lighting, real person energy)",
              "Sink edge + mid-routine (product on wet sink edge, toothbrush/cup nearby, steam/condensation visible, morning chaos)",
              "Nightstand + daily driver (product on messy nightstand with phone charger/glasses/water glass, warm lamp light, lived-in)",
              "Kitchen counter + casual discovery (product on granite/laminate, coffee mug nearby, mail/clutter, 'I use this every day' vibe)",
              "Shower ledge + in-context (product on shower shelf with other bottles, water visible, tile grout not perfect, steamy)",
              "Travel bag + on-the-go (product in dopp kit or gym bag, zipper open, other real toiletries visible, not curated)",
              "Hand holding + texture moment (wet or slightly soapy hand holding product, bathroom background blurred, phone selfie angle)",
              "Countertop overhead + 'my collection' (iPhone top-down of bathroom counter, product among real clutter, lived-in messy authentic)",
              "Package just opened + first impression (product on bed/couch with torn shipping envelope, tissue paper, 'just arrived' energy)",
            ],
          }
        : {
            ad: [
              "VS comparison chart (razor/competitor vs ManMade, x/check benefit rows)",
              "Before/after split (skin results, razor bumps cleared, transformation proof)",
              "Guarantee hero (guarantee-led headline, product as anchor)",
              "Bundle stack (trimmer + accessories + crossed-out prices to FREE)",
              "Mechanism reveal (macro close-up with feature callouts)",
              "Stat/proof chart (bold proof data with product)",
              "Bathroom-in-context (quiet social-proof setup, no person visible)",
              "Text-dominant hero (headline leads, product supports)",
              "Wife's text message overlay (screenshot-style text bubble + product)",
              "Component isolation (single part hero in extreme macro)",
            ],
            product: [
              "Hero shot + premium positioning (propped, 45-degree, warm light)",
              "Multi-angle grid + completeness (front/side/back/detail, e-commerce)",
              "Lifestyle context + use-case (where it fits in daily life)",
              "Before/after + transformation proof (clear visual difference)",
              "Feature callout + education (labels pointing to key features)",
              "Material close-up + quality signal (macro texture, craftsmanship)",
              "Scale-in-hand + relatability (shows real size, human element)",
              "Flat-lay bundle + value (product with accessories, overhead)",
              "Unboxing + first impression (emerging from box, opening experience)",
              "Action detail + mechanism proof (how it works money shot)",
            ],
            review: [
              "Bathroom counter + just received (water drops, packaging nearby, overhead light)",
              "Mirror selfie + genuine reaction (slightly smudged mirror, real lighting)",
              "Sink edge + mid-routine (wet surface, toothbrush nearby, morning chaos)",
              "Nightstand + daily driver (messy nightstand, warm lamp, lived-in)",
              "Kitchen counter + casual (coffee mug, mail clutter, everyday vibe)",
              "Shower ledge + in-context (other bottles, steam, tile grout visible)",
              "Travel bag + on-the-go (dopp kit, real toiletries, not curated)",
              "Wet hand holding + texture (soapy hands, bathroom background blurred)",
              "Overhead counter + collection (iPhone top-down, real clutter, authentic)",
              "Just opened package + first look (torn envelope, bed/couch, just arrived)",
            ],
          };
    const base = pools[scene] || pools.ad;
    return [...base];
  }

  normalizeKnowledgeVariantContext(rawContext = {}, fallback = {}) {
    const context =
      rawContext && typeof rawContext === "object" && !Array.isArray(rawContext)
        ? rawContext
        : {};
    const visual =
      context?.visual_elements &&
      typeof context.visual_elements === "object" &&
      !Array.isArray(context.visual_elements)
        ? context.visual_elements
        : {};
    const messaging =
      context?.messaging &&
      typeof context.messaging === "object" &&
      !Array.isArray(context.messaging)
        ? context.messaging
        : {};
    const environment =
      context?.environment &&
      typeof context.environment === "object" &&
      !Array.isArray(context.environment)
        ? context.environment
        : {};
    const emotional =
      context?.emotional_response &&
      typeof context.emotional_response === "object" &&
      !Array.isArray(context.emotional_response)
        ? context.emotional_response
        : {};
    const audience =
      context?.target_audience &&
      typeof context.target_audience === "object" &&
      !Array.isArray(context.target_audience)
        ? context.target_audience
        : {};

    return this.withVariantCopyFallback(
      {
        product_focus: String(
          context?.product_focus ||
            context?.product?.name ||
            fallback?.productFocus ||
            "",
        ).trim(),
        product_notes: String(
          context?.product_notes ||
            context?.product?.notes ||
            fallback?.productNotes ||
            "",
        ).trim(),
        promptChannel: String(
          context?.promptChannel || fallback?.promptChannel || "",
        ).trim(),
        focusArea: String(
          context?.focusArea || fallback?.focusArea || "",
        ).trim(),
        visual_elements: {
          primary_hook: String(visual?.primary_hook || "").trim(),
          composition: String(visual?.composition || "").trim(),
          key_visuals: this.normalizeCreativeStringList(visual?.key_visuals, 6),
          lighting_style: String(visual?.lighting_style || "").trim(),
          lighting_direction: String(visual?.lighting_direction || "").trim(),
          color_palette: this.normalizeCreativeStringList(
            visual?.color_palette,
            6,
          ),
          text_overlays: String(visual?.text_overlays || "").trim(),
        },
        messaging: {
          value_proposition: String(messaging?.value_proposition || "").trim(),
          cta: String(messaging?.cta || "").trim(),
        },
        environment: {
          setting: String(environment?.setting || "").trim(),
          backdrop: String(environment?.backdrop || "").trim(),
          props: this.normalizeCreativeStringList(environment?.props, 6),
        },
        emotional_response: {
          mood: String(emotional?.mood || "").trim(),
        },
        target_audience: {
          audience_segment: String(
            audience?.audience_segment || audience?.persona || "",
          ).trim(),
        },
      },
      {
        specialInstructions: fallback?.specialInstructions || "",
        promptChannel: fallback?.promptChannel || "",
        productFocus: fallback?.productFocus || "",
        focusArea: fallback?.focusArea || "",
      },
    );
  }

  async generateKnowledgeBasedVariants(options = {}) {
    const {
      quantity = 3,
      creativity = 0.7,
      focusArea = "Visual Hook",
      model = DEFAULT_VARIANT_MODEL,
      sceneMode = "mixed",
      specialInstructions = "",
      knowledgeFolders = [],
      tags = [],
      brandLibrary = {},
      aspectRatio = "",
      _previewOnly = false,
      promptOverride = "",
    } = options || {};

    const folders = Array.isArray(knowledgeFolders) ? knowledgeFolders : [];
    if (folders.length === 0) {
      throw new Error(
        "Knowledge-based generation requires at least one product folder",
      );
    }

    const provider = model.startsWith("gemini")
      ? "gemini"
      : model.startsWith("claude")
        ? "claude"
        : "openai";
    const trimmedSpecialInstructions = String(specialInstructions || "").trim();
    const normalizedSceneMode = this.normalizeSceneMode(sceneMode);
    const folderNames = folders
      .map((folder) => String(folder?.name || "").trim())
      .filter(Boolean);
    const defaultProductFocus = folderNames.join(", ");

    const buildPromptChannelPool = (count) => {
      const baseChannels = this.getSceneModeChannelPool(
        normalizedSceneMode,
        "knowledge",
        focusArea,
      );
      const channels = [];
      for (let i = 0; i < count; i += 1) {
        channels.push(
          i < baseChannels.length
            ? baseChannels[i]
            : `Alternate fresh concept ${i + 1} focused on ${focusArea}`,
        );
      }
      return channels;
    };

    const buildPrompt = (count, strict, channel) => {
      const contextPayload = this.buildScratchGenerationContext({
        knowledgeFolders: folders,
        tags,
        brandLibrary,
        focusArea,
        sceneMode: normalizedSceneMode,
        creativity,
        specialInstructions: trimmedSpecialInstructions,
        promptChannel: channel || "",
        aspectRatio,
      });
      const primaryTask = [
        `Generate ${count} net-new static ad concept${count === 1 ? "" : "s"} for image creation from scratch.`,
        "These must be fresh creatives, not remixes of uploaded analyses.",
        "Treat the CONTEXT JSON as the only source of truth for product identity, product image details, tags, settings, brand info, directions, and constraints.",
        "If multiple products are present, either choose one clearly or create a bundle only when the context supports it.",
        channel
          ? `Prompt channel: ${channel}. This channel specifies BOTH the visual direction AND the ad angle/desire — the headline and copy must reflect the specific angle and desire, not generic product claims.`
          : "",
        `Focus area: ${focusArea}.`,
        `Scene mode: ${normalizedSceneMode}.`,
      ]
        .filter(Boolean)
        .join(" ");
      return `You are a world-class direct response creative strategist, visual advertiser, and brand performance thinker.

Your job is to generate high-converting creative ideas, image prompts, static ad concepts, short video concepts, and supporting copy for a DTC ecommerce brand.

You will be given a CONTEXT JSON. You must treat that JSON as the source of truth for:
- brand identity
- visual style
- customer psychology
- product details
- creative strategy
- tone
- formatting preferences
- ad platform constraints
- performance goals

You must deeply study the CONTEXT JSON before generating anything.

========================
CONTEXT JSON
========================
${JSON.stringify(contextPayload, null, 2)}

========================
PRIMARY TASK
========================
${primaryTask}

========================
OUTPUT TYPE
========================
static_ad_concepts

Valid OUTPUT_TYPE examples:
- static_ad_concepts
- image_generation_prompts
- short_form_video_concepts
- pdp_image_concepts
- headline_variations
- offer_angles
- hook_bank
- competitor-style reinterpretations
- campaign_theme_generation

${normalizedSceneMode === "ad" ? this.buildNativeAdSystemPrompt() : ""}

========================
CORE RULES
========================

1. Use the CONTEXT JSON as the highest-priority source of truth.
2. Prioritize performance over decoration.
3. Every idea must be:
   - instantly understandable
   - emotionally resonant
   - visually strong
   - easy to process in 1-2 seconds
4. Avoid generic ecommerce output.
5. Avoid bland, corporate, safe, overexplained ideas.
6. Prefer ideas that feel:
   - clever
   - compressed
   - memorable
   - identity-driven
   - socially shareable
7. Sell through:
   - visual proof
   - emotional tension
   - contrast
   - pattern interruption
   - fast cognitive fluency
8. Do not rely on feature dumping.
9. One creative = one core idea.
10. Show more than you tell.
11. Unless the CONTEXT JSON or special instructions explicitly request a text-free creative, include on-image copy.
12. The creative must be understandable without outside context in under 2 seconds.

========================
CREATIVE PHILOSOPHY
========================

When generating ideas, apply these principles:

- Existing desire is more powerful than created desire.
- Channel what the customer already feels.
- Build around pain, insecurity, desire, identity, and relief.
- Strong ads compress one powerful idea into something instantly processed.
- The best ads feel like:
  - a realization
  - a truth
  - a joke
  - a sharp observation
  - a visual proof
- The image carries proof.
- The headline carries interpretation.
- Contrast creates clarity.
- Cleverness should never reduce clarity.
- The ad should pull attention, not beg for it.
- Participation increases persuasion:
  the viewer should connect at least one dot themselves.

========================
MANDATORY PSYCHOLOGY FILTER
========================

For every concept, identify:

1. The core emotional driver:
   - fear
   - embarrassment
   - frustration
   - disgust
   - confidence
   - desire
   - control
   - relief
   - partner approval
   - status
   - masculinity
   - self-respect

2. The belief being activated:
   - what the customer already suspects
   - what truth the ad is surfacing
   - what internal feeling is being made visible

3. The tension:
   - safe vs risky
   - gross vs clean
   - confidence vs embarrassment
   - control vs chaos
   - prepared vs exposed
   - premium vs cheap
   - direct vs indirect
   - old way vs smarter way

4. The proof:
   - visual metaphor
   - before/after implication
   - symbolic contrast
   - tactile detail
   - product-in-use realism
   - reaction or outcome cue

========================
VISUAL EXECUTION RULES
========================

When generating visual ideas or prompts:

- Respect all visual preferences in the CONTEXT JSON.
- Keep compositions intentional, not crowded.
- Use strong hierarchy.
- The focal point must be obvious.
- Product must look premium, tactile, real, and materially believable.
- If human skin or body parts are shown, make them realistic, not airbrushed.
- If discomfort, redness, bumps, sweat, texture, or irritation are shown, make them believable but controlled.
- Avoid fake-looking AI hands, plastic skin, or floating objects.
- Match lighting and atmosphere to the intended emotional effect:
  - harsh for pain/problem
  - clean for clinical/proof
  - soft premium for relief/outcome
  - high contrast for thumbstop
- Keep branding memorable but not forced.
- If text appears, make it feel like a real ad system:
  - obvious placement in a safe reading zone
  - strong hierarchy
  - high contrast
  - max two font styles
  - understandable without outside context

========================
COPY RULES
========================

When generating text:

- Keep headlines compressed.
- Prefer short, sharp phrasing.
- Avoid fluff.
- Avoid cliche direct-response phrases unless the CONTEXT JSON explicitly wants them.
- Avoid sounding like a generic dropshipper.
- Use language the target customer would naturally think or say.
- Prefer:
  - tension
  - irony
  - truth
  - cultural familiarity
  - phrase flips
  - understated confidence
- Unless special instructions explicitly request a text-free creative, every concept should include:
  - a short on-image headline
  - a short supporting line
  - an optional CTA only when it improves clarity or direct-response intent

If platform restrictions are present in the CONTEXT JSON, obey them fully.

========================
QUALITY CONTROL
========================

Before finalizing, reject any idea that:
- is generic
- could apply to any random brand
- lacks emotional tension
- explains too much
- has weak visual logic
- does not reflect the CONTEXT JSON
- feels like a template instead of a real insight

Only output ideas that feel specific, useful, and strong enough to test in a real ad account.

========================
INTERNAL RESPONSE CONTRACT
========================

Return ONLY JSON in this exact shape:
{
  "variants": [
    {
      "title": "",
      "rationale": "",
      "context": {
        "product_focus": "",
        "product_notes": "",
        "promptChannel": "",
        "focusArea": "",
        "visual_elements": {
          "primary_hook": "",
          "composition": "",
          "key_visuals": ["", ""],
          "lighting_style": "",
          "lighting_direction": "",
          "color_palette": ["", ""],
          "text_overlays": ""
        },
        "messaging": {
          "value_proposition": "",
          "cta": ""
        },
        "environment": {
          "setting": "",
          "backdrop": "",
          "props": ["", ""]
        },
        "emotional_response": {
          "mood": ""
        },
        "target_audience": {
          "audience_segment": ""
        }
      }
    }
  ]
}

JSON rules:
- Each variant is one static ad concept translated into the internal context schema.
- title = concept name.
- rationale = why it works in 1-2 tight sentences.
- context.product_focus must use only selected product/folder names from the CONTEXT JSON.
- context.product_notes must contain only product facts supported by the CONTEXT JSON.
- CRITICAL: The product visual identity comes ONLY from the knowledge folder reference images. Do not invent or reimagine the product appearance.
- Headlines (text_overlays) must draw from ALL knowledge sources: copy library sample_headlines, brand message, product notes, AI profile, tags. Combine insights for specific, compelling headlines — never generic.
- context.promptChannel must reflect the requested prompt channel.
- context.focusArea must reflect the requested focus area.
- context.visual_elements.primary_hook = core idea.
- context.visual_elements.composition = visual composition.
- context.visual_elements.key_visuals = 2-6 proof-oriented visual elements.
- context.visual_elements.lighting_style and lighting_direction must match the emotional effect.
- context.visual_elements.color_palette must follow the brand/scene direction from the CONTEXT JSON.
- Unless special instructions explicitly request a text-free creative, context.visual_elements.text_overlays must be a readable 2-6 word headline.
- Unless special instructions explicitly request a text-free creative, context.messaging.value_proposition must be a short supporting line.
- context.messaging.cta is optional. Only include it when a CTA improves clarity, offer communication, or direct-response intent.
- Make the ad understandable without outside context: the text and image together should explain the point in one glance.
- Bake text placement, hierarchy, contrast, and typography intent into composition choices so the creative reads clearly on mobile.
- context.environment.setting, backdrop, and props must make the visual execution obvious.
- context.emotional_response.mood should capture the dominant emotional driver and tension.
- context.target_audience.audience_segment should reflect the audience from the CONTEXT JSON.
- Do not invent claims, ingredients, certifications, product forms, packaging, colors, or directions not present in the CONTEXT JSON.
- If a detail is missing from the CONTEXT JSON, stay broad and truthful instead of guessing.
${strict ? "Return ONLY JSON. No extra text." : ""}${this.buildAnalysisGuidance()}${this.buildAdCopyGuidance()}${this.buildAiGuidance()}`;
    };

    const buildRepairPrompt = (
      brokenContent,
    ) => `You repair invalid JSON outputs for a creative generation system.
Convert the broken response below into VALID JSON matching this schema exactly:
{
  "variants": [
    {
      "title": "",
      "rationale": "",
      "context": {
        "product_focus": "",
        "product_notes": "",
        "promptChannel": "",
        "focusArea": "",
        "visual_elements": {
          "primary_hook": "",
          "composition": "",
          "key_visuals": ["", ""],
          "lighting_style": "",
          "lighting_direction": "",
          "color_palette": ["", ""],
          "text_overlays": ""
        },
        "messaging": {
          "value_proposition": "",
          "cta": ""
        },
        "environment": {
          "setting": "",
          "backdrop": "",
          "props": ["", ""]
        },
        "emotional_response": {
          "mood": ""
        },
        "target_audience": {
          "audience_segment": ""
        }
      }
    }
  ]
}
Rules:
- Return ONLY valid JSON.
- Keep the original intent where possible.
- If a field is missing, use an empty string or empty array.

Broken response:
${String(brokenContent || "").slice(0, 12000)}`;

    const resolveMaxOutputTokens = (strict) => (strict ? 3200 : 2200);

    if (_previewOnly) {
      return { prompt: buildPrompt(1, false, buildPromptChannelPool(1)[0] || "") };
    }

    const parseVariantResponse = (content) => {
      try {
        const parsed = this.extractJSONFromCandidates(content);
        const list = Array.isArray(parsed)
          ? parsed
          : parsed?.variants || parsed?.items || parsed?.data;
        if (!Array.isArray(list)) {
          const error = new Error("Model did not return a variants array");
          error.modelContent = content;
          throw error;
        }
        return list;
      } catch (error) {
        if (!error.modelContent) {
          error.modelContent = content;
        }
        throw error;
      }
    };

    const run = async (count, strict, channel, brokenContent = "") => {
      const prompt =
        brokenContent && strict
          ? buildRepairPrompt(brokenContent)
          : promptOverride || buildPrompt(count, strict, channel);

      let content = "";
      if (provider === "openai") {
        const response = await this.callOpenAIChat({
          model,
          messages: [{ role: "user", content: prompt }],
          temperature: strict ? 0.3 : creativity,
          max_completion_tokens: resolveMaxOutputTokens(strict),
          response_format: { type: "json_object" },
        });
        content = this.getOpenAIChatContentCandidatesOrThrow(response);
      } else if (provider === "gemini") {
        const data = await this.callGeminiGenerate(model, {
          contents: [{ parts: [{ text: prompt }] }],
          generationConfig: {
            temperature: strict ? 0.25 : creativity,
            maxOutputTokens: resolveMaxOutputTokens(strict),
            responseMimeType: "application/json",
          },
        });
        content = data?.candidates?.[0]?.content?.parts
          ?.map((part) => part.text)
          .filter(Boolean)
          .join("\n");
      } else {
        content = await this.callClaude({
          model,
          max_tokens: resolveMaxOutputTokens(strict),
          temperature: strict ? 0.2 : creativity,
          messages: [
            { role: "user", content: [{ type: "text", text: prompt }] },
          ],
        });
      }

      const list = parseVariantResponse(content);

      return list.map((item, index) => {
        const normalizedContext = this.normalizeKnowledgeVariantContext(
          item?.context || item,
          {
            productFocus: defaultProductFocus,
            productNotes: folders
              .map((folder) => String(folder?.notes || "").trim())
              .filter(Boolean)
              .join(" | "),
            promptChannel: channel || "",
            focusArea,
            specialInstructions: trimmedSpecialInstructions,
          },
        );
        return {
          id: item?.id ?? `${Date.now()}-${index}`,
          title:
            String(item?.title || "").trim() ||
            `${normalizedContext.promptChannel || "Fresh Concept"} ${index + 1}`.trim(),
          rationale: String(item?.rationale || "").trim(),
          context: normalizedContext,
          promptChannel: normalizedContext.promptChannel || channel || "",
        };
      });
    };

    const safeQuantity = Math.max(1, Math.min(quantity, MAX_VARIANT_QUANTITY));
    const maxPromptChannels = 10;
    const baseTime = Date.now();
    const channelCount = Math.min(maxPromptChannels, safeQuantity);
    const promptChannels = buildPromptChannelPool(channelCount);
    const jobsByChannel = Array.from({ length: channelCount }, () => []);
    for (let index = 0; index < safeQuantity; index += 1) {
      const channelIndex = index % channelCount;
      const promptNumber = Math.floor(index / channelCount) + 1;
      jobsByChannel[channelIndex].push({
        index,
        channel: promptChannels[channelIndex],
        promptNumber,
      });
    }
    const out = new Array(safeQuantity);

    const worker = async (jobs) => {
      for (const job of jobs) {
        const scopedChannel =
          job.promptNumber > 1
            ? `${job.channel} • concept ${job.promptNumber}`
            : job.channel;
        try {
          const generated = await run(1, false, scopedChannel);
          out[job.index] = generated?.[0] || null;
        } catch (error) {
          const message = String(error?.message || "");
          const shouldRetry =
            message.includes("Incomplete JSON") ||
            message.includes("No JSON") ||
            message.includes("Empty response") ||
            message.includes("variants array");
          if (!shouldRetry) {
            throw new Error(`Fresh concept generation failed: ${message}`);
          }
          const retryGenerated = await run(
            1,
            true,
            scopedChannel,
            error?.modelContent || "",
          );
          out[job.index] = retryGenerated?.[0] || null;
        }
      }
    };

    await Promise.all(jobsByChannel.map((jobs) => worker(jobs)));

    return out
      .filter(Boolean)
      .slice(0, safeQuantity)
      .map((item, index) => ({
        ...item,
        id: item.id ?? `${baseTime}-${index}`,
        title: item.title ?? `Fresh Concept ${index + 1}`,
      }));
  }

  async generateBlogFromBrief(brief = {}) {
    const topic = String(brief.topic || "").trim();
    const readingLevel = String(brief.readingLevel || "").trim();
    const blogType = String(brief.blogType || "").trim();
    const lengthTarget = Number(brief.lengthTarget) || 1000;
    const tone = String(brief.tone || "").trim();
    const imageCount = Math.max(0, Number(brief.imageCount) || 0);
    const imageAspectRatio = String(brief.imageAspectRatio || "16:9").trim();
    const brand = brief.brandLibrary || {};
    const tags = Array.isArray(brief.tags) ? brief.tags : [];
    const creativeSummary = this.buildCreativeKnowledgeSummary(
      brief.knowledgeDb || [],
    );
    const seoOverrides = brief.seoData || null;
    const seoGuidance = this.buildBlogSeoGuidance(seoOverrides);
    const blogGuidance = this.buildBlogKnowledgeGuidance();
    const aiGuidance = this.buildAiGuidance();
    const analysisGuidance = this.buildAnalysisGuidance();
    const copyGuidance = this.buildAdCopyGuidance();
    const tagSummary = this.buildTagSummary(tags, { limit: 6 });

    const lengthGuide =
      lengthTarget <= 700
        ? "600-800 words"
        : lengthTarget <= 1200
          ? "1000-1300 words"
          : "1600-2000 words";
    const sectionCount =
      lengthTarget <= 700 ? "3-4" : lengthTarget <= 1200 ? "4-5" : "5-6";
    const maxTokens =
      lengthTarget <= 700 ? 2600 : lengthTarget <= 1200 ? 3600 : 4800;

    const prompt = `You are a senior content strategist and SEO copywriter. Create a high-quality blog post.

Topic: ${topic || "Untitled topic"}
Blog type: ${blogType || "How-to / Guide"}
Reading level: ${readingLevel || "Grade 8"}
Target length: ${lengthGuide}
Tone: ${tone || "Helpful, confident, on-brand"}
Image count: ${imageCount}
Image aspect ratio: ${imageAspectRatio}

Brand context:
- Brand name: ${brand?.name || "Unspecified"}
- Brand message: ${brand?.message || "None"}
- Brand colors: ${(brand?.colors || []).join(", ") || "None"}

Tags: ${tagSummary || "None"}
${creativeSummary}
${blogGuidance}
${seoGuidance}
${analysisGuidance}
${copyGuidance}
${aiGuidance}

Requirements:
- Make it publish-ready and structured from start to finish, not an outline.
- Write a real article with a clear narrative arc: strong intro, ${sectionCount} body sections, conclusion, CTA.
- Intro, each section body, and conclusion should read like finished prose with paragraph flow.
- Use blank lines between paragraphs inside any multi-paragraph string value.
- Each section body should be 2-3 short paragraphs, not one sentence.
- Keep the copy specific, useful, and skimmable without becoming a bullet dump.
- Use headings that progress logically from problem/angle to proof/action.
- Do NOT invent claims that aren't supported by the knowledge base.
- Provide SEO metadata and schema markup when possible.
- Generate ${imageCount} image ideas aligned with the topic and sections.
- If the knowledge base is thin, stay general but still deliver a complete post.

Return ONLY JSON in this schema:
{
  "title": "",
  "subtitle": "",
  "intro": "",
  "sections": [
    { "heading": "", "body": "" }
  ],
  "conclusion": "",
  "cta": "",
  "seo": {
    "metaTitle": "",
    "metaDescription": "",
    "keywords": ["", ""],
    "slug": "",
    "canonicalUrl": "",
    "schemaMarkup": ""
  },
  "imageIdeas": [
    { "prompt": "", "alt": "" }
  ]
}
`;

    if (brief._previewOnly) return { prompt };
    const modelName = this.resolveBlogModel();
    const content = await this.generateTextWithModel(modelName, brief.promptOverride || prompt, {
      temperature: 0.4,
      maxTokens,
    });
    return this.extractJSON(content);
  }

  async reviseBlogText(payload = {}) {
    const mode = String(payload.mode || "revise")
      .trim()
      .toLowerCase();
    const text = String(payload.text || "").trim();
    if (!text) return "";
    const note = String(payload.note || "").trim();
    const context = payload.context || {};
    const modeLine =
      mode === "expand"
        ? "Expand the text with more depth and concrete detail. Keep tone consistent."
        : mode === "custom"
          ? "Apply the custom instruction below while preserving the intent."
          : "Revise for clarity, flow, and concision without changing meaning.";
    const noteLine =
      mode === "custom"
        ? `Custom instruction: ${note || "Improve the text."}`
        : note
          ? `User note: ${note}`
          : "";

    const prompt = `You are an expert editor. ${modeLine}
${noteLine}

Context:
- Blog title: ${context?.title || ""}
- Section: ${context?.sectionTitle || context?.section || ""}
- Topic: ${context?.topic || ""}
- Reading level: ${context?.readingLevel || ""}
- Blog type: ${context?.blogType || ""}
- Tone: ${context?.tone || ""}
${this.buildBlogKnowledgeGuidance()}
${this.buildBlogSeoGuidance(context?.seoData || null)}
${this.buildAnalysisGuidance()}
${this.buildAdCopyGuidance()}
${this.buildAiGuidance()}

Original text:
${text}

Return ONLY the revised text.`;

    const modelName = this.resolveBlogModel();
    const revised = await this.generateTextWithModel(modelName, prompt, {
      temperature: 0.35,
      maxTokens: 800,
    });
    return String(revised || "").trim();
  }

  async generateBlogCssFromPrompt(payload = {}) {
    const promptText = String(payload.prompt || "").trim();
    if (!promptText) return "";
    const presetLabel = String(payload.presetLabel || "").trim();
    const prompt = `You are a web designer. Create a CSS snippet for a blog article.
Rules:
- Scope only to ".blog-article" and its children.
- Use CSS variables --blog-font and --blog-accent.
- Keep it clean, responsive, and readable.
- Return ONLY CSS (no markdown).

Style direction: ${presetLabel || "Custom"}
User prompt: ${promptText}
`;
    if (payload._previewOnly) {
      return { prompt };
    }
    const modelName = this.resolveBlogModel();
    const css = await this.generateTextWithModel(modelName, prompt, {
      temperature: 0.3,
      maxTokens: 700,
    });
    return String(css || "").trim();
  }

  async generateBlogImage(payload = {}) {
    const prompt = String(payload.prompt || "").trim();
    if (!prompt) {
      throw new Error("Image prompt missing");
    }
    const aspectRatio = String(payload.aspectRatio || "16:9");
    const imageModel = String(payload.imageModel || DEFAULT_IMAGE_MODEL);
    const knowledge = {
      aspectRatio,
      imageModel,
      images: Array.isArray(payload.referenceImages)
        ? payload.referenceImages
        : [],
      brand: payload.brandLibrary || {},
    };

    if (
      this.shouldUseReferenceAwareImageRenderer(
        imageModel,
        knowledge,
        aspectRatio,
      )
    ) {
      return await this.generateImageFromContextWithGemini(prompt, {
        ...knowledge,
        referenceImages: this.collectImageReferenceImages(knowledge),
      });
    }

    if (imageModel.startsWith("gpt")) {
      const size = this.getOpenAISizeForAspect(aspectRatio);
      const response = await this.callOpenAIImages({
        model: OPENAI_IMAGE_MODEL,
        prompt,
        size,
      });
      const base64 = response?.data?.[0]?.b64_json;
      if (!base64) {
        throw new Error("No image data returned from OpenAI");
      }
      return await this.enforceAspectRatioOnDataUrl(
        `data:image/png;base64,${base64}`,
        aspectRatio,
        "image/png",
      );
    }

    if (imageModel.startsWith("claude")) {
      const refinedPrompt = await this.refineImagePromptWithClaude(
        prompt,
        imageModel,
      );
      try {
        return await this.generateImageFromContextWithGemini(
          refinedPrompt,
          knowledge,
        );
      } catch (error) {
        const size = this.getOpenAISizeForAspect(aspectRatio);
        const response = await this.callOpenAIImages({
          model: OPENAI_IMAGE_MODEL,
          prompt: refinedPrompt,
          size,
        });
        const base64 = response?.data?.[0]?.b64_json;
        if (!base64) {
          throw new Error("No image data returned from OpenAI");
        }
        return await this.enforceAspectRatioOnDataUrl(
          `data:image/png;base64,${base64}`,
          aspectRatio,
          "image/png",
        );
      }
    }

    return await this.generateImageFromContextWithGemini(
      prompt,
      knowledge,
      imageModel,
    );
  }

  async generateBlogAltText(payload = {}) {
    const promptText = String(payload.prompt || "").trim();
    if (!promptText) return "";
    const topic = String(payload.topic || "").trim();
    const prompt = `Write concise, descriptive alt text for a blog image.
Constraints:
- Max 120 characters.
- No quotes, no markdown.
- Describe the visible content clearly.

Blog topic: ${topic || "General"}
Image prompt: ${promptText}
`;
    const data = await this.callGeminiGenerate(GEMINI_VISION_MODEL, {
      contents: [{ parts: [{ text: prompt }] }],
      generationConfig: {
        temperature: 0.2,
        maxOutputTokens: 120,
      },
    });
    const text = data?.candidates?.[0]?.content?.parts
      ?.map((part) => part.text)
      .filter(Boolean)
      .join("\n");
    return String(text || "").trim();
  }

  normalizeSoraModel(modelId) {
    const raw = String(modelId || "").trim();
    if (!raw) return DEFAULT_VIDEO_MODEL;
    const normalized = raw.replace(/_/g, "-").toLowerCase();
    const aliases = {
      sora2: "sora-2",
      "sora-2": "sora-2",
      sora2pro: DEFAULT_VIDEO_MODEL,
      "sora-2-pro": DEFAULT_VIDEO_MODEL,
    };
    return aliases[normalized] || normalized;
  }

  resolveSoraSeconds(rawDuration) {
    let seconds = Number(rawDuration);
    if (!Number.isFinite(seconds)) seconds = 8;
    seconds = Math.min(60, Math.max(1, seconds));
    const allowed = [4, 8, 12];
    let nearest = allowed[0];
    for (const candidate of allowed) {
      if (Math.abs(candidate - seconds) < Math.abs(nearest - seconds)) {
        nearest = candidate;
      }
    }
    return nearest;
  }

  resolveSoraSize(aspectRatio, resolution, modelId) {
    const ratio = String(aspectRatio || "16:9").trim();
    const normalizedRatio = ratio === "9:16" ? "9:16" : "16:9";
    const normalizedResolution = String(resolution || "720p").toLowerCase();
    const normalizedModel = this.normalizeSoraModel(modelId);
    const wantsHigh = normalizedResolution === "pro-high";

    if (wantsHigh && normalizedModel === DEFAULT_VIDEO_MODEL) {
      return normalizedRatio === "9:16" ? "1024x1792" : "1792x1024";
    }

    return normalizedRatio === "9:16" ? "720x1280" : "1280x720";
  }

  async callClaude({ model, messages, temperature, max_tokens }) {
    const data = await this.callClaudeMessages({
      model,
      max_tokens: max_tokens || 2048,
      temperature,
      messages,
    });
    const text = data?.content
      ?.map((part) => part.text)
      .filter(Boolean)
      .join("\n");
    return text || "";
  }

  async uploadGeminiFile(file, mimeType, signal) {
    const uploaded = await this.uploadWorkspaceAssetForGemini(
      file,
      mimeType,
      signal,
    );
    const isRetryableUploadError = (error) => {
      const status = Number(error?.status || 0);
      const message = String(error?.message || "").toLowerCase();
      return (
        status === 0 ||
        status === 429 ||
        status === 500 ||
        status === 502 ||
        status === 503 ||
        status === 504 ||
        message.includes("failed to upload file") ||
        message.includes("failed to create file") ||
        message.includes("rate limit") ||
        message.includes("too many requests") ||
        message.includes("resource exhausted") ||
        message.includes("quota") ||
        message.includes("temporarily unavailable") ||
        message.includes("try again later")
      );
    };
    const maxAttempts = 4;
    let lastError = null;
    for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
      if (signal?.aborted) {
        throw new Error("Analysis cancelled");
      }
      try {
        const result = await this.callGeminiUpload(
          {
            name: file.name || "creative-upload",
            mimeType,
            url: uploaded.url,
          },
          signal,
        );
        const fileInfo = result.file || result;
        return {
          name: fileInfo.name,
          uri: fileInfo.uri,
          mimeType: fileInfo.mimeType || mimeType,
        };
      } catch (error) {
        lastError = error;
        if (!isRetryableUploadError(error) || attempt >= maxAttempts) {
          break;
        }
        const jitterMs = Math.floor(Math.random() * 220);
        const waitMs = 350 * attempt + jitterMs;
        await this.wait(waitMs, signal);
      }
    }
    const detail = String(lastError?.message || "Failed to create file.");
    throw new Error(`Gemini upload failed after retries: ${detail}`);
  }

  async waitForGeminiFileActive(name, signal) {
    const maxAttempts = 20;
    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
      if (signal?.aborted) {
        throw new Error("Analysis cancelled");
      }
      const data = await this.callGeminiFile({ name }, signal);
      const fileInfo = data.file || data;
      const rawState = fileInfo.state;
      const state =
        typeof rawState === "string" ? rawState : rawState?.name || "";
      if (state === "ACTIVE" || state === "READY") {
        return;
      }
      if (state === "FAILED") {
        throw new Error("Gemini file processing failed");
      }
      await new Promise((resolve) => setTimeout(resolve, 1000));
    }
  }

  async getGeminiVideoParts(file, signal) {
    const mimeType = file.type || "video/mp4";
    const inlineLimit = 18 * 1024 * 1024;
    const base64Estimate = Math.ceil(file.size / 3) * 4;
    const useFileApi =
      file.size > inlineLimit || base64Estimate > 20 * 1024 * 1024;

    if (!useFileApi) {
      const base64 = await this.fileToBase64(file);
      if (signal?.aborted) {
        throw new Error("Analysis cancelled");
      }
      return {
        mimeType,
        parts: [
          {
            inline_data: {
              mime_type: mimeType,
              data: base64,
            },
          },
        ],
      };
    }

    const uploaded = await this.uploadGeminiFile(file, mimeType, signal);
    await this.waitForGeminiFileActive(uploaded.name, signal);
    return {
      mimeType,
      parts: [
        {
          file_data: {
            mime_type: uploaded.mimeType,
            file_uri: uploaded.uri,
          },
        },
      ],
    };
  }

  async requestGeminiVideoAnalysis(parts, prompt, modelName, signal, strict) {
    if (signal?.aborted) {
      throw new Error("Analysis cancelled");
    }
    const finalPrompt = strict
      ? `${prompt}\nReturn ONLY JSON. No extra text or markdown.`
      : prompt;
    const data = await this.callGeminiGenerate(
      modelName,
      {
        contents: [
          {
            parts: [...parts, { text: finalPrompt }],
          },
        ],
      },
      signal,
    );
    const text = data?.candidates?.[0]?.content?.parts
      ?.map((part) => part.text)
      .filter(Boolean)
      .join("\n");
    return this.extractJSON(text);
  }

  selectVideoSegmentsForFallback(
    segments,
    maxSegments = MAX_VIDEO_IMPORT_SEGMENTS,
  ) {
    const list = Array.isArray(segments) ? segments : [];
    const safeMax = Math.max(
      1,
      Math.floor(Number(maxSegments) || MAX_VIDEO_IMPORT_SEGMENTS),
    );
    if (list.length <= safeMax) {
      return list;
    }

    const stride = Math.max(1, Math.ceil(list.length / safeMax));
    const selected = [];
    for (let i = 0; i < list.length; i += stride) {
      if (selected.length >= safeMax) break;
      selected.push(list[i]);
    }
    const last = list[list.length - 1];
    if (
      last &&
      !selected.some((entry) => entry?.batchIndex === last?.batchIndex)
    ) {
      selected.push(last);
    }
    return selected.slice(0, safeMax);
  }

  async captureVideoFrame(source, timeSec, signal, options = {}) {
    const isBlobSource = source instanceof Blob;
    if (!isBlobSource && typeof source !== "string") {
      throw new Error("Invalid video source");
    }

    const url = isBlobSource ? URL.createObjectURL(source) : String(source);
    const targetSec = Number.isFinite(Number(timeSec)) ? Number(timeSec) : 0;
    const maxDim = Number.isFinite(Number(options.maxDim))
      ? Number(options.maxDim)
      : MAX_VIDEO_FRAME_DIMENSION;
    const quality = Number.isFinite(Number(options.quality))
      ? Number(options.quality)
      : 0.55;

    const cleanupVideo = (video) => {
      try {
        if (video) {
          video.pause();
          video.removeAttribute("src");
          video.load();
        }
      } catch (error) {
        // best-effort cleanup
      }
    };

    let video = null;
    try {
      if (signal?.aborted) {
        throw new Error("Analysis cancelled");
      }

      video = await this.loadVideoFromUrl(url);
      video.muted = true;
      video.playsInline = true;

      const duration = Number(video?.duration || 0);
      const clampedTime =
        Number.isFinite(duration) && duration > 0
          ? Math.max(0, Math.min(targetSec, Math.max(0, duration - 0.05)))
          : Math.max(0, targetSec);

      const seek = async () =>
        new Promise((resolve, reject) => {
          if (signal?.aborted) {
            reject(new Error("Analysis cancelled"));
            return;
          }
          const onError = (error) => {
            video.removeEventListener("seeked", onSeeked);
            reject(error || new Error("Video seek failed"));
          };
          const onSeeked = () => {
            video.removeEventListener("error", onError);
            setTimeout(resolve, 50);
          };
          const onAbort = () => {
            video.removeEventListener("seeked", onSeeked);
            video.removeEventListener("error", onError);
            reject(new Error("Analysis cancelled"));
          };
          if (signal) {
            if (signal.aborted) {
              onAbort();
              return;
            }
            signal.addEventListener("abort", onAbort, { once: true });
          }
          video.addEventListener("seeked", onSeeked, { once: true });
          video.addEventListener("error", onError, { once: true });
          try {
            video.currentTime = clampedTime;
            if (Math.abs(video.currentTime - clampedTime) < 0.02) {
              setTimeout(() => {
                video.removeEventListener("seeked", onSeeked);
                video.removeEventListener("error", onError);
                if (signal) {
                  signal.removeEventListener("abort", onAbort);
                }
                resolve();
              }, 50);
            }
          } catch (error) {
            video.removeEventListener("seeked", onSeeked);
            video.removeEventListener("error", onError);
            if (signal) {
              signal.removeEventListener("abort", onAbort);
            }
            reject(error);
          }
        });

      await seek();
      if (signal?.aborted) {
        throw new Error("Analysis cancelled");
      }

      await new Promise((resolve) => {
        if (video.readyState >= 2) {
          resolve();
          return;
        }
        const onLoadedData = () => {
          video.removeEventListener("loadeddata", onLoadedData);
          resolve();
        };
        video.addEventListener("loadeddata", onLoadedData, { once: true });
        setTimeout(() => {
          video.removeEventListener("loadeddata", onLoadedData);
          resolve();
        }, 1000);
      });

      const videoWidth = Number(video.videoWidth || 0);
      const videoHeight = Number(video.videoHeight || 0);
      if (!videoWidth || !videoHeight) {
        throw new Error("Failed to read video frame");
      }
      const scale = Math.min(1, maxDim / Math.max(videoWidth, videoHeight));
      const width = Math.max(1, Math.round(videoWidth * scale));
      const height = Math.max(1, Math.round(videoHeight * scale));
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const context = canvas.getContext("2d");
      if (!context) {
        throw new Error("Canvas context unavailable");
      }
      context.drawImage(video, 0, 0, width, height);
      const outBlob = await new Promise((resolve) => {
        canvas.toBlob((blob) => resolve(blob), "image/jpeg", quality);
      });
      if (!outBlob) {
        throw new Error("Failed to encode video frame");
      }
      return {
        blob: outBlob,
        mimeType: outBlob.type || "image/jpeg",
      };
    } finally {
      cleanupVideo(video);
      if (isBlobSource) {
        URL.revokeObjectURL(url);
      }
    }
  }

  async captureVideoFrameForGemini(file, timeSec, signal, options = {}) {
    if (!(file instanceof Blob)) {
      throw new Error("Invalid video file");
    }
    const captured = await this.captureVideoFrame(
      file,
      timeSec,
      signal,
      options,
    );
    const base64 = await this.blobToBase64(captured.blob);
    if (!base64) {
      throw new Error("Failed to encode video frame");
    }
    return {
      inline_data: {
        mime_type: captured.mimeType || "image/jpeg",
        data: base64,
      },
    };
  }

  async buildVideoContinuityReferenceImages(
    source,
    segments = [],
    signal,
    options = {},
  ) {
    if (!source) return [];
    const selectedSegments = this.selectVideoSegmentsForFallback(
      segments,
      Math.max(1, Math.floor(Number(options.maxImages) || 3)),
    );
    const refs = [];
    for (let index = 0; index < selectedSegments.length; index += 1) {
      const segment = selectedSegments[index] || {};
      const midPoint =
        Number.isFinite(segment.startSec) && Number.isFinite(segment.endSec)
          ? segment.startSec +
            Math.max(0, Number(segment.endSec) - Number(segment.startSec)) * 0.5
          : Math.max(0, index * 2);
      try {
        const captured = await this.captureVideoFrame(
          source,
          midPoint,
          signal,
          {
            maxDim: Number(options.maxDim) || 768,
            quality: Number(options.quality) || 0.72,
          },
        );
        if (captured?.blob) {
          refs.push({
            blob: captured.blob,
            mimeType: captured.mimeType || "image/jpeg",
            name: `Continuity frame ${index + 1}`,
            role: "character-reference",
          });
        }
      } catch (error) {
        // Best-effort continuity references.
      }
    }
    return refs;
  }

  async buildVeoReferenceImages(referenceImages = [], options = {}) {
    const maxImages = Number.isFinite(options.maxImages)
      ? options.maxImages
      : 3;
    const maxPerImageBytes = Number.isFinite(options.maxPerImageBytes)
      ? options.maxPerImageBytes
      : 450_000;
    const maxTotalBytes = Number.isFinite(options.maxTotalBytes)
      ? options.maxTotalBytes
      : 900_000;
    const out = [];
    let totalBytes = 0;

    for (const image of referenceImages) {
      if (out.length >= maxImages) break;
      const resolved = await this.resolveReferenceImageBlob(image);
      if (!resolved?.blob) continue;

      let prepared = await this.prepareGeminiInlineImage(resolved.blob, {
        maxDim: 640,
        quality: 0.68,
      });
      if (!prepared?.base64) continue;

      let bytes = this.estimateBase64Bytes(prepared.base64);
      if (bytes > maxPerImageBytes) {
        prepared = await this.prepareGeminiInlineImage(resolved.blob, {
          maxDim: 512,
          quality: 0.58,
        });
        if (!prepared?.base64) continue;
        bytes = this.estimateBase64Bytes(prepared.base64);
      }

      if (bytes > maxPerImageBytes) continue;
      if (totalBytes + bytes > maxTotalBytes && out.length > 0) break;

      out.push({
        referenceType: "asset",
        image: {
          bytesBase64Encoded: prepared.base64,
          mimeType: prepared.mimeType || resolved.mimeType || "image/jpeg",
        },
      });
      totalBytes += bytes;
    }
    return out;
  }

  // Analyze a single captured frame with Claude (replaces the retired Gemini
  // video path). `framePart` is {inline_data:{mime_type, data(base64)}}.
  async analyzeFrameWithClaude(framePart, prompt, signal) {
    const data = framePart?.inline_data?.data;
    const mediaType = framePart?.inline_data?.mime_type || "image/jpeg";
    if (!data) return {};
    if (signal?.aborted) throw new Error("Analysis cancelled");
    const text = await this.callClaude({
      model: CLAUDE_DEFAULT_MODEL,
      max_tokens: 1500,
      messages: [
        {
          role: "user",
          content: [
            {
              type: "image",
              source: { type: "base64", media_type: mediaType, data },
            },
            { type: "text", text: prompt },
          ],
        },
      ],
    });
    return this.extractJSON(text) || {};
  }

  // Video analysis entry point: samples representative frames and analyzes each
  // with Claude. Claude vision works on frames, not raw video files.
  async analyzeVideoFrames(file, onProgress, signal) {
    const durationSec = await this.getVideoDuration(file);
    const segments = this.computeVideoSegments(durationSec);
    return this.analyzeVideoByFrameSampling(file, onProgress, signal, {
      durationSec,
      segments,
      segmentPolicy: "auto_6_8",
    });
  }

  async analyzeVideoByFrameSampling(file, onProgress, signal, options = {}) {
    const durationSec = Number.isFinite(Number(options.durationSec))
      ? Number(options.durationSec)
      : 0;
    const durationField = Number.isFinite(durationSec)
      ? Number(durationSec.toFixed(2))
      : 0;
    const segments = Array.isArray(options.segments) ? options.segments : [];
    const segmentPolicy = String(options.segmentPolicy || "auto_6_8");
    const selectedSegments = this.selectVideoSegmentsForFallback(segments);
    const importDetails = {
      method: "frame_sampling",
      source_file: {
        name: file?.name || "upload",
        mimeType: file?.type || "video/mp4",
        size: Number(file?.size || 0),
      },
      requested_segments: segments.length,
      sampled_segments: selectedSegments.length,
      segment_policy: segmentPolicy,
      durationSec: durationField,
    };

    if (signal?.aborted) {
      throw new Error("Analysis cancelled");
    }
    onProgress?.({
      stage: "frames",
      segment: 0,
      total: selectedSegments.length,
    });

    const fallbackSegments = [];
    let actorProfile = null;
    let continuityLocks = {};

    for (let index = 0; index < selectedSegments.length; index += 1) {
      if (signal?.aborted) {
        throw new Error("Analysis cancelled");
      }
      const segment = selectedSegments[index] || {};
      const midPoint =
        Number.isFinite(segment.startSec) && Number.isFinite(segment.endSec)
          ? segment.startSec +
            Math.max(0, segment.endSec - segment.startSec) * 0.5
          : Number(index);
      onProgress?.({
        stage: "frames",
        segment: index + 1,
        total: selectedSegments.length,
      });

      let framePart = null;
      try {
        framePart = await this.captureVideoFrameForGemini(
          file,
          midPoint,
          signal,
          {
            maxDim: MAX_VIDEO_FRAME_DIMENSION,
            quality: 0.55,
          },
        );
      } catch (error) {
        framePart = null;
      }

      let parsed = {};
      if (framePart) {
        const framePrompt = `You are an ad-ops continuity analyst. This is one representative frame from Batch ${segment.batchIndex || index + 1} (${segment.startSec || 0}s-${segment.endSec || 0}s) of a 6-8s style video split.
Return ONLY JSON in this exact shape:
{
  "environment_context": { },
  "action_context": { },
  "script_context": { },
  "actor_profile": { },
  "continuity_locks": { },
  "why_it_works": ""
}
Rules:
- Keep every field but be concise.
- Fill only fields you can infer from the frame.
- If unknown, use empty strings/arrays.
- Keep JSON only, no commentary.`;
        try {
          parsed = await this.analyzeFrameWithClaude(
            framePart,
            framePrompt,
            signal,
          );
        } catch (error) {
          parsed = {};
        }
      }

      const parsedActor = parsed?.actor_profile || parsed?.actorProfile || null;
      if (!actorProfile && parsedActor) {
        actorProfile = this.compactContextForPrompt(parsedActor, {
          maxDepth: 3,
          maxKeys: 16,
          maxString: 120,
        });
      }

      const parsedContinuity =
        parsed?.continuity_locks || parsed?.continuityLocks || null;
      if (parsedContinuity && typeof parsedContinuity === "object") {
        continuityLocks = {
          ...continuityLocks,
          ...this.compactContextForPrompt(parsedContinuity, {
            maxDepth: 3,
            maxKeys: 16,
            maxString: 120,
          }),
        };
      }

      const environment =
        parsed?.environment_context || parsed?.environment || {};
      const action = parsed?.action_context || parsed?.action || {};
      const script = parsed?.script_context || parsed?.script || {};
      fallbackSegments.push({
        ...segment,
        why_it_works: this.compactText(
          parsed?.why_it_works || parsed?.whyItWorks || "",
          300,
        ),
        environment_context: this.compactContextForPrompt(environment, {
          maxDepth: 4,
          maxArray: 4,
          maxKeys: 16,
          maxString: 140,
        }),
        action_context: {
          ...this.compactContextForPrompt(action, {
            maxDepth: 5,
            maxArray: 8,
            maxKeys: 18,
            maxString: 140,
          }),
          ...(parsedActor ? { actor: parsedActor } : {}),
        },
        script_context: this.compactScriptContext(script, {
          maxWordsPerLine: 6,
          maxTotalWords: 18,
        }),
      });
    }

    if (fallbackSegments.length === 0) {
      fallbackSegments.push({
        batchIndex: 1,
        startSec: 0,
        endSec: durationField,
        durationSec: durationField,
        why_it_works: "Frame sampling was not possible on this file.",
        environment_context: {},
        action_context: actorProfile ? { actor: actorProfile } : {},
        script_context: {},
      });
    }

    return {
      type: "video",
      durationSec: durationField,
      segmentPolicy,
      segments: fallbackSegments,
      actor_profile: actorProfile || {},
      continuity_locks: continuityLocks || {},
      importDetails,
    };
  }

  extractJSON(text) {
    if (text == null) {
      throw new Error("Empty response from model");
    }

    let normalized = text;
    if (Array.isArray(normalized)) {
      normalized = normalized
        .map((part) => {
          if (typeof part === "string") return part;
          if (part && typeof part === "object") {
            return part.text || part.content || part.value || "";
          }
          return "";
        })
        .filter(Boolean)
        .join("\n");
    }

    if (normalized && typeof normalized === "object") {
      if (typeof normalized.text === "string") {
        normalized = normalized.text;
      } else if (typeof normalized.content === "string") {
        normalized = normalized.content;
      } else {
        return normalized;
      }
    }

    const cleanedInput = String(normalized || "").trim();
    if (!cleanedInput) {
      throw new Error("Empty response from model");
    }

    const cleaned = cleanedInput
      .replace(/```json/gi, "```")
      .replace(/```/g, "")
      .trim();

    const firstObject = cleaned.indexOf("{");
    const firstArray = cleaned.indexOf("[");
    let start = -1;
    let openChar = "";
    let closeChar = "";

    if (firstObject === -1 && firstArray === -1) {
      throw new Error("No JSON found in model response");
    }

    if (firstArray !== -1 && (firstObject === -1 || firstArray < firstObject)) {
      start = firstArray;
      openChar = "[";
      closeChar = "]";
    } else {
      start = firstObject;
      openChar = "{";
      closeChar = "}";
    }

    let depth = 0;
    let inString = false;
    let escape = false;

    for (let i = start; i < cleaned.length; i += 1) {
      const ch = cleaned[i];

      if (inString) {
        if (escape) {
          escape = false;
        } else if (ch === "\\") {
          escape = true;
        } else if (ch === '"') {
          inString = false;
        }
        continue;
      }

      if (ch === '"') {
        inString = true;
        continue;
      }

      if (ch === openChar) depth += 1;
      if (ch === closeChar) depth -= 1;

      if (depth === 0) {
        const slice = cleaned.slice(start, i + 1);
        return JSON.parse(slice);
      }
    }

    const lastClose = cleaned.lastIndexOf(closeChar);
    if (lastClose > start) {
      const slice = cleaned.slice(start, lastClose + 1);
      try {
        return JSON.parse(slice);
      } catch (error) {
        // continue to fallback below
      }
    }

    if (openChar === "[") {
      const lastBrace = cleaned.lastIndexOf("}");
      if (lastBrace > start) {
        let slice = cleaned.slice(start, lastBrace + 1);
        slice = slice.replace(/,\s*$/, "");
        if (!slice.trim().endsWith("]")) {
          slice = `${slice}]`;
        }
        try {
          return JSON.parse(slice);
        } catch (error) {
          // fall through
        }
      }
    }

    throw new Error("Incomplete JSON in model response");
  }

  async fileToBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result.split(",")[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  async blobToBase64(blob) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result || "";
        const base64 = result.split(",")[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  computeVideoSegments(durationSec) {
    const minSeconds = 6;
    const maxSeconds = 8;
    const targetSeconds = 7;
    const safeDuration = Math.max(0, Number(durationSec) || 0);
    if (safeDuration === 0) {
      return [
        {
          batchIndex: 1,
          startSec: 0,
          endSec: 0,
          durationSec: 0,
        },
      ];
    }

    let segmentCount = Math.max(1, Math.round(safeDuration / targetSeconds));
    if (safeDuration <= maxSeconds) {
      segmentCount = 1;
    }

    let guard = 0;
    while (guard < 20) {
      const perSegment = safeDuration / segmentCount;
      if (perSegment < minSeconds && segmentCount > 1) {
        segmentCount -= 1;
      } else if (perSegment > maxSeconds) {
        segmentCount += 1;
      } else {
        break;
      }
      guard += 1;
    }

    const segmentLength = safeDuration / segmentCount;
    const segments = [];
    let startSec = 0;
    for (let i = 0; i < segmentCount; i += 1) {
      const endSec =
        i === segmentCount - 1
          ? safeDuration
          : Number((startSec + segmentLength).toFixed(2));
      const duration = Number((endSec - startSec).toFixed(2));
      segments.push({
        batchIndex: i + 1,
        startSec: Number(startSec.toFixed(2)),
        endSec: Number(endSec.toFixed(2)),
        durationSec: duration,
      });
      startSec = endSec;
    }
    return segments;
  }

  async getVideoDuration(file) {
    const url = URL.createObjectURL(file);
    try {
      const video = await this.loadVideoFromUrl(url);
      let duration = Number(video?.duration || 0);
      const shouldProbe =
        !Number.isFinite(duration) || duration <= 0 || duration < 8;
      if (shouldProbe) {
        const probed = await this.probeVideoDuration(video);
        if (Number.isFinite(probed) && probed > 0) {
          duration = probed;
        }
      }
      return Number.isFinite(duration) ? duration : 0;
    } catch (error) {
      return 0;
    } finally {
      URL.revokeObjectURL(url);
    }
  }

  async probeVideoDuration(video) {
    return new Promise((resolve) => {
      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        cleanup();
        const duration = Number(video?.duration || 0);
        resolve(Number.isFinite(duration) ? duration : 0);
      };
      const onDurationChange = () => {
        if (Number.isFinite(video?.duration) && video.duration > 0) {
          finish();
        }
      };
      const onLoadedData = () => {
        if (Number.isFinite(video?.duration) && video.duration > 0) {
          finish();
        }
      };
      const cleanup = () => {
        video.removeEventListener("durationchange", onDurationChange);
        video.removeEventListener("loadeddata", onLoadedData);
      };

      video.addEventListener("durationchange", onDurationChange);
      video.addEventListener("loadeddata", onLoadedData);
      try {
        video.currentTime = 1e101;
      } catch (error) {
        finish();
        return;
      }
      setTimeout(finish, 1500);
    });
  }

  async analyzeVideoWithGemini(file, onProgress, signal) {
    const durationSec = await this.getVideoDuration(file);
    const durationLabel = Number.isFinite(Number(durationSec))
      ? Number(durationSec.toFixed(2))
      : 0;
    const segments = this.computeVideoSegments(durationSec);
    const segmentPolicy = "auto_6_8";
    const segmentLines = segments
      .map(
        (seg) =>
          `Batch ${seg.batchIndex}: ${seg.startSec}s-${seg.endSec}s (${seg.durationSec}s)`,
      )
      .join("\n");

    const prompt = `You are a video director + continuity editor generating short, stitchable video segments.
You MUST split the reference video into 6-8 second batches and produce 3 JSONs per batch.

SEGMENTATION WINDOWS (fixed):
${segmentLines}

OUTPUT STRUCTURE (NON-NEGOTIABLE)
Return ONLY JSON in this exact shape:
{
  "durationSec": ${durationLabel},
  "segmentPolicy": "${segmentPolicy}",
  "actor_profile": {
    "identity": "",
    "age": "",
    "gender": "",
    "skin_tone": "",
    "face_structure": "",
    "hair": "",
    "body_type": "",
    "wardrobe": "",
    "grooming": "",
    "demeanor": ""
  },
  "continuity_locks": {
    "visual_style": "",
    "lens_language": "",
    "lighting_style": "",
    "color_grade": "",
    "background_rules": []
  },
  "segments": [
    {
      "batchIndex": 1,
      "startSec": 0,
      "endSec": 6.9,
      "durationSec": 6.9,
      "why_it_works": "",
      "environment_context": { },
      "action_context": { },
      "script_context": { }
    }
  ]
}

ENVIRONMENT_CONTEXT must define:
- Location & setting
- Lighting
- Camera framing & movement
- Color grading & mood
- Background continuity rules (what must stay consistent across clips)

ACTION_CONTEXT must define:
- Actor identity (must persist across ALL segments)
- Actor clothing, grooming, age, demeanor (LOCKED unless explicitly changed)
- Physical actions occurring ONLY in this 6-8s window
- Micro-movements that can continue into the next clip
- Entry state + exit state

SCRIPT_CONTEXT must define:
- Spoken dialogue OR internal monologue OR silence
- On-screen text (if any)
- Timing markers (beats at ~2s intervals)
- Emotional tone progression
- Must NOT reference future scenes explicitly

WHY_IT_WORKS must define:
- 2-3 rich paragraphs on why this segment works psychologically.
- Be specific about emotions, persuasion, attention mechanics, and pacing.

CONTINUITY LOCKS (CRITICAL)
- Reuse the exact same actor description across all batches.
- Preserve face structure, hair, skin tone, body type, clothing (unless change is explicitly scripted).
- Preserve visual language (lens, lighting, framing philosophy).
- If something changes, it must be justified and gradual (never abrupt).

STITCHING INTENT
- Each segment must end in a stitch-friendly moment: eye movement, camera drift, breath pause, hand mid-action.
- Every segment must be individually renderable.

JSON ONLY. No commentary. No markdown.${this.buildAiGuidance()}${this.buildAnalysisGuidance()}${this.buildAdCopyGuidance()}`;

    const importDetailsBase = {
      method: "gemini_file_upload",
      source_file: {
        name: file?.name || "upload",
        mimeType: file?.type || "video/mp4",
        size: Number(file?.size || 0),
      },
      durationSec: durationLabel,
      segment_policy: segmentPolicy,
      segments_requested: segments.length,
    };

    onProgress?.({ stage: "environment", segment: 1, total: segments.length });

    const normalizeSegmentsFromModel = (parsed) => {
      const parsedSegments = Array.isArray(parsed?.segments)
        ? parsed.segments
        : Array.isArray(parsed?.batches)
          ? parsed.batches
          : [];
      const actorProfile =
        parsed?.actor_profile ||
        parsed?.actorProfile ||
        parsedSegments?.[0]?.action_context?.actor ||
        parsedSegments?.[0]?.action_context?.actor_profile ||
        null;
      const continuityLocks =
        parsed?.continuity_locks || parsed?.continuityLocks || {};

      const normalizedSegments = segments.map((seg, index) => {
        const candidate = parsedSegments?.[index] || {};
        const environment =
          candidate.environment_context || candidate.environment || {};
        const action = candidate.action_context || candidate.action || {};
        const script = candidate.script_context || candidate.script || {};
        return {
          ...seg,
          why_it_works: this.compactText(
            candidate.why_it_works || candidate.whyItWorks || "",
            340,
          ),
          environment_context: this.compactContextForPrompt(environment, {
            maxDepth: 4,
            maxArray: 5,
            maxKeys: 18,
            maxString: 160,
          }),
          action_context: this.compactContextForPrompt(
            {
              ...action,
              ...(actorProfile ? { actor: actorProfile } : {}),
            },
            {
              maxDepth: 6,
              maxArray: 12,
              maxKeys: 24,
              maxString: 140,
            },
          ),
          script_context: this.compactScriptContext(script, {
            maxWordsPerLine: 8,
            maxTotalWords: 22,
          }),
        };
      });

      onProgress?.({
        stage: "actions",
        segment: segments.length,
        total: segments.length,
      });

      return {
        type: "video",
        durationSec: durationLabel,
        segmentPolicy,
        segments: normalizedSegments,
        actor_profile:
          this.compactContextForPrompt(actorProfile || {}, {
            maxDepth: 4,
            maxArray: 8,
            maxKeys: 18,
            maxString: 180,
          }) || {},
        continuity_locks:
          this.compactContextForPrompt(continuityLocks || {}, {
            maxDepth: 4,
            maxArray: 8,
            maxKeys: 16,
            maxString: 180,
          }) || {},
        importDetails: {
          ...importDetailsBase,
          status: "success",
        },
      };
    };

    try {
      const videoParts = await this.getGeminiVideoParts(file, signal);
      const parsed = await this.requestGeminiVideoAnalysis(
        videoParts.parts,
        prompt,
        GEMINI_VISION_MODEL,
        signal,
        true,
      );
      return normalizeSegmentsFromModel(parsed);
    } catch (error) {
      const isCancellation =
        signal?.aborted ||
        String(error?.message || "")
          .toLowerCase()
          .includes("cancel");
      if (isCancellation) {
        throw error;
      }
      if (this.isGeminiVideoUploadPayloadError(error)) {
        const fallback = await this.analyzeVideoByFrameSampling(
          file,
          onProgress,
          signal,
          {
            durationSec: durationLabel,
            segments,
            segmentPolicy,
          },
        );
        return {
          ...fallback,
          importDetails: {
            ...(fallback.importDetails || {}),
            fallback: true,
            fallback_from: importDetailsBase.method,
            payload_error: "object_exceeded_maximum_allowed_size",
          },
        };
      }
      throw error;
    }
  }

  parseAspectRatio(aspectRatio) {
    if (!aspectRatio || typeof aspectRatio !== "string") return null;
    const match = aspectRatio
      .trim()
      .match(/^(\d+(?:\.\d+)?)\s*:\s*(\d+(?:\.\d+)?)$/);
    if (!match) return null;
    const w = Number(match[1]);
    const h = Number(match[2]);
    if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) {
      return null;
    }
    return { w, h, ratio: w / h };
  }

  getMasterAspectRatio() {
    if (this.generationAspectRatio) {
      return this.parseAspectRatio(this.generationAspectRatio)
        ? this.generationAspectRatio
        : null;
    }
    return null;
  }

  getOpenAISizeForAspect(aspectRatio) {
    const parsed = this.parseAspectRatio(aspectRatio);
    if (!parsed) return "1024x1024";
    if (Math.abs(parsed.ratio - 1) <= 0.02) return "1024x1024";
    return parsed.ratio > 1 ? "1536x1024" : "1024x1536";
  }

  buildGeminiFallbackChain(modelName, body) {
    const requested = this.normalizeGeminiModelName(modelName);
    const responseModes =
      body?.generationConfig?.responseModalities ||
      body?.generation_config?.response_modalities ||
      [];
    const modeList = Array.isArray(responseModes)
      ? responseModes.map((mode) => String(mode || "").toUpperCase())
      : [];
    const wantsImageOutput =
      requested.includes("image") || modeList.includes("IMAGE");
    const familyOrder = wantsImageOutput
      ? GEMINI_IMAGE_FALLBACK_ORDER
      : GEMINI_TEXT_FALLBACK_ORDER;
    const ordered = [requested, ...familyOrder]
      .map((entry) => this.normalizeGeminiModelName(entry))
      .filter(Boolean);
    return Array.from(new Set(ordered));
  }

  normalizeGeminiModelName(modelName) {
    const raw = String(modelName || "")
      .trim()
      .toLowerCase();
    if (!raw) return "";
    const stripped = raw.replace(/^models\//, "");
    return GEMINI_MODEL_ALIAS[stripped] || stripped;
  }

  async wait(ms, signal) {
    const safeMs = Math.max(0, Number(ms) || 0);
    if (safeMs <= 0) return;
    await new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        if (signal) {
          signal.removeEventListener("abort", onAbort);
        }
        resolve();
      }, safeMs);
      const onAbort = () => {
        clearTimeout(timer);
        reject(new Error("Analysis cancelled"));
      };
      if (signal) {
        if (signal.aborted) {
          clearTimeout(timer);
          reject(new Error("Analysis cancelled"));
          return;
        }
        signal.addEventListener("abort", onAbort, { once: true });
      }
    });
  }

  isGeminiRateLimitError(error) {
    const status = Number(error?.status || 0);
    const message = String(
      error?.message ||
        error?.payload?.error?.message ||
        error?.payload?.message ||
        "",
    ).toLowerCase();
    return (
      status === 429 ||
      status === 503 ||
      message.includes("quota exceeded") ||
      message.includes("rate limit") ||
      message.includes("too many requests") ||
      message.includes("resource_exhausted") ||
      message.includes("generate_requests_per_model") ||
      message.includes("high demand") ||
      message.includes("try again later") ||
      message.includes("temporarily unavailable") ||
      message.includes("retry in")
    );
  }

  isGeminiTransientError(error) {
    const status = Number(error?.status || 0);
    const message = String(
      error?.message ||
        error?.payload?.error?.message ||
        error?.payload?.message ||
        "",
    ).toLowerCase();
    return (
      this.isGeminiRateLimitError(error) ||
      status === 500 ||
      status === 502 ||
      status === 503 ||
      status === 504 ||
      message.includes("service unavailable") ||
      message.includes("backend error") ||
      message.includes("internal error") ||
      message.includes("deadline exceeded") ||
      message.includes("timeout") ||
      message.includes("overloaded")
    );
  }

  isGeminiModelUnavailableError(error) {
    const status = Number(error?.status || 0);
    const message = String(
      error?.message ||
        error?.payload?.error?.message ||
        error?.payload?.message ||
        "",
    ).toLowerCase();
    return (
      status === 404 ||
      message.includes("model not found") ||
      message.includes("is not found for api version") ||
      message.includes("unknown model") ||
      message.includes("unsupported model") ||
      message.includes("is not supported for this operation") ||
      message.includes("is not supported for generatecontent") ||
      message.includes("call listmodels") ||
      message.includes("not available in this location")
    );
  }

  isOpenAINativeAspect(aspectRatio) {
    const parsed = this.parseAspectRatio(aspectRatio);
    if (!parsed) return true;
    const nativeRatios = [1, 2 / 3, 3 / 2];
    return nativeRatios.some((value) => Math.abs(parsed.ratio - value) <= 0.02);
  }

  isGeminiUnknownFieldError(errorText) {
    const lower = String(errorText || "").toLowerCase();
    return (
      lower.includes("unknown name") ||
      lower.includes("unknown field") ||
      lower.includes("cannot find field")
    );
  }

  isPayloadTooLargeError(errorText) {
    const lower = String(errorText || "").toLowerCase();
    return (
      lower.includes("payload too large") ||
      lower.includes("request entity too large") ||
      lower.includes("function_payload_too_large") ||
      lower.includes("payload_too_large")
    );
  }

  isGeminiVideoUploadPayloadError(error) {
    const status = Number(error?.status || 0);
    const message = String(
      error?.message ||
        error?.payload?.error?.message ||
        error?.payload?.error ||
        error?.payload?.message ||
        "",
    ).toLowerCase();
    return (
      status === 413 ||
      status === 400 ||
      this.isPayloadTooLargeError(message) ||
      message.includes("object exceeded the maximum allowed size")
    );
  }

  estimateBase64Bytes(base64) {
    if (!base64) return 0;
    return Math.ceil((base64.length * 3) / 4);
  }

  async resolveReferenceImageBlob(img) {
    if (img?.blob) {
      return { blob: img.blob, mimeType: img.mimeType || img.blob.type };
    }
    if (img?.dataUrl) {
      const match = String(img.dataUrl).match(/^data:(.+);base64,(.*)$/);
      if (!match) return null;
      const blob = await this.base64ToBlob(match[2], match[1]);
      return { blob, mimeType: match[1] };
    }
    if (img?.url) {
      const parsed = this.parseDataUrl(img.url);
      if (parsed?.base64) {
        const blob = await this.base64ToBlob(parsed.base64, parsed.mimeType);
        return { blob, mimeType: parsed.mimeType || blob.type };
      }
      try {
        const response = await fetch(img.url);
        if (!response.ok) return null;
        const blob = await response.blob();
        return { blob, mimeType: img.mimeType || blob.type };
      } catch (error) {
        return null;
      }
    }
    return null;
  }

  async prepareGeminiInlineImage(blob, options = {}) {
    if (!blob) return null;
    const maxDim = Number.isFinite(options.maxDim) ? options.maxDim : 768;
    const quality = Number.isFinite(options.quality) ? options.quality : 0.7;
    const tempUrl = URL.createObjectURL(blob);
    let img;
    try {
      img = await this.loadImageFromUrl(tempUrl);
    } catch (error) {
      URL.revokeObjectURL(tempUrl);
      const base64 = await this.blobToBase64(blob);
      return { base64, mimeType: blob.type || "image/png" };
    }
    URL.revokeObjectURL(tempUrl);

    const width = img.naturalWidth || img.width;
    const height = img.naturalHeight || img.height;
    if (!width || !height) {
      const base64 = await this.blobToBase64(blob);
      return { base64, mimeType: blob.type || "image/png" };
    }

    const scale = Math.min(1, maxDim / Math.max(width, height));
    const outWidth = Math.max(1, Math.round(width * scale));
    const outHeight = Math.max(1, Math.round(height * scale));
    const canvas = document.createElement("canvas");
    canvas.width = outWidth;
    canvas.height = outHeight;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) {
      const base64 = await this.blobToBase64(blob);
      return { base64, mimeType: blob.type || "image/png" };
    }
    ctx.drawImage(img, 0, 0, outWidth, outHeight);

    const outBlob = await new Promise((resolve) =>
      canvas.toBlob((result) => resolve(result || blob), "image/jpeg", quality),
    );
    const base64 = await this.blobToBase64(outBlob);
    return { base64, mimeType: outBlob.type || "image/jpeg" };
  }

  async prepareOpenAIImage(blob, options = {}) {
    if (!blob) return null;
    const maxDim = Number.isFinite(options.maxDim) ? options.maxDim : 1024;
    const quality = Number.isFinite(options.quality) ? options.quality : 0.72;
    const tempUrl = URL.createObjectURL(blob);
    let img;
    try {
      img = await this.loadImageFromUrl(tempUrl);
    } catch (error) {
      URL.revokeObjectURL(tempUrl);
      const base64 = await this.blobToBase64(blob);
      return { base64, mimeType: blob.type || "image/jpeg" };
    }
    URL.revokeObjectURL(tempUrl);

    const width = img.naturalWidth || img.width;
    const height = img.naturalHeight || img.height;
    if (!width || !height) {
      const base64 = await this.blobToBase64(blob);
      return { base64, mimeType: blob.type || "image/jpeg" };
    }

    const scale = Math.min(1, maxDim / Math.max(width, height));
    const outWidth = Math.max(1, Math.round(width * scale));
    const outHeight = Math.max(1, Math.round(height * scale));
    const canvas = document.createElement("canvas");
    canvas.width = outWidth;
    canvas.height = outHeight;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) {
      const base64 = await this.blobToBase64(blob);
      return { base64, mimeType: blob.type || "image/jpeg" };
    }
    ctx.drawImage(img, 0, 0, outWidth, outHeight);

    const outBlob = await new Promise((resolve) =>
      canvas.toBlob((result) => resolve(result || blob), "image/jpeg", quality),
    );
    const base64 = await this.blobToBase64(outBlob);
    return { base64, mimeType: outBlob.type || "image/jpeg" };
  }

  // Resolve reference image descriptors (product-folder images + the analyzed
  // source ad) into downscaled base64 payloads for OpenAI's image *edit*
  // endpoint, which — unlike images.generate — conditions generation on real
  // input pixels. Any reference that can't be fetched/decoded is skipped rather
  // than failing the whole render.
  async buildOpenAIReferenceImages(referenceImages = []) {
    const limited = this.dedupeReferenceImages(referenceImages).slice(0, 4);
    const prepared = await Promise.all(
      limited.map(async (img) => {
        try {
          let blob = img?.blob || null;
          let mimeType = img?.mimeType || blob?.type || "";
          if (!blob) {
            const src = img?.dataUrl || img?.url;
            if (!src) return null;
            const fetched = await this.fetchMediaBlob(src);
            blob = fetched.blob;
            mimeType = mimeType || fetched.mimeType;
          }
          if (!blob) return null;
          const ready = await this.prepareOpenAIImage(blob, {
            maxDim: 1024,
            quality: 0.8,
          });
          if (!ready?.base64) return null;
          return {
            b64: ready.base64,
            mimeType: ready.mimeType || "image/jpeg",
          };
        } catch (error) {
          return null;
        }
      }),
    );
    return prepared.filter(Boolean);
  }

  async buildGeminiReferenceImageParts(referenceImages = [], options = {}) {
    const maxImages = Number.isFinite(options.maxImages)
      ? options.maxImages
      : 4;
    const maxPerImageBytes = Number.isFinite(options.maxPerImageBytes)
      ? options.maxPerImageBytes
      : 600_000;
    const maxTotalBytes = Number.isFinite(options.maxTotalBytes)
      ? options.maxTotalBytes
      : 1_200_000;
    const preferFileData = options.preferFileData !== false;
    const parts = [];
    let totalBytes = 0;

    for (const img of referenceImages) {
      if (parts.length >= maxImages) break;
      const url = img?.url || "";
      if (preferFileData && url && !String(url).startsWith("data:")) {
        const cacheKey = img?.path || url;
        if (this.geminiFileCache.has(cacheKey)) {
          const cached = this.geminiFileCache.get(cacheKey);
          if (cached?.uri) {
            parts.push({
              file_data: {
                mime_type: cached.mimeType || "image/jpeg",
                file_uri: cached.uri,
              },
            });
            continue;
          }
        }
        try {
          const uploaded = await this.uploadGeminiUrl(
            url,
            img?.mimeType || "image/jpeg",
            img?.name || "reference-image",
          );
          if (uploaded?.uri) {
            this.geminiFileCache.set(cacheKey, uploaded);
            parts.push({
              file_data: {
                mime_type: uploaded.mimeType || "image/jpeg",
                file_uri: uploaded.uri,
              },
            });
            continue;
          }
        } catch (error) {
          // fall back to inline data
        }
      }

      const resolved = await this.resolveReferenceImageBlob(img);
      if (!resolved?.blob) continue;

      let prepared = await this.prepareGeminiInlineImage(resolved.blob, {
        maxDim: 640,
        quality: 0.65,
      });
      if (!prepared?.base64) continue;
      let bytes = this.estimateBase64Bytes(prepared.base64);

      if (bytes > maxPerImageBytes) {
        prepared = await this.prepareGeminiInlineImage(resolved.blob, {
          maxDim: 480,
          quality: 0.55,
        });
        if (!prepared?.base64) continue;
        bytes = this.estimateBase64Bytes(prepared.base64);
      }

      if (bytes > maxPerImageBytes) continue;
      if (totalBytes + bytes > maxTotalBytes && parts.length > 0) break;

      parts.push({
        inline_data: {
          mime_type: prepared.mimeType || resolved.mimeType || "image/jpeg",
          data: prepared.base64,
        },
      });
      totalBytes += bytes;
    }

    return parts;
  }

  buildGeminiImageRequestBody(
    imageParts,
    prompt,
    aspectRatio,
    includeAspectConfig,
    temperature,
  ) {
    const generationConfig = {
      responseModalities: ["TEXT", "IMAGE"],
      temperature: typeof temperature === "number" ? temperature : 0.2,
    };
    if (includeAspectConfig && aspectRatio) {
      generationConfig.imageConfig = { aspectRatio };
    }
    return {
      contents: [{ parts: [...imageParts, { text: prompt }] }],
      generationConfig,
    };
  }

  async requestGeminiImageResponse(
    imageModel,
    imageParts,
    prompt,
    aspectRatio,
    temperature,
  ) {
    const includeAspectConfigModes = [];
    if (aspectRatio && this.geminiImageAspectConfigSupport !== false) {
      includeAspectConfigModes.push(true);
    }
    includeAspectConfigModes.push(false);

    for (const includeAspectConfig of includeAspectConfigModes) {
      const requestBody = this.buildGeminiImageRequestBody(
        imageParts,
        prompt,
        aspectRatio,
        includeAspectConfig,
        temperature,
      );
      try {
        const data = await this.callGeminiGenerate(imageModel, requestBody);
        if (includeAspectConfig) {
          this.geminiImageAspectConfigSupport = true;
        }
        return data;
      } catch (error) {
        const errorText = String(error?.message || "");
        if (includeAspectConfig && this.isGeminiUnknownFieldError(errorText)) {
          this.geminiImageAspectConfigSupport = false;
          continue;
        }
        throw new Error(`Gemini image generation failed: ${errorText}`);
      }
    }

    throw new Error(
      "Gemini image generation failed: unsupported aspect config",
    );
  }

  getQcModel(qcLevel) {
    if (this.qcModelOverride) return this.qcModelOverride;
    if (qcLevel === "strict") return GEMINI_VISION_MODEL;
    return "gemini-3-flash-preview";
  }

  async prepareQcImage(blob, qcLevel) {
    const maxDim = qcLevel === "strict" ? 1024 : 768;
    const quality = qcLevel === "strict" ? 0.8 : 0.65;
    const tempUrl = URL.createObjectURL(blob);
    let img;
    try {
      img = await this.loadImageFromUrl(tempUrl);
    } catch (error) {
      URL.revokeObjectURL(tempUrl);
      const fallbackBase64 = await this.blobToBase64(blob);
      return {
        base64: fallbackBase64,
        mimeType: blob.type || "image/png",
      };
    }
    URL.revokeObjectURL(tempUrl);

    const width = img.naturalWidth || img.width;
    const height = img.naturalHeight || img.height;
    if (!width || !height) {
      const fallbackBase64 = await this.blobToBase64(blob);
      return {
        base64: fallbackBase64,
        mimeType: blob.type || "image/png",
      };
    }

    const scale = Math.min(1, maxDim / Math.max(width, height));
    const outWidth = Math.max(1, Math.round(width * scale));
    const outHeight = Math.max(1, Math.round(height * scale));
    const canvas = document.createElement("canvas");
    canvas.width = outWidth;
    canvas.height = outHeight;
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      const fallbackBase64 = await this.blobToBase64(blob);
      return {
        base64: fallbackBase64,
        mimeType: blob.type || "image/png",
      };
    }
    ctx.drawImage(img, 0, 0, outWidth, outHeight);

    const outBlob = await new Promise((resolve) =>
      canvas.toBlob((result) => resolve(result || blob), "image/jpeg", quality),
    );
    const base64 = await this.blobToBase64(outBlob);
    return {
      base64,
      mimeType: outBlob.type || "image/jpeg",
    };
  }

  async loadImageFromUrl(src) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = src;
    });
  }

  async detectLetterboxing(mediaUrl) {
    const img = await this.loadImageFromUrl(mediaUrl);
    const w = img.naturalWidth || img.width;
    const h = img.naturalHeight || img.height;
    if (!w || !h) {
      return { letterboxDetected: false, reason: "unable_to_measure" };
    }

    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      return { letterboxDetected: false, reason: "no_canvas_context" };
    }
    ctx.drawImage(img, 0, 0, w, h);

    const band = Math.max(2, Math.floor(Math.min(w, h) * 0.05));
    const sampleBand = (x, y, sw, sh) => {
      const data = ctx.getImageData(x, y, sw, sh).data;
      let sum = 0;
      let sumSq = 0;
      const n = data.length / 4 || 1;
      for (let i = 0; i < data.length; i += 4) {
        const lum =
          0.2126 * data[i] + 0.7152 * data[i + 1] + 0.0722 * data[i + 2];
        sum += lum;
        sumSq += lum * lum;
      }
      const mean = sum / n;
      const variance = sumSq / n - mean * mean;
      return { mean, std: Math.sqrt(Math.max(0, variance)) };
    };

    const top = sampleBand(0, 0, w, band);
    const bottom = sampleBand(0, h - band, w, band);
    const left = sampleBand(0, 0, band, h);
    const right = sampleBand(w - band, 0, band, h);
    const center = sampleBand(
      Math.floor(w * 0.2),
      Math.floor(h * 0.2),
      Math.max(1, Math.floor(w * 0.6)),
      Math.max(1, Math.floor(h * 0.6)),
    );

    const flat = (sample) => sample.std < 6;
    const similar = (a, b) => Math.abs(a.mean - b.mean) < 10;
    const horizontalBars =
      flat(top) &&
      flat(bottom) &&
      similar(top, bottom) &&
      center.std > Math.max(top.std, bottom.std) + 4;
    const verticalBars =
      flat(left) &&
      flat(right) &&
      similar(left, right) &&
      center.std > Math.max(left.std, right.std) + 4;
    const letterboxDetected = horizontalBars || verticalBars;

    return {
      letterboxDetected,
      axis: horizontalBars ? "horizontal" : verticalBars ? "vertical" : "none",
      bands: { top, bottom, left, right, center },
    };
  }

  async validateGeneratedImageComposition(mediaUrl, expectedAspectRatio) {
    const expected = this.parseAspectRatio(expectedAspectRatio);
    if (!expected) {
      return { ok: true };
    }
    const measured = await this.measureMediaAspectRatio(mediaUrl, "image");
    const ratioScore = this.scoreAspectRatio(expectedAspectRatio, measured);
    if (ratioScore.score < 95) {
      return {
        ok: false,
        reason: ratioScore.issue || "Aspect ratio mismatch.",
      };
    }
    const letterbox = await this.detectLetterboxing(mediaUrl);
    if (letterbox.letterboxDetected) {
      return {
        ok: false,
        reason:
          "Canvas utilization failed: detected edge bars / poster-in-frame composition.",
      };
    }
    return { ok: true, measured, letterbox };
  }

  async enforceAspectRatioOnBlob(blob, aspectRatio, mimeTypeHint) {
    const parsed = this.parseAspectRatio(aspectRatio);
    if (!parsed) {
      return {
        imageUrl: URL.createObjectURL(blob),
        mimeType: blob.type || mimeTypeHint || "image/png",
        blob,
      };
    }

    const tempUrl = URL.createObjectURL(blob);
    let img;
    try {
      img = await this.loadImageFromUrl(tempUrl);
    } catch (error) {
      URL.revokeObjectURL(tempUrl);
      return {
        imageUrl: tempUrl,
        mimeType: blob.type || mimeTypeHint || "image/png",
        blob,
      };
    }

    const width = img.naturalWidth || img.width;
    const height = img.naturalHeight || img.height;
    const currentRatio = width / height;
    const targetRatio = parsed.ratio;
    const ratioDelta = Math.abs(currentRatio - targetRatio);
    if (ratioDelta < 0.02) {
      return {
        imageUrl: tempUrl,
        mimeType: blob.type || mimeTypeHint || "image/png",
        blob,
      };
    }
    const canvas = document.createElement("canvas");
    let cropWidth = width;
    let cropHeight = height;
    let cropX = 0;
    let cropY = 0;
    if (currentRatio > targetRatio) {
      cropWidth = Math.max(1, Math.round(height * targetRatio));
      cropX = Math.max(0, Math.round((width - cropWidth) / 2));
    } else {
      cropHeight = Math.max(1, Math.round(width / targetRatio));
      cropY = Math.max(0, Math.round((height - cropHeight) / 2));
    }
    canvas.width = cropWidth;
    canvas.height = cropHeight;
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      URL.revokeObjectURL(tempUrl);
      return {
        imageUrl: tempUrl,
        mimeType: blob.type || mimeTypeHint || "image/png",
      };
    }
    ctx.drawImage(
      img,
      cropX,
      cropY,
      cropWidth,
      cropHeight,
      0,
      0,
      cropWidth,
      cropHeight,
    );
    URL.revokeObjectURL(tempUrl);

    const outBlob = await new Promise((resolve) =>
      canvas.toBlob(
        (result) => resolve(result || blob),
        blob.type || mimeTypeHint || "image/png",
      ),
    );
    return {
      imageUrl: URL.createObjectURL(outBlob),
      mimeType: outBlob.type || mimeTypeHint || "image/png",
      blob: outBlob,
    };
  }

  async enforceAspectRatioOnDataUrl(dataUrl, aspectRatio, mimeTypeHint) {
    const parsed = this.parseAspectRatio(aspectRatio);
    if (!parsed) {
      const inline = this.parseDataUrl(dataUrl);
      return {
        imageUrl: dataUrl,
        mimeType: mimeTypeHint || inline?.mimeType || "image/png",
        blob: inline?.base64
          ? await this.base64ToBlob(
              inline.base64,
              mimeTypeHint || inline.mimeType || "image/png",
            )
          : null,
      };
    }
    let img;
    try {
      img = await this.loadImageFromUrl(dataUrl);
    } catch (error) {
      const inline = this.parseDataUrl(dataUrl);
      return {
        imageUrl: dataUrl,
        mimeType: mimeTypeHint || inline?.mimeType || "image/png",
        blob: inline?.base64
          ? await this.base64ToBlob(
              inline.base64,
              mimeTypeHint || inline.mimeType || "image/png",
            )
          : null,
      };
    }

    const width = img.naturalWidth || img.width;
    const height = img.naturalHeight || img.height;
    const currentRatio = width / height;
    const targetRatio = parsed.ratio;
    const ratioDelta = Math.abs(currentRatio - targetRatio);
    if (ratioDelta < 0.02) {
      const inline = this.parseDataUrl(dataUrl);
      return {
        imageUrl: dataUrl,
        mimeType: mimeTypeHint || inline?.mimeType || "image/png",
        blob: inline?.base64
          ? await this.base64ToBlob(
              inline.base64,
              mimeTypeHint || inline.mimeType || "image/png",
            )
          : null,
      };
    }
    const canvas = document.createElement("canvas");
    let cropWidth = width;
    let cropHeight = height;
    let cropX = 0;
    let cropY = 0;
    if (currentRatio > targetRatio) {
      cropWidth = Math.max(1, Math.round(height * targetRatio));
      cropX = Math.max(0, Math.round((width - cropWidth) / 2));
    } else {
      cropHeight = Math.max(1, Math.round(width / targetRatio));
      cropY = Math.max(0, Math.round((height - cropHeight) / 2));
    }
    canvas.width = cropWidth;
    canvas.height = cropHeight;
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      const inline = this.parseDataUrl(dataUrl);
      return {
        imageUrl: dataUrl,
        mimeType: mimeTypeHint || inline?.mimeType || "image/png",
        blob: inline?.base64
          ? await this.base64ToBlob(
              inline.base64,
              mimeTypeHint || inline.mimeType || "image/png",
            )
          : null,
      };
    }
    ctx.drawImage(
      img,
      cropX,
      cropY,
      cropWidth,
      cropHeight,
      0,
      0,
      cropWidth,
      cropHeight,
    );

    const outBlob = await new Promise((resolve) =>
      canvas.toBlob((result) => resolve(result), mimeTypeHint || "image/png"),
    );
    if (!outBlob) {
      const inline = this.parseDataUrl(dataUrl);
      return {
        imageUrl: dataUrl,
        mimeType: mimeTypeHint || inline?.mimeType || "image/png",
        blob: inline?.base64
          ? await this.base64ToBlob(
              inline.base64,
              mimeTypeHint || inline.mimeType || "image/png",
            )
          : null,
      };
    }

    return {
      imageUrl: URL.createObjectURL(outBlob),
      mimeType: outBlob.type || mimeTypeHint || "image/png",
      blob: outBlob,
    };
  }

  async resolveGeminiFileToObjectUrl(fileUri, mimeTypeHint, _aspectRatio) {
    if (!fileUri) {
      throw new Error("Gemini file URI missing");
    }
    const data = await this.callAiServer("gemini.fileContent", { fileUri });
    if (!data?.base64) {
      throw new Error("Failed to download Gemini-generated image");
    }
    const blob = await this.base64ToBlob(
      data.base64,
      data.mimeType || mimeTypeHint || "image/png",
    );
    return {
      imageUrl: URL.createObjectURL(blob),
      mimeType: blob.type || data.mimeType || mimeTypeHint || "image/png",
      blob,
    };
  }

  async generateVariants(sourceContext, options) {
    const {
      quantity = 3,
      creativity = 0.7,
      focusArea = "Visual Hook",
      model = DEFAULT_VARIANT_MODEL,
      sceneMode = "mixed",
      specialInstructions = "",
      _previewOnly = false,
      promptOverride = "",
    } = options || {};
    const compactSource = this.compactContextForPrompt(sourceContext, {
      maxDepth: 5,
      maxArray: 10,
      maxKeys: 28,
      maxString: 260,
    });
    const sourceJson = JSON.stringify(compactSource);
    const trimmedSpecialInstructions = String(specialInstructions || "").trim();
    const normalizedSceneMode = this.normalizeSceneMode(sceneMode);
    const provider = model.startsWith("gemini")
      ? "gemini"
      : model.startsWith("claude")
        ? "claude"
        : "openai";

    const buildPromptChannelPool = (count) => {
      const baseChannels = this.getSceneModeChannelPool(
        normalizedSceneMode,
        "analysis",
        focusArea,
      );
      const channels = [];
      for (let i = 0; i < count; i += 1) {
        if (i < baseChannels.length) {
          channels.push(baseChannels[i]);
        } else {
          channels.push(
            `Alternate angle ${i + 1} (fresh composition + props, keep focus: ${focusArea})`,
          );
        }
      }
      return channels;
    };

    const run = async (count, strict, channel) => {
      const channelLine = channel
        ? `\nExecution direction for this variant: ${channel}\n`
        : "";
      const prompt = `You are a creative strategist. The Source JSON below is the neuro analysis of the creative the user shared. Generate ${count} unique variant concept(s) — each a fresh execution of that same shared creative, engineered from the analysis so it scores at least as strong as the source.

Rules:
- Build the concept FROM the neuro analysis: keep the core idea, subject, and message; double down on why_it_works, the listed strengths, and scroll_stopping_factors; fix the listed weaknesses using the analysis recommendations. This is the same creative re-executed — not a new concept, product, or offer.
- Every variant is a finished AD and MUST carry on-image ad copy written as the LITERAL words that appear on the ad:
  - visual_elements.text_overlays = the headline only. Max 8 words. Punchy and complete — never a cut-off sentence.
  - messaging.value_proposition = the support line only. Max 14 words.
  - messaging.cta = short button text (max 4 words), only if it helps.
  These fields must contain ONLY the copy itself — never labels or prefixes like "Headline:", "Top headline", "Support:", "CTA:", never surrounding quotes, never commentary. Sharpen the source's own copy; if the source is light on text, write strong copy from its core message. Never leave headline or support blank.
- rationale = 2-4 sentences: which analysis insight this variant exploits and how the execution differs.
- Explore a different execution each time: vary layout, art direction, color, backdrop, and composition. Emphasize the focus area: ${focusArea}.
- Keep the exact same JSON schema as the source and keep the same product. Do NOT describe or redesign the product — real reference photos of it are attached when the image is rendered.
- Apply the user's special instructions as a high-priority constraint, baked into the returned context. Keep any body-care framing ad-safe and non-sexual.
- Return ONLY a JSON object: { "variants": [ { "rationale": "...", "context": { ...same schema... } } ] }
${strict ? "- Do not include any extra text outside JSON." : ""}
${channelLine}

User special instructions:
${trimmedSpecialInstructions || "none"}

Source JSON:
${sourceJson}${this.buildAnalysisGuidance()}${this.buildAiGuidance()}${this.buildAdCopyGuidance()}`;

      const effectivePrompt = promptOverride || prompt;
      let content = "";
      if (provider === "openai") {
        const response = await this.callOpenAIChat({
          model,
          messages: [{ role: "user", content: effectivePrompt }],
          temperature: strict ? 0.3 : creativity,
          max_completion_tokens: 3000,
          response_format: { type: "json_object" },
        });
        content = this.getOpenAIChatContentCandidatesOrThrow(response);
      } else if (provider === "gemini") {
        const data = await this.callGeminiGenerate(model, {
          contents: [
            {
              parts: [{ text: effectivePrompt }],
            },
          ],
        });
        content = data?.candidates?.[0]?.content?.parts
          ?.map((part) => part.text)
          .filter(Boolean)
          .join("\n");
      } else if (provider === "claude") {
        content = await this.callClaude({
          model,
          // Each variant echoes the full analysis schema back as `context`, so
          // 2500 truncated the JSON mid-object ("Incomplete JSON"). 8000 gives
          // headroom while staying under the non-streaming HTTP timeout ceiling.
          max_tokens: 8000,
          temperature: strict ? 0.2 : creativity,
          messages: [
            { role: "user", content: [{ type: "text", text: effectivePrompt }] },
          ],
        });
      }
      const parsed = this.extractJSONFromCandidates(content);
      const list = Array.isArray(parsed)
        ? parsed
        : parsed.variants || parsed.items || parsed.data;

      if (!Array.isArray(list)) {
        throw new Error("Model did not return a variants array");
      }

      return list.map((item, index) => {
        const rawContext = item.context ?? item;
        // Carry the concept rationale inside the context so the image brief
        // can restate WHY this execution should work, not just what it shows.
        const contextWithConcept =
          rawContext && typeof rawContext === "object" && !Array.isArray(rawContext)
            ? {
                ...rawContext,
                concept_rationale: String(item.rationale || "").trim(),
                promptChannel: channel || "",
              }
            : rawContext;
        return {
          id: item.id ?? `${Date.now()}-${index}`,
          title: item.title ?? `Variant ${index + 1}`,
          rationale: item.rationale ?? "",
          context: this.withVariantCopyFallback(contextWithConcept, {
            specialInstructions: trimmedSpecialInstructions,
            promptChannel: channel || "",
            focusArea,
          }),
          promptChannel: channel || "",
        };
      });
    };

    if (_previewOnly) {
      return await run(1, false, buildPromptChannelPool(1)[0] || "");
    }

    const safeQuantity = Math.max(1, Math.min(quantity, MAX_VARIANT_QUANTITY));
    const maxPromptChannels = 10;
    const baseTime = Date.now();
    const channelCount = Math.min(maxPromptChannels, safeQuantity);
    const promptChannels = buildPromptChannelPool(channelCount);
    const jobsByChannel = Array.from({ length: channelCount }, () => []);
    for (let index = 0; index < safeQuantity; index += 1) {
      const channelIndex = index % channelCount;
      const promptNumber = Math.floor(index / channelCount) + 1;
      jobsByChannel[channelIndex].push({
        index,
        channel: promptChannels[channelIndex],
        promptNumber,
      });
    }
    const out = new Array(safeQuantity);

    const worker = async (jobs) => {
      for (const job of jobs) {
        const scopedChannel =
          job.promptNumber > 1
            ? `${job.channel} • prompt ${job.promptNumber}`
            : job.channel;
        try {
          const generated = await run(1, false, scopedChannel);
          out[job.index] = generated?.[0] || null;
        } catch (error) {
          const message = String(error?.message || "");
          const shouldRetry =
            message.includes("Incomplete JSON") ||
            message.includes("No JSON") ||
            message.includes("Empty response") ||
            message.includes("variants array");
          if (shouldRetry) {
            try {
              const retryGenerated = await run(1, true, scopedChannel);
              out[job.index] = retryGenerated?.[0] || null;
            } catch (retryError) {
              throw new Error(
                `Variant generation failed: ${retryError.message}`,
              );
            }
          } else {
            throw new Error(`Variant generation failed: ${message}`);
          }
        }
      }
    };

    await Promise.all(jobsByChannel.map((jobs) => worker(jobs)));

    return out
      .filter(Boolean)
      .slice(0, safeQuantity)
      .map((item, index) => ({
        ...item,
        id: item.id ?? `${baseTime}-${index}`,
        title: item.title ?? `Variant ${index + 1}`,
      }));
  }

  async generateSegmentedVariants(analysis, options) {
    const {
      quantity = 3,
      creativity = 0.7,
      focusArea = "Visual Hook",
      model = DEFAULT_VARIANT_MODEL,
      videoModel = DEFAULT_VIDEO_MODEL,
      videoResolution = "720p",
      referenceImages = [],
      specialInstructions = "",
    } = options || {};

    const sourceSegments = Array.isArray(analysis?.segments)
      ? analysis.segments
      : [];
    if (sourceSegments.length === 0) {
      throw new Error("Segmented analysis missing segments");
    }
    const actorProfile = analysis?.actor_profile || {};
    const continuityLocks = analysis?.continuity_locks || {};
    const sharedReferenceImages = this.collectVideoReferenceImages({
      referenceImages,
    });
    const sharedReferenceNotes = this.describeReferenceImages(
      sharedReferenceImages,
    );
    const trimmedSpecialInstructions = String(specialInstructions || "").trim();
    const supportedDurations = this.getSupportedVideoDurations(
      videoModel,
      videoResolution,
    );
    const minSceneCount = Math.max(2, Math.min(3, sourceSegments.length || 2));
    const maxSceneCount = Math.max(
      minSceneCount,
      Math.min(8, sourceSegments.length + 2),
    );

    const provider = model.startsWith("gemini")
      ? "gemini"
      : model.startsWith("claude")
        ? "claude"
        : "openai";

    const segmentSummary = sourceSegments
      .map(
        (seg) =>
          `Batch ${seg.batchIndex}: ${seg.startSec}s-${seg.endSec}s (${seg.durationSec}s)`,
      )
      .join("\n");

    const compactSegments = sourceSegments.map((seg) => ({
      batchIndex: seg?.batchIndex,
      startSec: seg?.startSec,
      endSec: seg?.endSec,
      durationSec: seg?.durationSec,
      why_it_works: this.compactText(
        seg?.why_it_works || seg?.whyItWorks || "",
        220,
      ),
      environment_context: this.compactContextForPrompt(
        seg?.environment_context || {},
        {
          maxDepth: 4,
          maxArray: 6,
          maxKeys: 18,
          maxString: 160,
        },
      ),
      action_context: this.compactContextForPrompt(seg?.action_context || {}, {
        maxDepth: 5,
        maxArray: 8,
        maxKeys: 20,
        maxString: 160,
      }),
      script_context: this.compactScriptContext(seg?.script_context || {}, {
        maxWordsPerLine: 8,
        maxTotalWords: 24,
      }),
    }));
    const compactActor = this.compactContextForPrompt(actorProfile, {
      maxDepth: 4,
      maxArray: 6,
      maxKeys: 18,
      maxString: 200,
    });
    const compactContinuity = this.compactContextForPrompt(continuityLocks, {
      maxDepth: 4,
      maxArray: 6,
      maxKeys: 18,
      maxString: 200,
    });
    const copyGuidance = this.buildAdCopyGuidance();
    const buildPromptChannelPool = (count) => {
      const baseChannels = [
        "Grounded testimonial realism",
        "Premium product-demo realism",
        "Problem-to-solution realism",
        "Documentary explainer realism",
        "UGC confessional realism",
        "Editorial minimal realism",
        "Clinical macro realism",
        "High-intent direct response realism",
      ];
      const channels = [];
      for (let i = 0; i < count; i += 1) {
        if (i < baseChannels.length) {
          channels.push(baseChannels[i]);
        } else {
          channels.push(
            `Alternate realistic angle ${i + 1} focused on ${focusArea}`,
          );
        }
      }
      return channels;
    };

    const buildPrompt = (strict, channel) => {
      const channelLine = channel
        ? `Prompt channel: ${channel}.\n- Keep the full variant aligned to this channel while staying realistic.\n`
        : "";
      return `You are a live-action ad director and continuity editor planning a stitchable short-form ad.
Create exactly 1 distinct variant from the source analysis below.

Rules:
- Do NOT mirror the source video's batch count just because it exists.
- Re-cut the source into the number of output scenes that best serves pacing and clarity.
- Choose between ${minSceneCount} and ${maxSceneCount} output scenes.
- Each output scene duration must be one of: ${supportedDurations.join(", ")} seconds.
- The total runtime should be director-led, not copied from the upload or user input.
- Every scene must be independently renderable and stitch cleanly to the next.
- Stay photoreal and live-action. Real skin texture, real lighting, believable motion.
- Use the SAME actor across all scenes (actor_profile is LOCKED).
- Preserve continuity locks, product identity, and environment logic.
- If shared reference images are provided, treat them as hard continuity anchors for actor/product appearance.
- Do NOT introduce new characters or unrelated locations.
- Do NOT reference future scenes explicitly inside script_context.
- Keep spoken copy concise enough to finish naturally within each scene duration.
- Apply the user's special instructions across the full scene plan as a high-priority constraint.
- Bake those instructions into environment/action/script choices instead of ignoring them.
- Return ONLY JSON in this exact shape:
{
  "variants": [
    {
      "title": "",
      "rationale": "",
      "director_summary": "",
      "totalDurationSec": 0,
      "segments": [
        {
          "batchIndex": 1,
          "durationSec": 4,
          "sourceBatchIndices": [1],
          "render_channel": "",
          "stitch_intent": "",
          "realism_notes": "",
          "why_it_works": "",
          "environment_context": { },
          "action_context": { },
          "script_context": { }
        }
      ]
    }
  ]
}
${strict ? "Return ONLY JSON. No extra text." : ""}

${channelLine}Focus area:
${focusArea}

Actor profile (locked):
${JSON.stringify(compactActor)}

Continuity locks (locked):
${JSON.stringify(compactContinuity)}

User special instructions:
${trimmedSpecialInstructions || "none"}

Shared reference image notes:
${sharedReferenceNotes || "none"}

Source segmentation windows:
${segmentSummary}

Source segments JSON:
${JSON.stringify(compactSegments)}${copyGuidance}${this.buildAiGuidance()}`;
    };

    const geminiReferenceParts =
      provider === "gemini" && sharedReferenceImages.length > 0
        ? await this.buildGeminiReferenceImageParts(sharedReferenceImages, {
            maxImages: 3,
            maxPerImageBytes: 450_000,
            maxTotalBytes: 900_000,
          })
        : [];

    const run = async (strict, channel) => {
      const prompt = buildPrompt(strict, channel);
      let content = "";
      if (provider === "openai") {
        const response = await this.callOpenAIChat({
          model,
          messages: [{ role: "user", content: prompt }],
          temperature: strict ? 0.3 : creativity,
          max_completion_tokens: 3000,
          response_format: { type: "json_object" },
        });
        content = this.getOpenAIChatContentCandidatesOrThrow(response);
      } else if (provider === "gemini") {
        const data = await this.callGeminiGenerate(model, {
          contents: [{ parts: [...geminiReferenceParts, { text: prompt }] }],
        });
        content = data?.candidates?.[0]?.content?.parts
          ?.map((part) => part.text)
          .filter(Boolean)
          .join("\n");
      } else if (provider === "claude") {
        content = await this.callClaude({
          model,
          // Each variant echoes the full analysis schema back as `context`, so
          // 2500 truncated the JSON mid-object ("Incomplete JSON"). 8000 gives
          // headroom while staying under the non-streaming HTTP timeout ceiling.
          max_tokens: 8000,
          temperature: strict ? 0.2 : creativity,
          messages: [
            { role: "user", content: [{ type: "text", text: prompt }] },
          ],
        });
      }

      const parsed = this.extractJSONFromCandidates(content);
      const list = Array.isArray(parsed)
        ? parsed
        : parsed.variants || parsed.items || parsed.data;
      if (!Array.isArray(list)) {
        throw new Error("Model did not return a variants array");
      }
      return list.map((item) => ({
        ...item,
        promptChannel: item?.promptChannel || channel || "",
      }));
    };

    const safeQuantity = Math.max(1, Math.min(quantity, MAX_VARIANT_QUANTITY));
    const maxPromptChannels = 8;
    const baseTime = Date.now();
    const channelCount = Math.min(maxPromptChannels, safeQuantity);
    const promptChannels = buildPromptChannelPool(channelCount);
    const jobsByChannel = Array.from({ length: channelCount }, () => []);
    for (let index = 0; index < safeQuantity; index += 1) {
      const channelIndex = index % channelCount;
      const promptNumber = Math.floor(index / channelCount) + 1;
      jobsByChannel[channelIndex].push({
        index,
        channel: promptChannels[channelIndex],
        promptNumber,
      });
    }
    const out = new Array(safeQuantity);

    const worker = async (jobs) => {
      for (const job of jobs) {
        const scopedChannel =
          job.promptNumber > 1
            ? `${job.channel} • prompt ${job.promptNumber}`
            : job.channel;
        try {
          const generated = await run(false, scopedChannel);
          out[job.index] = generated?.[0] || null;
        } catch (error) {
          const message = String(error?.message || "");
          const shouldRetry =
            message.includes("Incomplete JSON") ||
            message.includes("No JSON") ||
            message.includes("Empty response") ||
            message.includes("variants array");
          if (!shouldRetry) {
            throw error;
          }
          const retry = await run(true, scopedChannel);
          out[job.index] = retry?.[0] || null;
        }
      }
    };

    await Promise.all(jobsByChannel.map((jobs) => worker(jobs)));

    const normalized = out
      .filter(Boolean)
      .slice(0, safeQuantity)
      .map((item, index) => {
        const rawSegments = Array.isArray(item?.segments) ? item.segments : [];
        const fallbackSegments = sourceSegments.slice(
          0,
          Math.max(
            minSceneCount,
            Math.min(maxSceneCount, sourceSegments.length),
          ),
        );
        const renderSegments =
          rawSegments.length > 0 ? rawSegments : fallbackSegments;
        let cursor = 0;
        const normalizedSegments = renderSegments
          .slice(0, maxSceneCount)
          .map((candidate, segIndex) => {
            const sourceBatchIndices = Array.isArray(
              candidate?.sourceBatchIndices,
            )
              ? candidate.sourceBatchIndices
                  .map((value) => Number(value))
                  .filter((value) => Number.isFinite(value))
              : [];
            const mappedSourceSegments = sourceBatchIndices
              .map((batchIndex) =>
                sourceSegments.find(
                  (segment) => segment?.batchIndex === batchIndex,
                ),
              )
              .filter(Boolean);
            const sourceSegment =
              mappedSourceSegments[0] ||
              sourceSegments[Math.min(segIndex, sourceSegments.length - 1)] ||
              {};
            const durationSec = this.snapVideoSceneDuration(
              candidate?.durationSec || sourceSegment?.durationSec || 8,
              videoModel,
              videoResolution,
            );
            const environment = this.compactContextForPrompt(
              candidate?.environment_context ||
                candidate?.environment ||
                sourceSegment?.environment_context ||
                {},
              {
                maxDepth: 4,
                maxArray: 6,
                maxKeys: 20,
                maxString: 180,
              },
            );
            const action = this.compactContextForPrompt(
              {
                ...(candidate?.action_context ||
                  candidate?.action ||
                  sourceSegment?.action_context ||
                  {}),
                ...(actorProfile ? { actor: actorProfile } : {}),
              },
              {
                maxDepth: 5,
                maxArray: 10,
                maxKeys: 22,
                maxString: 180,
              },
            );
            const script = this.compactScriptContext(
              candidate?.script_context ||
                candidate?.script ||
                sourceSegment?.script_context ||
                {},
              {
                maxWordsPerLine: Math.min(
                  10,
                  Math.max(6, Math.round(durationSec * 1.5)),
                ),
                maxTotalWords: Math.max(12, Math.round(durationSec * 3)),
              },
            );
            const startSec = Number(cursor.toFixed(2));
            cursor += durationSec;
            const endSec = Number(cursor.toFixed(2));
            return {
              batchIndex: segIndex + 1,
              startSec,
              endSec,
              durationSec,
              sourceBatchIndices:
                sourceBatchIndices.length > 0
                  ? sourceBatchIndices
                  : [sourceSegment?.batchIndex || segIndex + 1],
              promptChannel: item?.promptChannel || "",
              renderChannel:
                candidate?.render_channel ||
                candidate?.renderChannel ||
                item?.promptChannel ||
                "",
              stitch_intent:
                candidate?.stitch_intent || candidate?.stitchIntent || "",
              realism_notes:
                candidate?.realism_notes || candidate?.realismNotes || "",
              why_it_works:
                candidate?.why_it_works ||
                candidate?.whyItWorks ||
                sourceSegment?.why_it_works ||
                "",
              environment_context: environment,
              action_context: action,
              script_context: script,
            };
          });
        const totalDurationSec = Number(
          normalizedSegments
            .reduce(
              (sum, segment) => sum + Number(segment?.durationSec || 0),
              0,
            )
            .toFixed(2),
        );

        return {
          id: item.id ?? `${baseTime}-${index}`,
          title: item.title ?? `Variant ${index + 1}`,
          rationale: item.rationale ?? "",
          directorSummary:
            item?.director_summary || item?.directorSummary || "",
          promptChannel: item?.promptChannel || "",
          totalDurationSec,
          segments: normalizedSegments,
        };
      });

    if (normalized.length === 0) {
      throw new Error("No segmented variants generated");
    }

    return normalized;
  }

  async analyzePoorPerformingCreative(context, performance = {}, options = {}) {
    const model = String(options?.model || GEMINI_VISION_MODEL).trim();
    const provider =
      model.startsWith("openai") || model.startsWith("gpt")
        ? "openai"
        : model.startsWith("claude")
          ? "claude"
          : "gemini";
    const isVideo =
      String(options?.mediaType || context?.type || "").toLowerCase() ===
        "video" || Array.isArray(context?.segments);
    const segments = Array.isArray(context?.segments) ? context.segments : [];
    const dropOffTimeSec = Number(performance?.dropOffTimeSec);
    const failingSegment = Number.isFinite(dropOffTimeSec)
      ? segments.find(
          (segment) =>
            Number(segment?.startSec) <= dropOffTimeSec &&
            Number(segment?.endSec) >= dropOffTimeSec,
        ) || null
      : null;
    const compactContext = this.compactContextForPrompt(context || {}, {
      maxDepth: 5,
      maxArray: 10,
      maxKeys: 24,
      maxString: 220,
    });
    const compactFailingSegment = failingSegment
      ? this.compactContextForPrompt(failingSegment, {
          maxDepth: 4,
          maxArray: 8,
          maxKeys: 20,
          maxString: 180,
        })
      : null;
    const performancePayload = {
      mediaType: isVideo ? "video" : "image",
      hookRatePercent: Number.isFinite(Number(performance?.hookRatePercent))
        ? Number(performance.hookRatePercent)
        : null,
      holdRatePercent: Number.isFinite(Number(performance?.holdRatePercent))
        ? Number(performance.holdRatePercent)
        : null,
      clickThroughRatePercent: Number.isFinite(
        Number(performance?.clickThroughRatePercent),
      )
        ? Number(performance.clickThroughRatePercent)
        : null,
      dropOffRatePercent: Number.isFinite(
        Number(performance?.dropOffRatePercent),
      )
        ? Number(performance.dropOffRatePercent)
        : null,
      dropOffTimeSec: Number.isFinite(dropOffTimeSec) ? dropOffTimeSec : null,
      notes: String(performance?.notes || "").trim(),
    };
    const prompt = `You are a direct-response creative strategist diagnosing why an ad underperformed.

Rules:
- Concrete and commercially useful. No praise unless it explains the failure.
- Diagnose why hook, clarity, trust, pacing, offer, or visual hierarchy failed.
- For video, use hook rate + drop-off timing to infer break point.
- Keep each string value under 2 sentences. Max 4 failure_points, max 5 avoid_rules.
- For images, treat hook rate like thumb-stop power and diagnose first-impression issues.
- Return ONLY JSON in this exact shape:
{
  "summary": "",
  "psychology": {
    "attention_breakdown": "",
    "trust_breakdown": "",
    "motivation_breakdown": "",
    "audience_mismatch": ""
  },
  "structure": {
    "hook": "",
    "message_clarity": "",
    "offer": "",
    "visual_hierarchy": "",
    "pacing_or_flow": ""
  },
  "failure_points": [
    {
      "stage": "",
      "reason": "",
      "evidence": ""
    }
  ],
  "avoid_rules": [""],
  "negative_knowledge_summary": ""
}

Performance metrics:
${JSON.stringify(performancePayload)}

Nearest failure segment:
${JSON.stringify(compactFailingSegment || {})}

Creative context:
${JSON.stringify(compactContext)}${this.buildAiGuidance()}${this.buildAnalysisGuidance()}${this.buildAdCopyGuidance()}`;

    let content = "";
    if (provider === "openai") {
      const response = await this.callOpenAIChat({
        model,
        messages: [{ role: "user", content: prompt }],
        temperature: 0.3,
        max_completion_tokens: 1400,
        response_format: { type: "json_object" },
      });
      content = this.getOpenAIChatContentCandidatesOrThrow(response);
    } else if (provider === "claude") {
      content = await this.callClaude({
        model,
        max_tokens: 1400,
        temperature: 0.2,
        messages: [{ role: "user", content: [{ type: "text", text: prompt }] }],
      });
    } else {
      const data = await this.callGeminiGenerate(model, {
        contents: [{ parts: [{ text: prompt }] }],
        generationConfig: {
          temperature: 0.2,
          maxOutputTokens: 1400,
        },
      });
      content = data?.candidates?.[0]?.content?.parts
        ?.map((part) => part.text)
        .filter(Boolean)
        .join("\n");
    }

    const parsed = this.extractJSONFromCandidates(content);
    const avoidRules = (
      Array.isArray(parsed?.avoid_rules)
        ? parsed.avoid_rules
        : Array.isArray(parsed?.avoidRules)
          ? parsed.avoidRules
          : []
    )
      .map((rule) => String(rule || "").trim())
      .filter(Boolean)
      .slice(0, 8);

    return {
      summary: String(parsed?.summary || "").trim(),
      psychology:
        parsed?.psychology && typeof parsed.psychology === "object"
          ? parsed.psychology
          : {},
      structure:
        parsed?.structure && typeof parsed.structure === "object"
          ? parsed.structure
          : {},
      failurePoints: Array.isArray(parsed?.failure_points)
        ? parsed.failure_points
        : Array.isArray(parsed?.failurePoints)
          ? parsed.failurePoints
          : [],
      avoidRules,
      negativeKnowledgeSummary: String(
        parsed?.negative_knowledge_summary ||
          parsed?.negativeKnowledgeSummary ||
          "",
      ).trim(),
    };
  }

  async analyzeNeuroResponse(context, options = {}) {
    const model = String(options?.model || CLAUDE_DEFAULT_MODEL).trim();
    const provider =
      model.startsWith("openai") || model.startsWith("gpt")
        ? "openai"
        : model.startsWith("claude")
          ? "claude"
          : "gemini";
    const isVideo =
      String(options?.mediaType || context?.type || "").toLowerCase() ===
        "video" || Array.isArray(context?.segments);
    const segments = Array.isArray(context?.segments) ? context.segments : [];
    const compactContext = this.compactContextForPrompt(context || {}, {
      maxDepth: 5,
      maxArray: 12,
      maxKeys: 28,
      maxString: 280,
    });

    const segmentTimeline =
      isVideo && segments.length > 0
        ? segments.map((seg, i) => ({
            segment: i + 1,
            startSec: seg.startSec,
            endSec: seg.endSec,
            environment:
              seg.environment_context?.scene_description ||
              seg.environment_context?.setting ||
              "",
            action:
              seg.action_context?.primary_action ||
              seg.action_context?.movement ||
              "",
            script:
              seg.script_context?.dialogue ||
              seg.script_context?.narrative ||
              "",
            mood:
              seg.environment_context?.mood ||
              seg.environment_context?.tone ||
              "",
          }))
        : [];

    const prompt = `You are a neuromarketing scientist and cognitive neuroscience expert analyzing a creative ad ${isVideo ? "video" : "image"} using principles from Neurons Predict (Meta Tribe v2) methodology.

Your task: Analyze the creative from a brain-science perspective. Cross-reference the creative's visual, auditory, and narrative elements with established neuroscience research on attention, emotion, memory encoding, and decision-making.

Rules:
- Ground claims in specific neuroscience: name brain region or mechanism.
- For each trigger: stimulus → brain response → behavioral outcome (one line each).
- Reference specific ${isVideo ? "timestamps" : "spatial locations (top-left, center, etc.)"}.
- Score each dimension 1-10. Keep string values under 2 sentences each.
- Max 3-4 attention_grabbers, max 3 emotional triggers, max 4 improvement suggestions.
- Dense, no filler. Every word must add information.
- Return ONLY valid JSON in the exact shape below.
${
  isVideo
    ? `
Video segment timeline for cross-referencing:
${JSON.stringify(segmentTimeline)}
`
    : ""
}
Return this JSON shape:
{
  "overall_neuro_score": 0,
  "attention_analysis": {
    "score": 0,
    "predicted_attention_flow": ["First fixation point → reason", "Second fixation → reason"],
    "attention_grabbers": [
      {
        "element": "",
        "location": "",
        ${isVideo ? '"timestamp": "",' : ""}
        "brain_mechanism": "",
        "brain_region": "",
        "effectiveness": 0,
        "explanation": ""
      }
    ],
    "attention_blind_spots": [""]
  },
  "emotional_response": {
    "score": 0,
    "primary_emotion": "",
    "emotional_arc": ${isVideo ? '"description of emotional journey across video timeline"' : '"single emotional impression"'},
    "triggers": [
      {
        "stimulus": "",
        "emotion_triggered": "",
        "brain_pathway": "",
        "intensity": 0,
        "explanation": ""
      }
    ],
    "valence_balance": ""
  },
  "memory_encoding": {
    "score": 0,
    "brand_recall_prediction": "",
    "memory_hooks": [
      {
        "element": "",
        "encoding_type": "",
        "brain_mechanism": "",
        "durability": ""
      }
    ],
    "peak_end_assessment": ""
  },
  "cognitive_load": {
    "score": 0,
    "overall_assessment": "",
    "complexity_factors": [
      {
        "factor": "",
        "load_level": "",
        "brain_impact": "",
        "suggestion": ""
      }
    ],
    "processing_fluency": ""
  },
  "decision_triggers": {
    "score": 0,
    "persuasion_mechanisms": [
      {
        "mechanism": "",
        "neural_basis": "",
        "strength": 0,
        "location_or_timestamp": ""
      }
    ],
    "call_to_action_effectiveness": ""
  },
  "brain_region_map": [
    {
      "region": "",
      "activation_level": "",
      "triggered_by": "",
      "behavioral_implication": ""
    }
  ],
  "improvement_suggestions": [
    {
      "priority": 0,
      "area": "",
      "current_issue": "",
      "neuroscience_basis": "",
      "specific_change": "",
      "predicted_impact": ""
    }
  ],
  "summary": ""
}

Creative context:
${JSON.stringify(compactContext)}${this.buildAiGuidance()}${this.buildAnalysisGuidance()}`;

    let content = "";
    if (provider === "openai") {
      const response = await this.callOpenAIChat({
        model,
        messages: [{ role: "user", content: prompt }],
        temperature: 0.35,
        max_completion_tokens: 3000,
        response_format: { type: "json_object" },
      });
      content = this.getOpenAIChatContentCandidatesOrThrow(response);
    } else if (provider === "claude") {
      content = await this.callClaude({
        model,
        max_tokens: 3000,
        temperature: 0.25,
        messages: [{ role: "user", content: [{ type: "text", text: prompt }] }],
      });
    } else {
      const data = await this.callGeminiGenerate(model, {
        contents: [{ parts: [{ text: prompt }] }],
        generationConfig: {
          temperature: 0.25,
          maxOutputTokens: 3000,
        },
      });
      content = data?.candidates?.[0]?.content?.parts
        ?.map((part) => part.text)
        .filter(Boolean)
        .join("\n");
    }

    const parsed = this.extractJSONFromCandidates(content);

    const normalizeArray = (value, fallbackKey) =>
      Array.isArray(value)
        ? value
        : Array.isArray(parsed?.[fallbackKey])
          ? parsed[fallbackKey]
          : [];

    return {
      overallNeuroScore: Number(
        parsed?.overall_neuro_score || parsed?.overallNeuroScore || 0,
      ),
      attentionAnalysis: {
        score: Number(
          parsed?.attention_analysis?.score ||
            parsed?.attentionAnalysis?.score ||
            0,
        ),
        predictedAttentionFlow: normalizeArray(
          parsed?.attention_analysis?.predicted_attention_flow ||
            parsed?.attentionAnalysis?.predictedAttentionFlow,
        ),
        attentionGrabbers: normalizeArray(
          parsed?.attention_analysis?.attention_grabbers ||
            parsed?.attentionAnalysis?.attentionGrabbers,
        ),
        attentionBlindSpots: normalizeArray(
          parsed?.attention_analysis?.attention_blind_spots ||
            parsed?.attentionAnalysis?.attentionBlindSpots,
        ),
      },
      emotionalResponse: {
        score: Number(
          parsed?.emotional_response?.score ||
            parsed?.emotionalResponse?.score ||
            0,
        ),
        primaryEmotion: String(
          parsed?.emotional_response?.primary_emotion ||
            parsed?.emotionalResponse?.primaryEmotion ||
            "",
        ),
        emotionalArc: String(
          parsed?.emotional_response?.emotional_arc ||
            parsed?.emotionalResponse?.emotionalArc ||
            "",
        ),
        triggers: normalizeArray(
          parsed?.emotional_response?.triggers ||
            parsed?.emotionalResponse?.triggers,
        ),
        valenceBalance: String(
          parsed?.emotional_response?.valence_balance ||
            parsed?.emotionalResponse?.valenceBalance ||
            "",
        ),
      },
      memoryEncoding: {
        score: Number(
          parsed?.memory_encoding?.score || parsed?.memoryEncoding?.score || 0,
        ),
        brandRecallPrediction: String(
          parsed?.memory_encoding?.brand_recall_prediction ||
            parsed?.memoryEncoding?.brandRecallPrediction ||
            "",
        ),
        memoryHooks: normalizeArray(
          parsed?.memory_encoding?.memory_hooks ||
            parsed?.memoryEncoding?.memoryHooks,
        ),
        peakEndAssessment: String(
          parsed?.memory_encoding?.peak_end_assessment ||
            parsed?.memoryEncoding?.peakEndAssessment ||
            "",
        ),
      },
      cognitiveLoad: {
        score: Number(
          parsed?.cognitive_load?.score || parsed?.cognitiveLoad?.score || 0,
        ),
        overallAssessment: String(
          parsed?.cognitive_load?.overall_assessment ||
            parsed?.cognitiveLoad?.overallAssessment ||
            "",
        ),
        complexityFactors: normalizeArray(
          parsed?.cognitive_load?.complexity_factors ||
            parsed?.cognitiveLoad?.complexityFactors,
        ),
        processingFluency: String(
          parsed?.cognitive_load?.processing_fluency ||
            parsed?.cognitiveLoad?.processingFluency ||
            "",
        ),
      },
      decisionTriggers: {
        score: Number(
          parsed?.decision_triggers?.score ||
            parsed?.decisionTriggers?.score ||
            0,
        ),
        persuasionMechanisms: normalizeArray(
          parsed?.decision_triggers?.persuasion_mechanisms ||
            parsed?.decisionTriggers?.persuasionMechanisms,
        ),
        callToActionEffectiveness: String(
          parsed?.decision_triggers?.call_to_action_effectiveness ||
            parsed?.decisionTriggers?.callToActionEffectiveness ||
            "",
        ),
      },
      brainRegionMap: normalizeArray(
        parsed?.brain_region_map || parsed?.brainRegionMap,
      ),
      improvementSuggestions: normalizeArray(
        parsed?.improvement_suggestions || parsed?.improvementSuggestions,
      ).sort((a, b) => Number(a?.priority || 99) - Number(b?.priority || 99)),
      summary: String(parsed?.summary || "").trim(),
    };
  }

  async callHuggingFaceInference({ model, inputs, parameters = {} }, signal) {
    return this.callAiServer(
      "huggingface.inference",
      { model, inputs, parameters },
      { signal },
    );
  }

  async callTribeServer(payload, signal) {
    this.requireWorkspace();
    const requestUrl = `${this.getServerBaseUrl()}/ai/tribe`;
    const response = await fetch(requestUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ ...payload, workspace_id: this.workspaceId }),
      signal,
    });
    if (!response.ok) {
      const text = await response.text().catch(() => "");
      let parsed;
      try {
        parsed = JSON.parse(text);
      } catch {
        parsed = null;
      }
      throw new Error(
        parsed?.error || `Tribe server error (${response.status})`,
      );
    }
    return response.json();
  }

  async interpretBrainScan(tribeResult, options = {}) {
    const { signal, isVideo = false } = options;
    const activations = tribeResult?.activations;
    if (!activations) return null;

    const tribeContext = this.buildTribeContext(tribeResult);

    const prompt = `You are a neuroscience researcher interpreting REAL fMRI brain scan data from Meta's Tribe v2 model. This is actual predicted neural activation data from a ${isVideo ? "video" : "static image"} creative.

${tribeContext}

Your job: Explain what this brain activation data means in plain English. Be specific to THIS data — reference actual numbers, regions, timesteps, and peaks.

Return ONLY valid JSON:
{
  "overall": "2-3 sentence overall assessment of what the brain is doing when viewing this creative. Reference specific activation levels.",
  "consumerExperience": "What is the viewer actually experiencing moment by moment? Describe their internal experience: what grabs their eye first, what emotion hits, do they feel compelled to act? Ground every claim in the actual activation data.",
  "advertiserInsights": "What does this activation pattern mean for ad performance? Be specific: will this stop the scroll (occipital)? Will they remember the brand (temporal)? Will they consider buying (frontal)? Reference the actual numbers.",
  "peakMoments": "What happened at the peak activation moments? Which regions spiked and why that matters for the ad's effectiveness.",
  "regions": {
    "visual_cortex": "What the occipital activation level means for THIS specific creative. Is the visual attention strong enough? What would improve it?",
    "auditory_cortex": "What the temporal activation means. Is emotional/language processing engaged? Is brand recall likely?",
    "prefrontal_decision": "What the frontal activation means. Is the viewer in decision mode? Too much cognitive load? Just right?",
    "somatosensory": "What the parietal activation means. Is the viewer imagining using the product? Is spatial processing helping or confusing?"
  },
  "predictions": [
    {"metric": "Scroll-Stop Rate", "prediction": "High/Medium/Low with percentage estimate", "reasoning": "Based on occipital activation of X..."},
    {"metric": "Brand Recall", "prediction": "High/Medium/Low", "reasoning": "Based on temporal activation of X..."},
    {"metric": "Purchase Intent", "prediction": "High/Medium/Low", "reasoning": "Based on frontal activation of X..."},
    {"metric": "Share/Save Rate", "prediction": "High/Medium/Low", "reasoning": "Based on temporal + frontal pattern..."},
    {"metric": "Ad Fatigue Risk", "prediction": "High/Medium/Low", "reasoning": "Based on activation volatility and trend..."}
  ],
  "improvements": [
    {"area": "Specific area to improve", "issue": "What the data shows is weak", "fix": "Specific actionable change to make"}
  ]
}`;

    const rawText = await this.callClaude({
      model: CLAUDE_DEFAULT_MODEL,
      max_tokens: 3000,
      temperature: 0.3,
      messages: [{ role: "user", content: [{ type: "text", text: prompt }] }],
    });
    const parsed = this.extractJSONFromCandidates(
      String(rawText || "").trim() ? [String(rawText).trim()] : [],
    );

    return {
      overall: String(parsed?.overall || ""),
      consumerExperience: String(
        parsed?.consumerExperience || parsed?.consumer_experience || "",
      ),
      advertiserInsights: String(
        parsed?.advertiserInsights || parsed?.advertiser_insights || "",
      ),
      peakMoments: String(parsed?.peakMoments || parsed?.peak_moments || ""),
      regions: {
        visual_cortex: String(parsed?.regions?.visual_cortex || ""),
        auditory_cortex: String(parsed?.regions?.auditory_cortex || ""),
        prefrontal_decision: String(parsed?.regions?.prefrontal_decision || ""),
        somatosensory: String(parsed?.regions?.somatosensory || ""),
      },
      predictions: Array.isArray(parsed?.predictions)
        ? parsed.predictions.map((p) => ({
            metric: String(p?.metric || ""),
            prediction: String(p?.prediction || ""),
            reasoning: String(p?.reasoning || ""),
          }))
        : [],
      improvements: Array.isArray(parsed?.improvements)
        ? parsed.improvements.map((imp) => ({
            area: String(imp?.area || ""),
            issue: String(imp?.issue || ""),
            fix: String(imp?.fix || ""),
          }))
        : [],
    };
  }

  async runTribeV2Analysis(context, options = {}) {
    const { signal, onProgress } = options;
    const isVideo =
      String(options?.mediaType || context?.type || "").toLowerCase() ===
        "video" || Array.isArray(context?.segments);
    onProgress?.({ step: "tribe", message: "Running Tribe v2 brain scan..." });

    // Resolve the media URL — prefer a publicly accessible URL
    const rawImageUrl =
      context?.signedUrl ||
      context?.imageUrl ||
      context?.thumbnailUrl ||
      context?.url ||
      "";
    const rawVideoUrl = context?.videoSignedUrl || context?.videoUrl || "";

    // The Tribe model server downloads URLs from the public internet, so
    // browser-local blob:/data: URLs and inline base64 must be forwarded as
    // raw bytes via the *_b64 fields instead of a URL.
    const isFetchableUrl = (url) => {
      const value = String(url || "").trim();
      if (!value) return false;
      if (value.startsWith("blob:") || value.startsWith("data:")) return false;
      return value.startsWith("http://") || value.startsWith("https://");
    };

    const imageUrl = isFetchableUrl(rawImageUrl) ? rawImageUrl : "";
    const videoUrl = isFetchableUrl(rawVideoUrl) ? rawVideoUrl : "";

    const payload = {};
    if (isVideo && videoUrl) {
      payload.video_url = videoUrl;
    } else if (imageUrl) {
      payload.image_url = imageUrl;
    } else if (context?.imageBase64 || context?.image_b64) {
      payload.image_b64 = context.imageBase64 || context.image_b64;
      if (context?.imageMime || context?.image_mime) {
        payload.image_mime = context.imageMime || context.image_mime;
      }
    } else if (context?.videoBase64 || context?.video_b64) {
      payload.video_b64 = context.videoBase64 || context.video_b64;
      if (context?.videoMime || context?.video_mime) {
        payload.video_mime = context.videoMime || context.video_mime;
      }
    } else if (context?.file instanceof Blob) {
      // Read directly from a File/Blob if provided
      try {
        const buf = await context.file.arrayBuffer();
        const bytes = new Uint8Array(buf);
        let binary = "";
        const CHUNK = 0x8000;
        for (let i = 0; i < bytes.length; i += CHUNK) {
          binary += String.fromCharCode.apply(
            null,
            bytes.subarray(i, i + CHUNK),
          );
        }
        const b64 = btoa(binary);
        const mime = context.file.type || "";
        if (isVideo) {
          payload.video_b64 = b64;
          if (mime) payload.video_mime = mime;
        } else {
          payload.image_b64 = b64;
          if (mime) payload.image_mime = mime;
        }
      } catch {
        // fall through to text fallback below
      }
    }

    if (!payload.video_url && !payload.image_url && !payload.image_b64 && !payload.video_b64) {
      // Fallback: send a text description so Tribe can still predict
      const description =
        context?.visual?.primary_hook ||
        context?.summary ||
        context?.description ||
        "advertising creative";
      payload.text = String(description).slice(0, 500);
    }

    try {
      const result = await this.callTribeServer(payload, signal);
      if (result?.error) {
        return { error: result.error, images: [], activations: null };
      }
      return {
        overview: result.overview || null,
        timestepImages: Array.isArray(result.timestep_images)
          ? result.timestep_images
          : [],
        activations: result.activations || null,
        predictionsShape: result.predictions_shape || [],
        inputType: result.input_type || "unknown",
      };
    } catch (error) {
      return {
        error: error?.message || String(error),
        images: [],
        activations: null,
      };
    }
  }

  async runFullNeuroAnalysis(context, options = {}) {
    const { signal, onProgress } = options;
    const isVideo =
      String(options?.mediaType || context?.type || "").toLowerCase() ===
        "video" || Array.isArray(context?.segments);

    // Step 1: Tribe v2 brain scan via Modal GPU server
    let tribeResult = null;
    try {
      tribeResult = await this.runTribeV2Analysis(context, {
        signal,
        onProgress,
        mediaType: isVideo ? "video" : "image",
      });
    } catch (error) {
      tribeResult = { error: error?.message || String(error) };
    }
    onProgress?.({
      step: "tribe_done",
      message: "Brain scan complete",
      tribeResult,
    });

    // Step 2: Parallel delegation — Claude and Gemini analyze independently
    onProgress?.({
      step: "delegation",
      message: "Delegating to Claude and Gemini for independent analysis...",
    });

    // Build structured Tribe context for the AI prompts
    const tribeContext = this.buildTribeContext(tribeResult);

    const makeNeuroOptions = (model) => ({
      ...options,
      model,
      mediaType: isVideo ? "video" : "image",
      tribeContext,
    });

    const [claudeResult, geminiResult] = await Promise.allSettled([
      this.analyzeNeuroWithProvider(
        context,
        makeNeuroOptions(CLAUDE_DEFAULT_MODEL),
      ),
      this.analyzeNeuroWithProvider(
        context,
        makeNeuroOptions(GEMINI_VISION_MODEL),
      ),
    ]);

    const gptNeuro = claudeResult.status === "fulfilled" ? claudeResult.value : null;
    const geminiNeuro =
      geminiResult.status === "fulfilled" ? geminiResult.value : null;
    const gptError =
      claudeResult.status === "rejected" ? claudeResult.reason?.message : null;
    const geminiError =
      geminiResult.status === "rejected" ? geminiResult.reason?.message : null;

    onProgress?.({
      step: "delegation_done",
      message: "Independent analyses complete",
    });

    // Step 3: Synthesize combined unbiased response
    onProgress?.({
      step: "synthesis",
      message: "Synthesizing unbiased combined analysis...",
    });

    const synthesized = await this.synthesizeNeuroAnalyses({
      gptNeuro,
      geminiNeuro,
      gptError,
      geminiError,
      tribeResult,
      context,
      isVideo,
      signal,
    });

    onProgress?.({ step: "synthesis_done", message: "Synthesis complete" });

    // Step 4: Generate 10/10 neuro score variant prompt
    onProgress?.({
      step: "optimal_prompt",
      message: "Generating optimized 10/10 prompt...",
    });

    const optimalPrompt = await this.generateOptimalNeuroPrompt({
      synthesized,
      context,
      isVideo,
      signal,
    });

    onProgress?.({ step: "complete", message: "Full neuro analysis complete" });

    return {
      tribeResult,
      gptNeuro,
      geminiNeuro,
      gptError,
      geminiError,
      synthesized,
      optimalPrompt,
    };
  }

  buildTribeContext(tribeResult) {
    const a = tribeResult?.activations;
    if (!a) return "";
    const regions = a.regions || {};
    const functional = a.functional_groups || {};
    const peak = a.peak || {};
    const dynamics = a.temporal_dynamics || {};
    const hemi = a.hemisphere_dominance || {};
    const timeline = a.timeline || [];

    // Build a human-readable summary the AI can reason about
    const lines = [
      `\n## REAL TRIBE v2 fMRI BRAIN SCAN DATA (use this as ground truth for your analysis)`,
      `This is actual predicted neural activation data from Meta's Tribe v2 model, NOT hypothetical.`,
      `Your brain_region_map, scores, and analysis MUST be grounded in this data.\n`,
      `### Overall activation`,
      `- Mean: ${a.overall_mean}, Peak: ${a.overall_max}, Std: ${a.overall_std}`,
      `- ${a.n_timesteps} timesteps, ${a.n_vertices?.toLocaleString()} cortical vertices\n`,
      `### Region activations (time-averaged)`,
    ];
    for (const [name, scores] of Object.entries(regions)) {
      const label = name.replace(/_/g, " ");
      lines.push(
        `- ${label}: mean=${scores.mean}, max=${scores.max}, std=${scores.std}`,
      );
    }

    lines.push(`\n### Functional group activations`);
    for (const [name, data] of Object.entries(functional)) {
      const label = name.replace(/_/g, " ");
      lines.push(
        `- ${label} (${(data.regions || []).join(", ")}): mean=${data.mean}, max=${data.max}`,
      );
    }

    lines.push(`\n### Hemisphere dominance`);
    lines.push(
      `- Left hemisphere mean: ${hemi.left_mean}, Right: ${hemi.right_mean}`,
    );
    lines.push(
      `- Dominant: ${hemi.dominant} (laterality index: ${hemi.laterality_index})`,
    );

    if (peak.top_moments?.length) {
      lines.push(`\n### Peak activation moments`);
      for (const m of peak.top_moments) {
        lines.push(
          `- Timestep ${m.timestep}s: activation=${m.global_activation}, dominant region=${m.dominant_region.replace(/_/g, " ")}`,
        );
      }
    }

    if (dynamics.activation_trend) {
      lines.push(`\n### Temporal dynamics`);
      lines.push(`- Overall trend: ${dynamics.activation_trend}`);
      lines.push(`- Volatility: ${dynamics.volatility}`);
      if (dynamics.max_rise) {
        lines.push(
          `- Biggest activation spike: timestep ${dynamics.max_rise.from_timestep}→${dynamics.max_rise.to_timestep} (delta=${dynamics.max_rise.delta})`,
        );
      }
      if (dynamics.max_drop) {
        lines.push(
          `- Biggest activation drop: timestep ${dynamics.max_drop.from_timestep}→${dynamics.max_drop.to_timestep} (delta=${dynamics.max_drop.delta})`,
        );
      }
    }

    if (timeline.length > 0) {
      lines.push(`\n### Per-timestep activation timeline (sampled)`);
      // Show every timestep if <=8, otherwise sample evenly
      const maxShow = 8;
      const shown =
        timeline.length <= maxShow
          ? timeline
          : Array.from(
              { length: maxShow },
              (_, i) =>
                timeline[
                  Math.round((i * (timeline.length - 1)) / (maxShow - 1))
                ],
            );
      for (const frame of shown) {
        const regionVals = Object.entries(frame)
          .filter(([k]) => k !== "timestep")
          .sort(([, a], [, b]) => b - a)
          .map(([k, v]) => `${k.replace(/_/g, " ")}=${v}`)
          .join(", ");
        lines.push(`- t=${frame.timestep}s: ${regionVals}`);
      }
    }

    lines.push(`\n### Instructions for using this data`);
    lines.push(
      `- Your brain_region_map MUST reflect these actual activation levels — do not invent activations.`,
    );
    lines.push(
      `- Map region activations to cognitive functions: occipital=visual processing, temporal=auditory/language, frontal=decision-making/attention, parietal=spatial/somatosensory.`,
    );
    lines.push(
      `- Use peak moments to identify attention-grabbing elements at specific timestamps.`,
    );
    lines.push(
      `- Use temporal dynamics to assess emotional arc and engagement sustainability.`,
    );
    lines.push(
      `- Hemisphere dominance indicates processing style: left=analytical/verbal, right=emotional/spatial.`,
    );
    lines.push(
      `- activation_level in brain_region_map should be: "high" if region mean > ${(a.overall_mean * 1.5).toFixed(4)}, "moderate" if > ${(a.overall_mean * 0.75).toFixed(4)}, "low" otherwise.`,
    );

    return lines.join("\n");
  }

  async analyzeNeuroWithProvider(context, options = {}) {
    const model = String(options?.model || CLAUDE_DEFAULT_MODEL).trim();
    const provider =
      model.startsWith("openai") || model.startsWith("gpt")
        ? "openai"
        : model.startsWith("claude")
          ? "claude"
          : "gemini";
    const isVideo =
      String(options?.mediaType || context?.type || "").toLowerCase() ===
        "video" || Array.isArray(context?.segments);
    const segments = Array.isArray(context?.segments) ? context.segments : [];
    const compactContext = this.compactContextForPrompt(context || {}, {
      maxDepth: 5,
      maxArray: 12,
      maxKeys: 28,
      maxString: 280,
    });

    const segmentTimeline =
      isVideo && segments.length > 0
        ? segments.map((seg, i) => ({
            segment: i + 1,
            startSec: seg.startSec,
            endSec: seg.endSec,
            environment:
              seg.environment_context?.scene_description ||
              seg.environment_context?.setting ||
              "",
            action:
              seg.action_context?.primary_action ||
              seg.action_context?.movement ||
              "",
            script:
              seg.script_context?.dialogue ||
              seg.script_context?.narrative ||
              "",
            mood:
              seg.environment_context?.mood ||
              seg.environment_context?.tone ||
              "",
          }))
        : [];

    const tribeContext = options?.tribeContext || "";

    const prompt = `You are a neuromarketing scientist. Your job is to analyze a creative ad ${isVideo ? "video" : "image"} by interpreting REAL brain scan data from Meta's Tribe v2 fMRI prediction model.

## WHAT MAKES A HIGH-PERFORMING AD (target brain activation profile)
For a high-converting ad, we WANT these regions active:
- OCCIPITAL (visual cortex): HIGH activation = strong visual salience, the creative commands visual attention
- TEMPORAL (auditory/language): MODERATE-HIGH = emotional resonance, story comprehension, brand name processing
- FRONTAL (prefrontal/decision): MODERATE = engagement without overwhelm, indicates the viewer is considering action
- PARIETAL (somatosensory/spatial): LOW-MODERATE = tactile imagination, product-in-hand feeling

The IDEAL pattern: Strong occipital (grabs attention) → temporal spike (emotional hook lands) → sustained frontal (decision processing engaged). Parietal supports but shouldn't dominate.

RED FLAGS:
- Low occipital = creative doesn't grab visual attention, scroll-past risk
- Very high frontal + low temporal = too analytical, no emotional connection
- Flat temporal = no emotional arc, forgettable
- Dominant parietal = confusion, spatial disorientation from layout
${tribeContext}

## YOUR TASK
Compare the ACTUAL brain scan data above against the ideal activation profile. Score each dimension based on how close the real activations match what we WANT for a high-performing ad.

Rules:
- brain_region_map MUST directly use the measured activation levels from Tribe data. Do not invent numbers.
- Map each region's activation to what creative element likely caused it.
- Score 1-10 based on how well activations match the ideal profile above.
- Use peak moments to identify the strongest attention-grabbing elements.
- Use temporal dynamics to characterize the emotional arc.
- Hemisphere dominance: left=analytical processing, right=emotional/spatial.
- Reference specific ${isVideo ? "timestamps" : "spatial locations"}.
- Keep string values under 2 sentences. Dense, no filler.
- Max 3-4 attention_grabbers, max 3 emotional triggers, max 4 improvement suggestions.
- Return ONLY valid JSON.
${isVideo ? `Video segment timeline: ${JSON.stringify(segmentTimeline)}` : ""}
Return this JSON shape:
{
  "overall_neuro_score": 0,
  "attention_analysis": {
    "score": 0,
    "predicted_attention_flow": ["First fixation → reason"],
    "attention_grabbers": [{"element":"","location":"",${isVideo ? '"timestamp":"",' : ""}"brain_mechanism":"","brain_region":"","effectiveness":0,"explanation":""}],
    "attention_blind_spots": [""]
  },
  "emotional_response": {
    "score": 0, "primary_emotion": "", "emotional_arc": "",
    "triggers": [{"stimulus":"","emotion_triggered":"","brain_pathway":"","intensity":0,"explanation":""}],
    "valence_balance": ""
  },
  "memory_encoding": {
    "score": 0, "brand_recall_prediction": "",
    "memory_hooks": [{"element":"","encoding_type":"","brain_mechanism":"","durability":""}],
    "peak_end_assessment": ""
  },
  "cognitive_load": {
    "score": 0, "overall_assessment": "",
    "complexity_factors": [{"factor":"","load_level":"","brain_impact":"","suggestion":""}],
    "processing_fluency": ""
  },
  "decision_triggers": {
    "score": 0,
    "persuasion_mechanisms": [{"mechanism":"","neural_basis":"","strength":0,"location_or_timestamp":""}],
    "call_to_action_effectiveness": ""
  },
  "brain_region_map": [{"region":"","activation_level":"","triggered_by":"","behavioral_implication":""}],
  "ad_type_scores": {
    "top_of_funnel": {"score":0,"reason":""},
    "brand_awareness": {"score":0,"reason":""},
    "direct_response": {"score":0,"reason":""},
    "bottom_funnel": {"score":0,"reason":""},
    "retargeting": {"score":0,"reason":""},
    "ugc_social": {"score":0,"reason":""}
  },
  "improvement_suggestions": [{"priority":0,"area":"","current_issue":"","neuroscience_basis":"","specific_change":"","predicted_impact":""}],
  "summary": ""
}

Ad type scoring guide (score 1-10 based on brain activation fit):
- top_of_funnel: High occipital + high temporal = strong for discovery/awareness. Emotional hook matters most.
- brand_awareness: Sustained temporal + moderate frontal = brand processing and recall. Memory encoding score matters.
- direct_response: High frontal + moderate temporal = decision engagement with emotional urgency. CTA effectiveness matters.
- bottom_funnel: Very high frontal + low cognitive load = ready to convert. Decision triggers score matters.
- retargeting: High temporal (recognition) + moderate parietal (familiarity) = "I've seen this" processing.
- ugc_social: High temporal + low frontal = feels authentic, not salesy. Emotional > analytical.

Creative context:
${JSON.stringify(compactContext)}${this.buildAiGuidance()}`;

    let content = "";
    if (provider === "openai") {
      const response = await this.callOpenAIChat({
        model,
        messages: [{ role: "user", content: prompt }],
        temperature: 0.35,
        max_completion_tokens: 4500,
        response_format: { type: "json_object" },
      });
      content = this.getOpenAIChatContentCandidatesOrThrow(response);
    } else if (provider === "claude") {
      content = await this.callClaude({
        model,
        max_tokens: 4500,
        temperature: 0.25,
        messages: [{ role: "user", content: [{ type: "text", text: prompt }] }],
      });
    } else {
      const data = await this.callGeminiGenerate(model, {
        contents: [{ parts: [{ text: prompt }] }],
        generationConfig: {
          temperature: 0.25,
          maxOutputTokens: 4500,
          responseMimeType: "application/json",
        },
      });
      content = data?.candidates?.[0]?.content?.parts
        ?.map((part) => part.text)
        .filter(Boolean)
        .join("\n");
    }

    const parsed = this.extractJSONFromCandidates(content);
    const normalizeArray = (value) => (Array.isArray(value) ? value : []);

    return {
      provider,
      model,
      overallNeuroScore: Number(
        parsed?.overall_neuro_score || parsed?.overallNeuroScore || 0,
      ),
      attentionAnalysis: {
        score: Number(
          parsed?.attention_analysis?.score ||
            parsed?.attentionAnalysis?.score ||
            0,
        ),
        predictedAttentionFlow: normalizeArray(
          parsed?.attention_analysis?.predicted_attention_flow ||
            parsed?.attentionAnalysis?.predictedAttentionFlow,
        ),
        attentionGrabbers: normalizeArray(
          parsed?.attention_analysis?.attention_grabbers ||
            parsed?.attentionAnalysis?.attentionGrabbers,
        ),
        attentionBlindSpots: normalizeArray(
          parsed?.attention_analysis?.attention_blind_spots ||
            parsed?.attentionAnalysis?.attentionBlindSpots,
        ),
      },
      emotionalResponse: {
        score: Number(
          parsed?.emotional_response?.score ||
            parsed?.emotionalResponse?.score ||
            0,
        ),
        primaryEmotion: String(
          parsed?.emotional_response?.primary_emotion ||
            parsed?.emotionalResponse?.primaryEmotion ||
            "",
        ),
        emotionalArc: String(
          parsed?.emotional_response?.emotional_arc ||
            parsed?.emotionalResponse?.emotionalArc ||
            "",
        ),
        triggers: normalizeArray(
          parsed?.emotional_response?.triggers ||
            parsed?.emotionalResponse?.triggers,
        ),
        valenceBalance: String(
          parsed?.emotional_response?.valence_balance ||
            parsed?.emotionalResponse?.valenceBalance ||
            "",
        ),
      },
      memoryEncoding: {
        score: Number(
          parsed?.memory_encoding?.score || parsed?.memoryEncoding?.score || 0,
        ),
        brandRecallPrediction: String(
          parsed?.memory_encoding?.brand_recall_prediction ||
            parsed?.memoryEncoding?.brandRecallPrediction ||
            "",
        ),
        memoryHooks: normalizeArray(
          parsed?.memory_encoding?.memory_hooks ||
            parsed?.memoryEncoding?.memoryHooks,
        ),
        peakEndAssessment: String(
          parsed?.memory_encoding?.peak_end_assessment ||
            parsed?.memoryEncoding?.peakEndAssessment ||
            "",
        ),
      },
      cognitiveLoad: {
        score: Number(
          parsed?.cognitive_load?.score || parsed?.cognitiveLoad?.score || 0,
        ),
        overallAssessment: String(
          parsed?.cognitive_load?.overall_assessment ||
            parsed?.cognitiveLoad?.overallAssessment ||
            "",
        ),
        complexityFactors: normalizeArray(
          parsed?.cognitive_load?.complexity_factors ||
            parsed?.cognitiveLoad?.complexityFactors,
        ),
        processingFluency: String(
          parsed?.cognitive_load?.processing_fluency ||
            parsed?.cognitiveLoad?.processingFluency ||
            "",
        ),
      },
      decisionTriggers: {
        score: Number(
          parsed?.decision_triggers?.score ||
            parsed?.decisionTriggers?.score ||
            0,
        ),
        persuasionMechanisms: normalizeArray(
          parsed?.decision_triggers?.persuasion_mechanisms ||
            parsed?.decisionTriggers?.persuasionMechanisms,
        ),
        callToActionEffectiveness: String(
          parsed?.decision_triggers?.call_to_action_effectiveness ||
            parsed?.decisionTriggers?.callToActionEffectiveness ||
            "",
        ),
      },
      brainRegionMap: normalizeArray(
        parsed?.brain_region_map || parsed?.brainRegionMap,
      ),
      adTypeScores: (() => {
        const raw = parsed?.ad_type_scores || parsed?.adTypeScores || {};
        const normalize = (key) => ({
          score: Number(raw?.[key]?.score || 0),
          reason: String(raw?.[key]?.reason || ""),
        });
        return {
          topOfFunnel: normalize("top_of_funnel"),
          brandAwareness: normalize("brand_awareness"),
          directResponse: normalize("direct_response"),
          bottomFunnel: normalize("bottom_funnel"),
          retargeting: normalize("retargeting"),
          ugcSocial: normalize("ugc_social"),
        };
      })(),
      improvementSuggestions: normalizeArray(
        parsed?.improvement_suggestions || parsed?.improvementSuggestions,
      ).sort((a, b) => Number(a?.priority || 99) - Number(b?.priority || 99)),
      summary: String(parsed?.summary || "").trim(),
    };
  }

  async synthesizeNeuroAnalyses({
    gptNeuro,
    geminiNeuro,
    gptError,
    geminiError,
    tribeResult,
    context,
    isVideo,
    signal,
  }) {
    const analyses = [];
    if (gptNeuro) analyses.push({ source: "Claude", data: gptNeuro });
    if (geminiNeuro) analyses.push({ source: "Gemini", data: geminiNeuro });
    if (analyses.length === 0) {
      return {
        overallNeuroScore: 0,
        summary: "No analyses available to synthesize.",
        sources: [],
        error: [gptError, geminiError].filter(Boolean).join("; "),
      };
    }
    if (analyses.length === 1) {
      return { ...analyses[0].data, sources: [analyses[0].source] };
    }

    const summaryPayload = analyses.map((a) => ({
      source: a.source,
      overallScore: a.data.overallNeuroScore,
      attentionScore: a.data.attentionAnalysis?.score,
      emotionScore: a.data.emotionalResponse?.score,
      memoryScore: a.data.memoryEncoding?.score,
      cognitiveScore: a.data.cognitiveLoad?.score,
      decisionScore: a.data.decisionTriggers?.score,
      summary: a.data.summary,
      topGrabbers: (a.data.attentionAnalysis?.attentionGrabbers || [])
        .slice(0, 3)
        .map((g) => g?.element || ""),
      blindSpots: a.data.attentionAnalysis?.attentionBlindSpots || [],
      primaryEmotion: a.data.emotionalResponse?.primaryEmotion,
      topImprovements: (a.data.improvementSuggestions || [])
        .slice(0, 3)
        .map((s) => s?.specific_change || s?.specificChange || ""),
      brainRegions: (a.data.brainRegionMap || [])
        .slice(0, 4)
        .map((r) => ({
          region: r?.region,
          activation: r?.activation_level || r?.activationLevel,
        })),
    }));

    const tribeNotes = this.buildTribeContext(tribeResult);

    const prompt = `You are a neuromarketing meta-analyst. Two independent AI models have analyzed the same creative against REAL Tribe v2 brain scan data. Synthesize into one analysis.

Target activation profile for high-converting ads:
- Occipital (visual): HIGH = visual salience
- Temporal (emotion/language): MODERATE-HIGH = emotional resonance
- Frontal (decision): MODERATE = decision engagement
- Parietal (spatial): LOW-MODERATE = tactile imagination
Ideal pattern: occipital grab → temporal emotion spike → sustained frontal decision.
${tribeNotes}

Input analyses:
${JSON.stringify(summaryPayload)}

Rules:
- Where models agree, state with higher confidence.
- Where they disagree, note the disagreement and pick the better-supported claim (cite which model).
- Average numeric scores, rounding to nearest 0.5.
- CRITICAL: If Tribe v2 fMRI data is provided above, the brain_region_map MUST reflect the actual measured activations. Do not override real data with AI speculation.
- Use Tribe peak moments to anchor attention analysis timestamps.
- Use Tribe temporal dynamics to validate or correct the emotional arc claims from both models.
- Keep every string value under 2 sentences. Dense, no filler.
- Return ONLY valid JSON in this shape:
{
  "overall_neuro_score": 0,
  "confidence": "high/medium/low",
  "agreement_summary": "",
  "disagreements": [""],
  "attention_analysis": {
    "score": 0, "key_findings": [""], "blind_spots": [""]
  },
  "emotional_response": {
    "score": 0, "primary_emotion": "", "key_findings": [""]
  },
  "memory_encoding": {
    "score": 0, "key_findings": [""]
  },
  "cognitive_load": {
    "score": 0, "key_findings": [""]
  },
  "decision_triggers": {
    "score": 0, "key_findings": [""]
  },
  "brain_region_map": [{"region":"","activation_level":"","triggered_by":"","behavioral_implication":""}],
  "ad_type_scores": {
    "top_of_funnel": {"score":0,"reason":""},
    "brand_awareness": {"score":0,"reason":""},
    "direct_response": {"score":0,"reason":""},
    "bottom_funnel": {"score":0,"reason":""},
    "retargeting": {"score":0,"reason":""},
    "ugc_social": {"score":0,"reason":""}
  },
  "improvement_suggestions": [{"priority":0,"area":"","specific_change":"","predicted_impact":"","source_agreement":"both/claude/gemini"}],
  "summary": ""
}`;

    const rawContent = await this.callClaude({
      model: CLAUDE_DEFAULT_MODEL,
      max_tokens: 4000,
      temperature: 0.2,
      messages: [{ role: "user", content: [{ type: "text", text: prompt }] }],
    });
    const content = String(rawContent || "").trim();
    const parsed = this.extractJSONFromCandidates(content ? [content] : []);
    const normalizeArray = (value) => (Array.isArray(value) ? value : []);

    return {
      overallNeuroScore: Number(
        parsed?.overall_neuro_score || parsed?.overallNeuroScore || 0,
      ),
      confidence: String(parsed?.confidence || "medium"),
      agreementSummary: String(
        parsed?.agreement_summary || parsed?.agreementSummary || "",
      ),
      disagreements: normalizeArray(parsed?.disagreements),
      attentionAnalysis: {
        score: Number(parsed?.attention_analysis?.score || 0),
        keyFindings: normalizeArray(
          parsed?.attention_analysis?.key_findings ||
            parsed?.attention_analysis?.keyFindings,
        ),
        blindSpots: normalizeArray(
          parsed?.attention_analysis?.blind_spots ||
            parsed?.attention_analysis?.blindSpots,
        ),
      },
      emotionalResponse: {
        score: Number(parsed?.emotional_response?.score || 0),
        primaryEmotion: String(
          parsed?.emotional_response?.primary_emotion ||
            parsed?.emotional_response?.primaryEmotion ||
            "",
        ),
        keyFindings: normalizeArray(
          parsed?.emotional_response?.key_findings ||
            parsed?.emotional_response?.keyFindings,
        ),
      },
      memoryEncoding: {
        score: Number(parsed?.memory_encoding?.score || 0),
        keyFindings: normalizeArray(
          parsed?.memory_encoding?.key_findings ||
            parsed?.memory_encoding?.keyFindings,
        ),
      },
      cognitiveLoad: {
        score: Number(parsed?.cognitive_load?.score || 0),
        keyFindings: normalizeArray(
          parsed?.cognitive_load?.key_findings ||
            parsed?.cognitive_load?.keyFindings,
        ),
      },
      decisionTriggers: {
        score: Number(parsed?.decision_triggers?.score || 0),
        keyFindings: normalizeArray(
          parsed?.decision_triggers?.key_findings ||
            parsed?.decision_triggers?.keyFindings,
        ),
      },
      brainRegionMap: normalizeArray(
        parsed?.brain_region_map || parsed?.brainRegionMap,
      ),
      adTypeScores: (() => {
        const raw = parsed?.ad_type_scores || parsed?.adTypeScores || {};
        const normalize = (key) => ({
          score: Number(raw?.[key]?.score || 0),
          reason: String(raw?.[key]?.reason || ""),
        });
        return {
          topOfFunnel: normalize("top_of_funnel"),
          brandAwareness: normalize("brand_awareness"),
          directResponse: normalize("direct_response"),
          bottomFunnel: normalize("bottom_funnel"),
          retargeting: normalize("retargeting"),
          ugcSocial: normalize("ugc_social"),
        };
      })(),
      improvementSuggestions: normalizeArray(
        parsed?.improvement_suggestions || parsed?.improvementSuggestions,
      ).sort((a, b) => Number(a?.priority || 99) - Number(b?.priority || 99)),
      summary: String(parsed?.summary || "").trim(),
      sources: analyses.map((a) => a.source),
    };
  }

  async generateOptimalNeuroPrompt({ synthesized, context, isVideo, signal }) {
    const compactContext = this.compactContextForPrompt(context || {}, {
      maxDepth: 4,
      maxArray: 8,
      maxKeys: 20,
      maxString: 200,
    });

    const improvements = (synthesized?.improvementSuggestions || [])
      .slice(0, 5)
      .map((s) => s?.specific_change || s?.specificChange || "")
      .filter(Boolean);
    const blindSpots = synthesized?.attentionAnalysis?.blindSpots || [];
    const disagreements = synthesized?.disagreements || [];
    const currentScore = synthesized?.overallNeuroScore || 0;

    const prompt = `You are a neuromarketing creative director. Based on the neuro analysis of a creative ${isVideo ? "video" : "image"} that scored ${currentScore}/10, generate an optimized creative brief that would achieve a 10/10 neuro score.

Current creative context: ${JSON.stringify(compactContext)}

Neuro findings to address:
- Improvements needed: ${improvements.join("; ") || "none identified"}
- Attention blind spots: ${blindSpots.join("; ") || "none"}
- Analyst disagreements: ${disagreements.join("; ") || "none"}
- Current emotion: ${synthesized?.emotionalResponse?.primaryEmotion || "unknown"}
- Memory encoding score: ${synthesized?.memoryEncoding?.score || 0}/10

Rules:
- Be specific and actionable — describe exact visual elements, colors, compositions, timing.
- Address every weakness identified in the analysis.
- Maximize attention grabbers, emotional triggers, and memory hooks.
- Optimize cognitive load — not too simple, not too complex.
- Include specific neuroscience-backed techniques (e.g., Von Restorff effect, peak-end rule, dopamine anticipation loops).
- Dense, no filler. Under 300 words.
- Return ONLY valid JSON:
{
  "prompt": "The full creative generation prompt targeting 10/10 neuro score",
  "key_neuro_techniques": ["technique 1", "technique 2"],
  "predicted_score_improvement": { "from": ${currentScore}, "to": 0, "confidence": "" },
  "rationale": "Why this prompt addresses each weakness"
}`;

    const rawText = await this.callClaude({
      model: CLAUDE_DEFAULT_MODEL,
      max_tokens: 1500,
      temperature: 0.4,
      messages: [{ role: "user", content: [{ type: "text", text: prompt }] }],
    });
    const parsed = this.extractJSONFromCandidates(
      String(rawText || "").trim() ? [String(rawText).trim()] : [],
    );

    return {
      prompt: String(parsed?.prompt || "").trim(),
      keyNeuroTechniques: Array.isArray(
        parsed?.key_neuro_techniques || parsed?.keyNeuroTechniques,
      )
        ? parsed.key_neuro_techniques || parsed.keyNeuroTechniques
        : [],
      predictedScoreImprovement:
        parsed?.predicted_score_improvement ||
        parsed?.predictedScoreImprovement ||
        {},
      rationale: String(parsed?.rationale || "").trim(),
    };
  }

  /**
   * Follow-up conversation about one creative's neuro analysis.
   *
   * Each creative keeps its own thread, so the model is handed that creative's
   * synthesized findings, its brain-scan data and its 10/10 prompt — and
   * nothing from any other creative in the batch.
   */
  async chatAboutNeuroAnalysis({
    synthesized,
    optimalPrompt = null,
    tribeResult = null,
    context = null,
    fileName = "",
    isVideo = false,
    history = [],
    message: userMessage = "",
    model,
    signal,
  } = {}) {
    const question = String(userMessage || "").trim();
    if (!question) return "";

    const compactFindings = this.compactContextForPrompt(synthesized || {}, {
      maxDepth: 5,
      maxArray: 10,
      maxKeys: 26,
      maxString: 400,
    });
    const compactCreative = context
      ? this.compactContextForPrompt(context, {
          maxDepth: 4,
          maxArray: 8,
          maxKeys: 20,
          maxString: 200,
        })
      : null;
    const tribeContext = this.buildTribeContext(tribeResult);
    const transcript = (Array.isArray(history) ? history : [])
      .slice(-12)
      .map(
        (entry) =>
          `${entry?.role === "user" ? "User" : "You"}: ${String(entry?.content || "").slice(0, 1500)}`,
      )
      .join("\n");

    const prompt = `You are the neuromarketing scientist who produced the neuro analysis below for a single advertising ${isVideo ? "video" : "image"}${fileName ? ` named "${fileName}"` : ""}. The user is asking a follow-up about THIS creative only.

## YOUR NEURO FINDINGS (the analysis you already delivered)
${JSON.stringify(compactFindings)}
${
  optimalPrompt?.prompt
    ? `\n## THE 10/10 PROMPT YOU WROTE\n${optimalPrompt.prompt}\n${
        optimalPrompt.rationale ? `Rationale: ${optimalPrompt.rationale}` : ""
      }`
    : ""
}${compactCreative ? `\n\n## CREATIVE CONTEXT\n${JSON.stringify(compactCreative)}` : ""}${tribeContext}

${transcript ? `## CONVERSATION SO FAR\n${transcript}\n` : ""}
## THE QUESTION
${question}

Rules:
- Answer only from the findings and scan data above. Never invent activation numbers.
- Defend or revise the scores you gave, and say plainly when the data can't settle the question.
- Be concrete: name the element, the region, the timestamp or location.
- Plain prose, no JSON, no markdown headers. Under 200 words unless the user asks for depth.`;

    const text = await this.generateTextWithModel(
      model || CLAUDE_DEFAULT_MODEL,
      prompt,
      { temperature: 0.4, maxTokens: 1200, signal },
    );
    return String(text || "").trim();
  }

  async generateVideoNeuroReport(context, variantSegments, options = {}) {
    const model = String(options?.model || CLAUDE_DEFAULT_MODEL).trim();
    const provider =
      model.startsWith("openai") || model.startsWith("gpt")
        ? "openai"
        : model.startsWith("claude")
          ? "claude"
          : "gemini";

    const segments = Array.isArray(context?.segments) ? context.segments : [];
    const compactContext = this.compactContextForPrompt(context || {}, {
      maxDepth: 5,
      maxArray: 12,
      maxKeys: 30,
      maxString: 300,
    });

    const segmentTimeline = segments.map((seg, i) => {
      const variantSeg = variantSegments?.[i] || {};
      return {
        segment: i + 1,
        startSec: seg.startSec || variantSeg.startSec,
        endSec: seg.endSec || variantSeg.endSec,
        durationSec: seg.durationSec || variantSeg.durationSec,
        environment:
          seg.environment_context?.scene_description ||
          seg.environment_context?.setting ||
          "",
        action:
          seg.action_context?.primary_action ||
          seg.action_context?.movement ||
          "",
        script:
          seg.script_context?.dialogue || seg.script_context?.narrative || "",
        mood:
          seg.environment_context?.mood || seg.environment_context?.tone || "",
        renderChannel:
          variantSeg.renderChannel || variantSeg.render_channel || "",
        stitchIntent: variantSeg.stitch_intent || variantSeg.stitchIntent || "",
      };
    });

    const totalDuration = segmentTimeline.reduce(
      (acc, seg) => acc + (Number(seg.durationSec) || 0),
      0,
    );

    const prompt = `You are a neuromarketing scientist conducting a Meta Tribe v2 brain-scan analysis on a generated advertising video.

Video: ${segmentTimeline.length} stitched clips, ~${totalDuration}s total.
Task: Predict neural performance as if from an EEG/fMRI study (n=200+).

Keep ALL string values under 2 sentences. Be dense — every word must carry new information. No filler or hedging.
Max 2 brain regions per segment, max 2 key_triggers per segment, max 5 improvement_roadmap items, max 3 stitch_analysis items.

Return ONLY valid JSON in this exact shape:
{
  "report_title": "Neuro Performance Report",
  "overall_score": 0,
  "grade": "A/B/C/D/F",
  "predicted_performance": {
    "hook_rate_prediction": { "value": 0, "benchmark": "", "explanation": "" },
    "hold_rate_prediction": { "value": 0, "benchmark": "", "explanation": "" },
    "completion_rate_prediction": { "value": 0, "benchmark": "", "explanation": "" },
    "click_intent_prediction": { "value": 0, "benchmark": "", "explanation": "" }
  },
  "brain_scan_timeline": [
    {
      "segment": 1,
      "time_range": "0s-4s",
      "dominant_brain_regions": [
        { "region": "", "activation": "high/medium/low", "role": "" }
      ],
      "attention_level": 0,
      "emotional_valence": 0,
      "cognitive_load": 0,
      "neural_narrative": "",
      "key_triggers": [
        { "stimulus": "", "brain_response": "", "behavioral_outcome": "" }
      ],
      "stitch_continuity_score": 0
    }
  ],
  "attention_curve": {
    "peak_attention_timestamp": "",
    "attention_drop_timestamp": "",
    "predicted_drop_off_point": "",
    "attention_recovery_points": [""],
    "flow_description": ""
  },
  "emotional_arc": {
    "opening_emotion": "",
    "peak_emotion": "",
    "closing_emotion": "",
    "arc_type": "",
    "dopamine_triggers": [""],
    "cortisol_triggers": [""],
    "oxytocin_triggers": [""]
  },
  "memory_encoding_assessment": {
    "score": 0,
    "brand_recall_probability": "",
    "strongest_memory_anchor": "",
    "weakest_memory_point": "",
    "peak_end_rule_compliance": "",
    "spaced_repetition_elements": [""]
  },
  "stitch_analysis": [
    {
      "transition": "Segment X → Segment Y",
      "continuity_score": 0,
      "neural_disruption_risk": "",
      "cognitive_bridging": "",
      "suggestion": ""
    }
  ],
  "brain_region_activation_summary": [
    {
      "region": "",
      "peak_activation_segment": 0,
      "overall_activation": "high/medium/low",
      "function": "",
      "impact_on_ad_performance": ""
    }
  ],
  "improvement_roadmap": [
    {
      "rank": 1,
      "segment_affected": 0,
      "area": "",
      "current_state": "",
      "neuroscience_rationale": "",
      "specific_change": "",
      "predicted_score_lift": 0,
      "effort": "low/medium/high"
    }
  ],
  "executive_summary": ""
}

Segment timeline for cross-referencing:
${JSON.stringify(segmentTimeline)}

Full creative context:
${JSON.stringify(compactContext)}${this.buildAiGuidance()}${this.buildAnalysisGuidance()}`;

    let content = "";
    if (provider === "openai") {
      const response = await this.callOpenAIChat({
        model,
        messages: [{ role: "user", content: prompt }],
        temperature: 0.3,
        max_completion_tokens: 4000,
        response_format: { type: "json_object" },
      });
      content = this.getOpenAIChatContentCandidatesOrThrow(response);
    } else if (provider === "claude") {
      content = await this.callClaude({
        model,
        max_tokens: 4000,
        temperature: 0.2,
        messages: [{ role: "user", content: [{ type: "text", text: prompt }] }],
      });
    } else {
      const data = await this.callGeminiGenerate(model, {
        contents: [{ parts: [{ text: prompt }] }],
        generationConfig: {
          temperature: 0.2,
          maxOutputTokens: 4000,
        },
      });
      content = data?.candidates?.[0]?.content?.parts
        ?.map((part) => part.text)
        .filter(Boolean)
        .join("\n");
    }

    const parsed = this.extractJSONFromCandidates(content);
    const normalizeArray = (value) => (Array.isArray(value) ? value : []);

    const gradeFromScore = (score) => {
      const n = Number(score || 0);
      if (n >= 9) return "A";
      if (n >= 7) return "B";
      if (n >= 5) return "C";
      if (n >= 3) return "D";
      return "F";
    };

    const overallScore = Number(
      parsed?.overall_score || parsed?.overallScore || 0,
    );

    return {
      reportTitle: String(
        parsed?.report_title ||
          parsed?.reportTitle ||
          "Neuro Performance Report",
      ),
      overallScore,
      grade: String(parsed?.grade || gradeFromScore(overallScore)),
      predictedPerformance: {
        hookRate:
          parsed?.predicted_performance?.hook_rate_prediction ||
          parsed?.predictedPerformance?.hookRatePrediction ||
          {},
        holdRate:
          parsed?.predicted_performance?.hold_rate_prediction ||
          parsed?.predictedPerformance?.holdRatePrediction ||
          {},
        completionRate:
          parsed?.predicted_performance?.completion_rate_prediction ||
          parsed?.predictedPerformance?.completionRatePrediction ||
          {},
        clickIntent:
          parsed?.predicted_performance?.click_intent_prediction ||
          parsed?.predictedPerformance?.clickIntentPrediction ||
          {},
      },
      brainScanTimeline: normalizeArray(
        parsed?.brain_scan_timeline || parsed?.brainScanTimeline,
      ).map((entry) => ({
        segment: Number(entry?.segment || 0),
        timeRange: String(entry?.time_range || entry?.timeRange || ""),
        dominantBrainRegions: normalizeArray(
          entry?.dominant_brain_regions || entry?.dominantBrainRegions,
        ),
        attentionLevel: Number(
          entry?.attention_level || entry?.attentionLevel || 0,
        ),
        emotionalValence: Number(
          entry?.emotional_valence || entry?.emotionalValence || 0,
        ),
        cognitiveLoad: Number(
          entry?.cognitive_load || entry?.cognitiveLoad || 0,
        ),
        neuralNarrative: String(
          entry?.neural_narrative || entry?.neuralNarrative || "",
        ),
        keyTriggers: normalizeArray(entry?.key_triggers || entry?.keyTriggers),
        stitchContinuityScore: Number(
          entry?.stitch_continuity_score || entry?.stitchContinuityScore || 0,
        ),
      })),
      attentionCurve: {
        peakAttentionTimestamp: String(
          parsed?.attention_curve?.peak_attention_timestamp ||
            parsed?.attentionCurve?.peakAttentionTimestamp ||
            "",
        ),
        attentionDropTimestamp: String(
          parsed?.attention_curve?.attention_drop_timestamp ||
            parsed?.attentionCurve?.attentionDropTimestamp ||
            "",
        ),
        predictedDropOffPoint: String(
          parsed?.attention_curve?.predicted_drop_off_point ||
            parsed?.attentionCurve?.predictedDropOffPoint ||
            "",
        ),
        attentionRecoveryPoints: normalizeArray(
          parsed?.attention_curve?.attention_recovery_points ||
            parsed?.attentionCurve?.attentionRecoveryPoints,
        ),
        flowDescription: String(
          parsed?.attention_curve?.flow_description ||
            parsed?.attentionCurve?.flowDescription ||
            "",
        ),
      },
      emotionalArc: {
        openingEmotion: String(
          parsed?.emotional_arc?.opening_emotion ||
            parsed?.emotionalArc?.openingEmotion ||
            "",
        ),
        peakEmotion: String(
          parsed?.emotional_arc?.peak_emotion ||
            parsed?.emotionalArc?.peakEmotion ||
            "",
        ),
        closingEmotion: String(
          parsed?.emotional_arc?.closing_emotion ||
            parsed?.emotionalArc?.closingEmotion ||
            "",
        ),
        arcType: String(
          parsed?.emotional_arc?.arc_type ||
            parsed?.emotionalArc?.arcType ||
            "",
        ),
        dopamineTriggers: normalizeArray(
          parsed?.emotional_arc?.dopamine_triggers ||
            parsed?.emotionalArc?.dopamineTriggers,
        ),
        cortisolTriggers: normalizeArray(
          parsed?.emotional_arc?.cortisol_triggers ||
            parsed?.emotionalArc?.cortisolTriggers,
        ),
        oxytocinTriggers: normalizeArray(
          parsed?.emotional_arc?.oxytocin_triggers ||
            parsed?.emotionalArc?.oxytocinTriggers,
        ),
      },
      memoryEncoding: {
        score: Number(
          parsed?.memory_encoding_assessment?.score ||
            parsed?.memoryEncodingAssessment?.score ||
            0,
        ),
        brandRecallProbability: String(
          parsed?.memory_encoding_assessment?.brand_recall_probability ||
            parsed?.memoryEncodingAssessment?.brandRecallProbability ||
            "",
        ),
        strongestMemoryAnchor: String(
          parsed?.memory_encoding_assessment?.strongest_memory_anchor ||
            parsed?.memoryEncodingAssessment?.strongestMemoryAnchor ||
            "",
        ),
        weakestMemoryPoint: String(
          parsed?.memory_encoding_assessment?.weakest_memory_point ||
            parsed?.memoryEncodingAssessment?.weakestMemoryPoint ||
            "",
        ),
        peakEndRuleCompliance: String(
          parsed?.memory_encoding_assessment?.peak_end_rule_compliance ||
            parsed?.memoryEncodingAssessment?.peakEndRuleCompliance ||
            "",
        ),
        spacedRepetitionElements: normalizeArray(
          parsed?.memory_encoding_assessment?.spaced_repetition_elements ||
            parsed?.memoryEncodingAssessment?.spacedRepetitionElements,
        ),
      },
      stitchAnalysis: normalizeArray(
        parsed?.stitch_analysis || parsed?.stitchAnalysis,
      ),
      brainRegionSummary: normalizeArray(
        parsed?.brain_region_activation_summary ||
          parsed?.brainRegionActivationSummary,
      ),
      improvementRoadmap: normalizeArray(
        parsed?.improvement_roadmap || parsed?.improvementRoadmap,
      ).sort((a, b) => Number(a?.rank || 99) - Number(b?.rank || 99)),
      executiveSummary: String(
        parsed?.executive_summary || parsed?.executiveSummary || "",
      ).trim(),
    };
  }

  async suggestTagsFromContext(context) {
    const prompt = `You are helping create reusable generation tags for an ad creative system.
Based on the analysis JSON below, suggest 3-6 concise tags.
Rules:
- Tag names should be 1-2 words, Title Case.
- Each tag includes a short description used as a prompt injection. Keep it general enough to reuse across creatives, but still aligned with the image's mood and intent.
- Avoid duplicates or synonyms.
- Return ONLY JSON in this shape:
{ "tags": [ { "name": "", "description": "" } ] }

Analysis JSON:
${JSON.stringify(context)}${this.buildAiGuidance()}`;

    const rawText = await this.callClaude({
      model: CLAUDE_DEFAULT_MODEL,
      max_tokens: 600,
      temperature: 0.4,
      messages: [{ role: "user", content: [{ type: "text", text: prompt }] }],
    });
    const parsed = this.extractJSONFromCandidates(
      String(rawText || "").trim() ? [String(rawText).trim()] : [],
    );
    const list = Array.isArray(parsed)
      ? parsed
      : parsed.tags || parsed.items || parsed.data;

    if (!Array.isArray(list)) {
      throw new Error("Tag suggestions missing");
    }

    return list
      .map((tag) => ({
        name: tag.name || tag.tag || "",
        description: tag.description || tag.details || "",
      }))
      .filter((tag) => tag.name);
  }

  async reviseSpecialInstructions({ mode, instructions, note }) {
    const cleanMode = String(mode || "")
      .trim()
      .toLowerCase();
    const base = String(instructions || "").trim();
    if (!base) return "";
    const guidance = String(note || "").trim();
    const modeLine =
      cleanMode === "expand"
        ? "Expand the instructions with more concrete detail, without changing intent."
        : "Rephrase the instructions to be clearer and more actionable, without changing intent.";
    const noteLine = guidance ? `User note: ${guidance}` : "";
    const prompt = `You are revising special instructions for creative generation.
${modeLine}
- Preserve meaning, do not add new requirements unless implied.
- Return only the revised instructions, no quotes or labels.
${noteLine}

Original instructions:
${base}`;

    const data = await this.callGeminiGenerate(GEMINI_VISION_MODEL, {
      contents: [{ parts: [{ text: prompt }] }],
      generationConfig: {
        temperature: 0.3,
        maxOutputTokens: 300,
      },
    });
    const text = data?.candidates?.[0]?.content?.parts
      ?.map((part) => part.text)
      .filter(Boolean)
      .join("\n");
    return String(text || "").trim();
  }

  buildImagePrompt(context, knowledge) {
    const visual = context?.visual_elements || {};
    const messaging = context?.messaging || {};
    const audience = context?.target_audience || {};
    const environment = context?.environment || {};
    const strictKnowledgeLock =
      Boolean(knowledge?.strictKnowledgeLock) ||
      knowledge?.sourceMode === "knowledge";
    const generationSettings = knowledge?.generationSettings || {};
    const productName = strictKnowledgeLock
      ? knowledge?.name ||
        context?.product_focus ||
        context?.product?.name ||
        ""
      : context?.product_focus ||
        context?.product?.name ||
        knowledge?.name ||
        "";
    const productNotes = [
      strictKnowledgeLock
        ? knowledge?.notes || ""
        : context?.product_notes || context?.product?.notes || "",
      strictKnowledgeLock
        ? context?.product_notes || context?.product?.notes || ""
        : knowledge?.notes || "",
    ]
      .map((value) => String(value || "").trim())
      .filter(Boolean)
      .join(" | ");
    const aspectRatio =
      knowledge?.aspectRatio || this.getMasterAspectRatio() || "1:1";
    const brandName = knowledge?.brand?.name || "";
    const brandMessage = knowledge?.brand?.message || "";
    const brandColors = knowledge?.brand?.colors || [];
    const brandFonts = knowledge?.brand?.fonts || [];
    const imageNotes = (knowledge?.images || [])
      .map((img) => img.name)
      .filter(Boolean)
      .join("; ");
    const brandAssetNotes = (knowledge?.brand?.assets || [])
      .map((asset) => asset.name)
      .filter(Boolean)
      .join("; ");
    const knowledgeSummary = Array.isArray(knowledge?.knowledgeFolders)
      ? this.buildCreativeKnowledgeSummary(knowledge.knowledgeFolders)
      : "";
    const tagLines = (knowledge?.tags || [])
      .map((tag) =>
        tag.description ? `${tag.name}: ${tag.description}` : tag.name,
      )
      .join("; ");
    const specialInstructions = knowledge?.specialInstructions || "";
    const focusArea =
      knowledge?.focusArea || generationSettings?.focusArea || "";
    const creativityValue = Number(knowledge?.creativity);
    const creativity =
      Number.isFinite(creativityValue) && creativityValue >= 0
        ? creativityValue.toFixed(1)
        : "";
    const aiProfile = this.getAiProfile?.() || {};
    const aiBrief = [aiProfile.instructions, aiProfile.mustDo, aiProfile.avoid]
      .map((val) => String(val || "").trim())
      .filter(Boolean)
      .join(" | ");

    const visuals = Array.isArray(visual.key_visuals)
      ? visual.key_visuals.join(", ")
      : visual.key_visuals || "";
    const list = (items, limit = 6, maxLen = 50) =>
      (items || [])
        .map((item) => this.truncateText(item, maxLen))
        .filter(Boolean)
        .slice(0, limit)
        .join(", ");
    const colorPalette = list(visual.color_palette, 6, 24);
    const brandColorLine = list(brandColors, 6, 24);
    const promptChannel =
      context?.promptChannel ||
      context?.prompt_channel ||
      generationSettings?.promptChannel ||
      knowledge?.promptChannel ||
      "";
    const sceneMode = this.normalizeSceneMode(
      generationSettings?.sceneMode ||
        knowledge?.sceneMode ||
        context?.sceneMode ||
        context?.scene_mode ||
        "mixed",
    );
    const audienceSegment = this.truncateText(
      audience?.audience_segment || audience?.persona || "general audience",
      100,
    );
    const backdrop = this.truncateText(environment?.backdrop || "none", 100);
    const props = list(environment?.props, 6, 40) || "none";
    const productReferenceImages = this.collectImageReferenceImages(knowledge, {
      maxProductImages: 6,
      maxAnalysisImages: 0,
      maxBrandAssets: 0,
    });
    const analysisReferenceImages = this.dedupeReferenceImages(
      knowledge?.analysisReferenceImages || [],
    ).slice(0, 2);
    const productReferenceCount = productReferenceImages.length;
    const productReferenceNotes = this.describeReferenceImages(
      productReferenceImages,
    );
    const analysisReferenceCount = analysisReferenceImages.length;
    const analysisReferenceNotes = this.describeReferenceImages(
      analysisReferenceImages,
    );
    const bodyCareRiskText = [
      productName,
      productNotes,
      promptChannel,
      environment?.setting,
      backdrop,
      props,
      visuals,
      specialInstructions,
    ]
      .join(" ")
      .toLowerCase();
    const needsAdSafeBodyCareFraming =
      /(bath|bathroom|bathtub|body wash|body buffer|body care|grooming|loofah|scrub|scrubber|shower|soap|steam)/.test(
        bodyCareRiskText,
      );
    const multiProductBlock = this.buildMultiProductFidelityBlock(
      knowledge?.knowledgeFolders,
    );
    const isProductMode = sceneMode === "product";
    const productIdentityBlock = productReferenceCount
      ? `Product: ${productReferenceCount} real reference photo(s) of the product are attached${productReferenceNotes ? ` (${this.truncateText(productReferenceNotes, 160)})` : ""}. Reproduce that exact product from the attached photos — do not describe, restyle, reinvent, or substitute it. The photos are the only source of truth for how the product looks.`
      : `Product: keep the named product's identity consistent throughout the image; do not substitute a generic product.`;
    // The analyzed source ad is never attached to image generation — its
    // learnings travel as text (concept + this style cue). Keeps source-ad
    // pixels and text from ghosting into the new creative.
    const analysisReferenceBlock = analysisReferenceCount
      ? `Source-ad style cue (${this.truncateText(analysisReferenceNotes || "analyzed creative", 120)}): keep the layout logic, shot type, scene structure, and overall ad language close to the analyzed source described in this brief. Product identity must follow the attached product photos.`
      : "";
    const styleReferenceImages = this.dedupeReferenceImages(
      knowledge?.styleReferenceImages || [],
    ).slice(0, 2);
    const styleReferenceBlock = styleReferenceImages.length
      ? `Style reference: ${styleReferenceImages.length} user-pasted reference image(s) are attached. Match their illustration style, layout energy, and pattern-interrupt feel closely. Do NOT copy their product, logos, or exact text — product truth still comes from the attached product photos.`
      : "";
    const knowledgeLockBlock = strictKnowledgeLock
      ? `Knowledge-base source of truth:
- Product identity, product image details, tags, brand info, settings, and directions must come from the selected knowledge folders and workspace settings.
- If the creative context suggests details not supported by the knowledge base, ignore those invented details and keep the knowledge-base version.
${knowledgeSummary ? `${knowledgeSummary}` : "- Selected knowledge folders: not provided."}
- Selected tags: ${this.truncateText(tagLines || "none", 180)}.
- Focus area: ${this.truncateText(focusArea || "none", 100)}.
- Prompt channel: ${this.truncateText(promptChannel || "none", 120)}.
- Scene mode: ${sceneMode}.`
      : "";
    const textFreeRequested = this.hasExplicitNoTextRequest(
      specialInstructions,
      promptChannel,
    );
    const headlineText = this.truncateText(
      this.cleanAdCopyText(
        visual.text_overlays || messaging.headline || visual.primary_hook || "",
      ),
      90,
    );
    const subheadlineText = this.truncateText(
      this.cleanAdCopyText(
        messaging.value_proposition || messaging.subheadline || "",
      ),
      120,
    );
    const ctaText = this.trimOverlayCopy(
      this.cleanAdCopyText(messaging.cta || ""),
      { maxWords: 4, maxChars: 24 },
    );
    const shouldShowCta = Boolean(ctaText);
    const fontComboGuidance = brandFonts.length
      ? `Use the brand font system where possible: ${brandFonts
          .slice(0, 2)
          .join(
            ", ",
          )}. Keep it to two font styles max with a dominant attention-grabbing headline treatment and a cleaner support style.`
      : "Use a two-font max system: a bold high-attention headline treatment paired with a clean highly legible support style.";
    const colorAttentionGuidance =
      "Use color contrast to direct attention fast. Text and accent colors must separate clearly from the background. If brand colors are low-contrast, anchor the layout with neutrals and use one strong brand accent for emphasis.";
    const placementGuidance =
      "Place text in a clear reading zone that does not fight the product: top-left, top-center, side column, or another obvious safe-area block. The hierarchy should be understood in one glance on mobile.";
    // The image model renders the ad copy itself. Quote the literal words and
    // forbid field labels so "HEADLINE:" never ends up typeset on the ad.
    const adCopyBlock = textFreeRequested
      ? "No text on this image — keep it completely text-free."
      : [
          "ON-IMAGE COPY — typeset exactly these words on the image, correctly spelled, and no other words:",
          headlineText
            ? `1. "${headlineText}" — the dominant headline: large, bold, high-contrast.`
            : "1. Write the headline yourself from the concept — max 8 words, punchy, complete.",
          subheadlineText
            ? `2. "${subheadlineText}" — the smaller support line.`
            : "",
          shouldShowCta ? `3. "${ctaText}" — a small CTA button/pill.` : "",
          `The quoted strings are the complete copy. Do NOT typeset the numbering, quotation marks, or field names — never render words like "Headline", "Top headline", "Support", or "CTA" unless they appear inside the quotes. No placeholder text, no gibberish micro-text, no watermarks.`,
          `- ${fontComboGuidance}`,
          `- ${colorAttentionGuidance}`,
          `- ${placementGuidance}`,
        ]
          .filter(Boolean)
          .join("\n");
    const bodyCareSafetyBlock = needsAdSafeBodyCareFraming
      ? `Compliance / framing:
- Keep all imagery non-sexual and ad-safe.
- Avoid nudity, explicit anatomy, erotic gaze, or fetishized body focus.
- Do NOT default to a shower, bathroom, or steam scene. Body-care products can be shown in many settings: studio, outdoor, lifestyle, graphic layouts, flat-lays, product-only hero shots, textured surfaces, etc.
- Only use a bathroom/shower setting if the creative direction or special instructions explicitly request it.`
      : "";

    const creativityDirective = creativity
      ? Number(creativity) >= 0.7
        ? `\nCreativity level: HIGH (${creativity}). Push for bold, unexpected compositions — but keep it real and photo-based. Unusual angles, strong graphic text layouts, striking surface/lighting choices. No CGI, no animation style, no surreal renders.`
        : Number(creativity) >= 0.4
          ? `\nCreativity level: MEDIUM (${creativity}). Balance proven ad layouts with fresh surface/lighting choices. Keep it grounded and real.`
          : `\nCreativity level: LOW (${creativity}). Stay close to conventional, proven ad formats.`
      : "";

    if (sceneMode === "product") {
      const productParts = [
        `Product page carousel slide. Mix of clean product photography and graphic/text slides — like a full Shopify PDP carousel. No people, no faces, no models.`,
        `Aspect ratio: ${aspectRatio}. Fill edge-to-edge. No borders.`,
        `Product: ${this.truncateText(productName || "unspecified", 80)}.`,
        productNotes
          ? `Product notes: ${this.truncateText(productNotes, 160)}.`
          : "",
        multiProductBlock || (productReferenceCount ? productIdentityBlock : ""),
        knowledgeSummary
          ? `Knowledge context (for product accuracy only): ${this.truncateText(knowledgeSummary, 300)}.`
          : "",
        specialInstructions
          ? `Instructions: ${this.truncateText(specialInstructions, 200)}.`
          : "",
        aiBrief ? `Global notes: ${this.truncateText(aiBrief, 200)}.` : "",
        this.buildSceneModeRuleBlock("product", "image"),
        `Photo shots: no text overlays needed. Graphic slides: bold readable text on image is expected. No brand logos unless the slide calls for it.`,
      ]
        .filter(Boolean)
        .join("\n");
      return productParts;
    }

    if (sceneMode === "review") {
      const reviewParts = [
        `Authentic customer review photo. Real iPhone shot of the product sitting on a surface — counter, sink, shower rack. No faces, no selfies. Product is the subject.`,
        `Aspect ratio: ${aspectRatio}. Fill edge-to-edge.`,
        `Product: ${this.truncateText(productName || "unspecified", 80)}.`,
        productNotes
          ? `Product notes: ${this.truncateText(productNotes, 160)}.`
          : "",
        multiProductBlock || "",
        knowledgeSummary
          ? `Knowledge context (for product accuracy only): ${this.truncateText(knowledgeSummary, 300)}.`
          : "",
        specialInstructions
          ? `Instructions: ${this.truncateText(specialInstructions, 200)}.`
          : "",
        aiBrief ? `Global notes: ${this.truncateText(aiBrief, 200)}.` : "",
        this.buildSceneModeRuleBlock("review", "image"),
        `No text overlays, no ad copy, no logos, no CTAs. Just a real-looking review photo.`,
      ]
        .filter(Boolean)
        .join("\n");
      return reviewParts;
    }

    if (sceneMode === "custom") {
      const customParts = [
        `Custom scene image. The scene description below is the primary creative brief — follow it exactly. Produce one real photo-based image (not CGI, not animated, not surreal).`,
        `Aspect ratio: ${aspectRatio}. Fill edge-to-edge. No borders.`,
        specialInstructions
          ? `SCENE DESCRIPTION (primary brief — highest priority, overrides default constraints):\n${this.truncateText(specialInstructions, 400)}`
          : "",
        multiProductBlock ||
          `Product: ${this.truncateText(productName || "unspecified", 80)}. The product must appear in the scene. Match it exactly from reference images — same shape, colors, materials, textures, logo. Do not redesign or substitute it.`,
        !multiProductBlock && productNotes ? `Product notes: ${this.truncateText(productNotes, 160)}.` : "",
        !multiProductBlock ? productIdentityBlock : "",
        analysisReferenceBlock || "",
        styleReferenceBlock || "",
        knowledgeLockBlock || "",
        `Brand: ${this.truncateText(brandName || "unspecified", 80)}. Colors: ${brandColorLine || "none"}. Gold (#D4A85A) only on product hardware and 1-2 accent words.`,
        brandMessage ? `Brand message: ${this.truncateText(brandMessage, 140)}.` : "",
        brandAssetNotes ? `Brand assets: ${this.truncateText(brandAssetNotes, 120)}.` : "",
        adCopyBlock,
        knowledgeSummary ? `Knowledge context: ${this.truncateText(knowledgeSummary, 250)}.` : "",
        tagLines ? `Tags: ${this.truncateText(tagLines, 160)}.` : "",
        aiBrief ? `Global notes: ${this.truncateText(aiBrief, 200)}.` : "",
        creativityDirective,
      ]
        .filter(Boolean)
        .join("\n");
      return customParts;
    }

    // Default ad mode: a complete brief for one fresh chat. The concept (born
    // from the neuro analysis) plus settings, product photos, tags, and any
    // custom prompt — the source ad itself is never part of image generation.
    const conceptRationale = this.compactText(
      context?.concept_rationale || context?.rationale || "",
      500,
    );
    const compositionLine = this.compactText(visual.composition || "", 220);
    const settingLine = this.compactText(
      environment?.setting || (backdrop !== "none" ? backdrop : ""),
      140,
    );
    return [
      "Create one finished, scroll-stopping paid-social ad image. Real product photos are attached in this chat — study them first, then design the ad around them. This brief is the only context; nothing outside this chat applies.",
      conceptRationale
        ? `CONCEPT (built from the neuro analysis of a proven ad):\n${conceptRationale}`
        : "",
      promptChannel
        ? `Execution angle for this variant: ${this.truncateText(promptChannel, 220)}`
        : "",
      adCopyBlock,
      "ART DIRECTION",
      compositionLine ? `- Composition: ${compositionLine}` : "",
      visuals ? `- Key visuals: ${this.truncateText(visuals, 200)}` : "",
      settingLine ? `- Setting: ${settingLine}` : "",
      props !== "none" ? `- Props: ${props}` : "",
      colorPalette
        ? `- Palette: ${colorPalette}${brandColorLine ? ` (brand colors: ${brandColorLine})` : ""}`
        : brandColorLine
          ? `- Brand colors: ${brandColorLine}`
          : "",
      `- Audience: ${audienceSegment}.`,
      multiProductBlock || productIdentityBlock,
      styleReferenceBlock,
      brandName
        ? `- Brand: ${this.truncateText(brandName, 80)}.${brandMessage ? ` Message: ${this.truncateText(brandMessage, 140)}.` : ""}`
        : "",
      tagLines ? `- Tags: ${this.truncateText(tagLines, 180)}.` : "",
      knowledgeSummary
        ? `- Product knowledge: ${this.truncateText(knowledgeSummary, 250)}.`
        : "",
      aiBrief ? `- Global notes: ${this.truncateText(aiBrief, 200)}.` : "",
      specialInstructions
        ? `- Custom prompt (high priority): ${this.truncateText(specialInstructions, 300)}`
        : "",
      "FORMAT",
      `- Aspect ratio ${aspectRatio}, fill edge-to-edge, no borders or letterboxing.`,
      "- Real ad photo/graphic — not CGI, not a 3D render. The product is the hero; no people unless the concept calls for them.",
      bodyCareSafetyBlock,
      creativityDirective.trim(),
    ]
      .filter(Boolean)
      .join("\n");
  }

  truncateText(text, maxLength) {
    const raw = String(text || "");
    if (!maxLength || raw.length <= maxLength) return raw;
    return `${raw.slice(0, Math.max(0, maxLength - 3))}...`;
  }

  compactText(text, maxLength = 200) {
    const normalized = String(text || "")
      .replace(/\s+/g, " ")
      .trim();
    if (!normalized) return "";
    return this.truncateText(normalized, maxLength);
  }

  compactContextForPrompt(value, options = {}, depth = 0) {
    const {
      maxDepth = 4,
      maxArray = 8,
      maxKeys = 24,
      maxString = 260,
    } = options || {};
    if (value == null) return value;
    if (typeof value === "string") return this.compactText(value, maxString);
    if (typeof value === "number" || typeof value === "boolean") return value;
    if (Array.isArray(value)) {
      return value
        .slice(0, maxArray)
        .map((item) => this.compactContextForPrompt(item, options, depth + 1));
    }
    if (typeof value === "object") {
      if (depth >= maxDepth) return value;
      const entries = Object.entries(value).slice(0, maxKeys);
      return entries.reduce((acc, [key, val]) => {
        acc[key] = this.compactContextForPrompt(val, options, depth + 1);
        return acc;
      }, {});
    }
    return value;
  }

  compactScriptContext(
    script,
    { maxWordsPerLine = 8, maxTotalWords = 24 } = {},
  ) {
    const truncateWords = (text, limit) => {
      const words = String(text || "")
        .replace(/\s+/g, " ")
        .trim()
        .split(" ")
        .filter(Boolean);
      if (words.length <= limit) return words.join(" ");
      return words.slice(0, limit).join(" ");
    };

    if (!script || typeof script !== "object") {
      if (!script) return "";
      return truncateWords(script, maxTotalWords);
    }

    const compact = { ...script };
    const textKeys = [
      "dialogue",
      "spoken",
      "voiceover",
      "monologue",
      "internal_monologue",
      "narration",
      "line",
    ];
    textKeys.forEach((key) => {
      if (typeof compact[key] === "string") {
        compact[key] = truncateWords(compact[key], maxTotalWords);
      }
    });

    if (typeof compact.on_screen_text === "string") {
      compact.on_screen_text = truncateWords(
        compact.on_screen_text,
        Math.min(6, maxWordsPerLine),
      );
    }

    if (compact.timing_markers && typeof compact.timing_markers === "object") {
      const entries = Object.entries(compact.timing_markers);
      const trimmed = entries.slice(0, 4).reduce((acc, [time, text]) => {
        acc[time] = truncateWords(text, maxWordsPerLine);
        return acc;
      }, {});
      compact.timing_markers = trimmed;
    }

    return compact;
  }

  collectVideoReferenceImages(knowledge = {}) {
    const list = [
      ...(knowledge?.actorReferenceImages || []),
      ...(knowledge?.referenceImages || []),
      ...(knowledge?.images || []),
    ];
    return this.dedupeReferenceImages(list);
  }

  dedupeReferenceImages(images = []) {
    const seen = new Set();
    return (Array.isArray(images) ? images : []).filter((item, index) => {
      const key =
        item?.path ||
        item?.url ||
        item?.dataUrl ||
        item?.id ||
        item?.name ||
        (item?.blob ? `blob-${index}` : "");
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  collectImageReferenceImages(knowledge = {}, options = {}) {
    const maxProductImages = Number.isFinite(options.maxProductImages)
      ? Math.max(0, options.maxProductImages)
      : 4;
    const maxAnalysisImages = Number.isFinite(options.maxAnalysisImages)
      ? Math.max(0, options.maxAnalysisImages)
      : 1;
    const maxBrandAssets = Number.isFinite(options.maxBrandAssets)
      ? Math.max(0, options.maxBrandAssets)
      : 1;
    // User-pasted style references (from the special-instructions field).
    // Opt-in per call site so product-fidelity paths stay style-free.
    const maxStyleImages = Number.isFinite(options.maxStyleImages)
      ? Math.max(0, options.maxStyleImages)
      : 0;
    const styleImages = this.dedupeReferenceImages(
      (knowledge?.styleReferenceImages || []).map((img) => ({
        ...img,
        __source: img?.__source || "style",
      })),
    ).slice(0, maxStyleImages);
    const productImages = this.dedupeReferenceImages([
      ...(knowledge?.images || []).map((img) => ({
        ...img,
        __source: img?.__source || "knowledge",
      })),
      ...(knowledge?.referenceImages || []).map((img) => ({
        ...img,
        __source: img?.__source || "reference",
      })),
    ]).slice(0, maxProductImages);
    const analysisImages = this.dedupeReferenceImages(
      (knowledge?.analysisReferenceImages || []).map((img) => ({
        ...img,
        __source: img?.__source || "analysis",
      })),
    ).slice(0, maxAnalysisImages);
    const brandAssets = this.dedupeReferenceImages(
      (knowledge?.brand?.assets || []).map((img) => ({
        ...img,
        __source: img?.__source || "brand",
      })),
    ).slice(0, maxBrandAssets);
    // Style refs go first: they're explicit user intent for this run and must
    // survive downstream caps (e.g. buildOpenAIReferenceImages slices to 4).
    return this.dedupeReferenceImages([
      ...styleImages,
      ...productImages,
      ...analysisImages,
      ...brandAssets,
    ]);
  }

  shouldUseReferenceAwareImageRenderer(
    imageModel,
    /* knowledge */ _knowledge = {},
    /* aspectRatio */ _aspectRatio,
  ) {
    // Previously routed GPT requests to Gemini whenever there were reference
    // images or a non-native aspect ratio. That made sense for gpt-image-1,
    // but gpt-image-2-2026-04-21 handles reference-instructed prompts directly
    // and we post-crop non-native aspect ratios via enforceAspectRatioOnDataUrl.
    // Always prefer the user's selected model.
    if (String(imageModel || "").startsWith("gpt")) return false;
    return false;
  }

  describeReferenceImages(referenceImages = []) {
    return referenceImages
      .map(
        (image, index) =>
          image?.name || image?.label || `reference ${index + 1}`,
      )
      .filter(Boolean)
      .slice(0, 6)
      .join("; ");
  }

  buildMultiProductFidelityBlock(knowledgeFolders = []) {
    const folders = (Array.isArray(knowledgeFolders) ? knowledgeFolders : [])
      .filter((f) => f?.name)
      .slice(0, 6);
    if (folders.length <= 1) return null;
    let imageIndex = 1;
    const productLines = folders.map((folder, i) => {
      const name = String(folder.name || "").trim();
      const notes = this.compactText(String(folder.notes || "").trim(), 100);
      const folderImageCount = Math.min((folder.images || []).length, 2);
      const rangeStr =
        folderImageCount === 0
          ? "no reference image"
          : folderImageCount === 1
            ? `attached image ${imageIndex}`
            : `attached images ${imageIndex}–${imageIndex + folderImageCount - 1}`;
      imageIndex += folderImageCount;
      return `• Product ${i + 1}: ${name} (${rangeStr})${notes ? ` — ${notes}` : ""}`;
    });
    return `MULTIPLE DISTINCT PRODUCTS — ${folders.length} separate physical objects must appear:
${productLines.join("\n")}

Reference images are attached in the order listed. Each product has its own unique shape, size, color, and proportions. Do NOT blend, merge, or swap features between products. Render each one exactly as its own reference images show — wrong proportions or mixed-up designs are a critical failure.`;
  }

  getSupportedVideoDurations(modelName, resolution) {
    const { provider, modelId } = this.parseVideoModel(modelName);
    if (provider === "sora") {
      return [4, 8, 12];
    }
    const isVeo3 = String(modelId || "")
      .toLowerCase()
      .includes("veo-3");
    if (isVeo3) {
      return [4, 6, 8, 12];
    }
    if (String(resolution || "720p").toLowerCase() !== "720p") {
      return [8];
    }
    return [4, 6, 8];
  }

  snapVideoSceneDuration(rawDuration, modelName, resolution) {
    const allowedDurations = this.getSupportedVideoDurations(
      modelName,
      resolution,
    );
    let duration = Number(rawDuration);
    if (!Number.isFinite(duration)) {
      return allowedDurations[allowedDurations.length - 1];
    }
    let nearest = allowedDurations[0];
    for (const candidate of allowedDurations) {
      if (Math.abs(candidate - duration) < Math.abs(nearest - duration)) {
        nearest = candidate;
      }
    }
    return nearest;
  }

  buildVideoPrompt(context, knowledge) {
    const environment =
      context?.environment_context || context?.environment || {};
    const action = context?.action_context || context?.action || {};
    const script = context?.script_context || context?.script || {};
    const continuityLocks =
      context?.continuity_locks || knowledge?.continuityLocks || {};
    const actorProfile =
      context?.actor_profile || knowledge?.actorProfile || {};
    const aspectRatio = knowledge?.aspectRatio || "1:1";
    const productName = knowledge?.name || "";
    const productNotes = knowledge?.notes || "";
    const productImageNotes = (knowledge?.images || [])
      .map((img) => img?.name || "")
      .filter(Boolean)
      .join("; ");
    const brandName = knowledge?.brand?.name || "";
    const brandMessage = knowledge?.brand?.message || "";
    const brandColors = knowledge?.brand?.colors || [];
    const brandAssetNotes = (knowledge?.brand?.assets || [])
      .map((asset) => asset?.name || "")
      .filter(Boolean)
      .join("; ");
    const sharedReferenceImages = this.collectVideoReferenceImages(knowledge);
    const referenceNotes = this.describeReferenceImages(sharedReferenceImages);
    const tagLines = (knowledge?.tags || [])
      .map((tag) =>
        tag.description ? `${tag.name}: ${tag.description}` : tag.name,
      )
      .join("\n");
    const specialInstructions = knowledge?.specialInstructions || "";
    const aiGuidance = this.buildAiGuidance();
    const copyGuidance = this.buildAdCopyGuidance();
    const durationSeconds = Number(knowledge?.videoConfig?.durationSeconds);
    const durationLabel = Number.isFinite(durationSeconds)
      ? `${durationSeconds}s`
      : "6-8s";
    const maxTotalWords = Number.isFinite(durationSeconds)
      ? Math.max(12, Math.round(durationSeconds * 3))
      : 24;
    const maxWordsPerLine = Math.min(
      10,
      Math.max(6, Math.round(maxTotalWords / 2)),
    );

    const compactScript = this.compactScriptContext(script, {
      maxWordsPerLine,
      maxTotalWords,
    });
    const promptChannel =
      context?.renderChannel ||
      context?.promptChannel ||
      knowledge?.promptChannel ||
      "";
    const stitchIntent =
      context?.stitch_intent || context?.stitchIntent || "none";
    const realismNotes =
      context?.realism_notes ||
      context?.realismNotes ||
      knowledge?.videoStyle ||
      "Photoreal live-action realism with natural skin texture, believable lighting, and real-camera movement.";

    return `Create a short-form advertising video that matches the provided creative context.
Aspect ratio: ${aspectRatio}.
Clip duration target: ${durationLabel}.
Product knowledge base (must follow, do not invent or substitute products):
Product reference: ${productName || "unspecified product"}.
Product notes: ${productNotes || "none"}.
Product image refs: ${productImageNotes || "none"}.
Brand name: ${brandName || "unspecified"}.
Brand message: ${brandMessage || "none"}.
Brand colors: ${brandColors.length ? brandColors.join(", ") : "none"}.
Brand asset refs: ${brandAssetNotes || "none"}.
Shared visual references: ${referenceNotes || "none"}.
If details are missing, stay generic but keep the same product identity and name.
Tag directives:
${tagLines || "None"}
Special instructions: ${specialInstructions || "none"}.
- Treat special instructions as high priority. If they conflict with weaker defaults, follow the special instructions unless they break product identity or continuity locks.

Script constraints:
- Use the copy library whenever possible for phrasing/claims/CTAs.
- Keep spoken lines short so they fully finish within the clip.
- Max ${maxWordsPerLine} words per line, max ~${maxTotalWords} words total.
- Prefer 1-2 short lines with pauses. If script_context is longer, compress it.
- Keep on-screen text to short, punchy phrases (<= 6 words).
- If any product is referenced, use the exact product name: "${productName || "the product"}".
- Do not invent or swap to other products.

Editing constraints:
- Avoid excessive cuts. Prefer 1-2 shots, smooth continuous movement.
- No jump cuts. Use gentle camera motion and seamless transitions.
- Keep the result photoreal and physically plausible.
- No stylized CGI, synthetic skin, surreal warping, or animated look.

Anatomy constraints (strict):
- Single actor only. No extra limbs, no duplicated arms/hands, no distorted fingers.
- Hands must be anatomically correct and consistent across frames.

Environment context:
${JSON.stringify(environment)}

Action context:
${JSON.stringify(action)}

Script context:
${JSON.stringify(script)}

Shortened script target (follow this if the original script is too long):
${JSON.stringify(compactScript)}

Actor profile (locked):
${JSON.stringify(actorProfile)}

Continuity locks (locked):
${JSON.stringify(continuityLocks)}

Prompt channel:
${promptChannel || "none"}

Stitch intent:
${stitchIntent}

Realism notes:
${realismNotes}

Deliver a coherent video that follows the script beats, matches the environment lighting, mirrors the described creator/camera actions, and uses the shared references to keep actor/product identity stable.${copyGuidance}${aiGuidance}`;
  }

  parseVideoModel(modelName) {
    const raw = String(modelName || "").trim();
    if (!raw) return { provider: "seedance", modelId: DEFAULT_VIDEO_MODEL };
    const normalized = raw.toLowerCase();
    if (normalized.startsWith("bytedance") || normalized.includes("seedance")) {
      return { provider: "seedance", modelId: raw };
    }
    if (normalized.startsWith("sora")) {
      return { provider: "sora", modelId: raw };
    }
    if (normalized.startsWith("veo")) {
      return { provider: "veo", modelId: raw };
    }
    if (normalized.startsWith("kling")) {
      return { provider: "kling", modelId: raw };
    }
    return { provider: "seedance", modelId: DEFAULT_VIDEO_MODEL };
  }

  async generateImageFromPrompt({ prompt, aspectRatio = "1:1", referenceImages = [], signal } = {}) {
    const safePrompt = this.truncateText(String(prompt || "").trim(), 2800);
    if (!safePrompt) throw new Error("Prompt is required.");

    const hasReferences = Array.isArray(referenceImages) && referenceImages.length > 0;

    // If reference images are provided, route to Gemini — it natively accepts
    // image inputs and conditions the generation on them. OpenAI gpt-image-2
    // ignores standalone reference URLs through the chat API.
    if (hasReferences) {
      const geminiResult = await this.generateImageFromContextWithGemini(
        safePrompt,
        {
          aspectRatio,
          images: referenceImages.map((url) => ({ url, name: "" })),
          referenceImages: referenceImages.map((url) => ({ url, name: "" })),
        },
      );
      const imageUrl = geminiResult?.imageUrl;
      if (!imageUrl) throw new Error("Image generation with references failed.");
      return imageUrl;
    }

    // No references — try OpenAI first (faster + sharper for text-only prompts).
    try {
      const size = this.getOpenAISizeForAspect(aspectRatio);
      const response = await this.callOpenAIImages(
        { model: OPENAI_IMAGE_MODEL, prompt: safePrompt, size },
        signal,
        { suppressAuthEvent: true },
      );
      const base64 = response?.data?.[0]?.b64_json;
      if (base64) return `data:image/png;base64,${base64}`;
    } catch (err) {
      if (err?.name === "AbortError") throw err;
    }

    // Gemini fallback for text-only prompts.
    const geminiResult = await this.generateImageFromContextWithGemini(
      safePrompt,
      { aspectRatio },
    );
    const imageUrl = geminiResult?.imageUrl;
    if (!imageUrl) throw new Error("Image generation failed on all available providers.");
    return imageUrl;
  }

  async generateVideoFromSeedance({
    prompt,
    modelId,
    aspectRatio,
    resolution,
    durationSeconds,
    referenceImages = [],
    generateAudio = false,
    firstFrameImage = null,
    onStatusUpdate = null,
    signal,
  }) {
    if (signal?.aborted) {
      const err = new Error("Generation cancelled");
      err.name = "AbortError";
      throw err;
    }
    const apiKey = this.atlascloudApiKey;
    if (!apiKey) {
      throw new Error(
        "AtlasCloud API key not configured. Add your key in Settings → API Keys → AtlasCloud.",
      );
    }
    const ATLAS_BASE = "https://api.atlascloud.ai/api/v1/model";
    const CREATE_URL = `${ATLAS_BASE}/generateVideo`;
    const pollUrl = (id) => `${ATLAS_BASE}/prediction/${id}`;

    const safePrompt = this.truncateText(prompt, 1000);
    const safeDuration =
      durationSeconds === -1
        ? -1
        : Math.min(15, Math.max(4, Number(durationSeconds) || 5));
    const safeResolution = ["480p", "720p", "1080p-SR", "1440p-SR"].includes(resolution)
      ? resolution
      : "720p";
    const safeRatio = ["16:9", "4:3", "1:1", "3:4", "9:16", "21:9", "adaptive"].includes(aspectRatio)
      ? aspectRatio
      : "adaptive";

    // Only pass AtlasCloud Asset Library references (asset://) as reference_images.
    const assetRefs = (referenceImages || []).filter((u) => u.startsWith("asset://"));

    // If a first-frame image is supplied, use image-to-video model.
    const isImageToVideo = Boolean(firstFrameImage);
    const resolvedModel = isImageToVideo
      ? "bytedance/seedance-2.0/image-to-video"
      : (modelId || "bytedance/seedance-2.0-fast/text-to-video");

    const body = {
      model: resolvedModel,
      prompt: safePrompt,
      duration: safeDuration,
      resolution: safeResolution,
      ratio: safeRatio,
      generate_audio: generateAudio,
      watermark: false,
    };
    if (isImageToVideo) {
      body.image = firstFrameImage;
    }
    if (assetRefs.length > 0) {
      body.reference_images = assetRefs;
    }

    const createRes = await fetch(CREATE_URL, {
      method: "POST",
      headers: { Authorization: `Bearer ${apiKey}`, "Content-Type": "application/json" },
      body: JSON.stringify(body),
      signal,
    });
    if (!createRes.ok) {
      const text = await createRes.text().catch(() => "");
      let detail = text || createRes.statusText;
      // Try to extract the deepest message from nested JSON error strings
      try {
        const parsed = JSON.parse(text);
        const inner = parsed?.message || "";
        const bodyMatch = inner.match(/body:\s*(\{.*)/);
        if (bodyMatch) {
          const innerParsed = JSON.parse(bodyMatch[1]);
          detail = innerParsed?.message || inner || detail;
        } else {
          detail = inner || detail;
        }
      } catch {}
      throw new Error(`Seedance error (${createRes.status}): ${detail}`);
    }
    const createJson = await createRes.json();
    const predictionId = createJson?.data?.id || createJson?.id;
    if (!predictionId) {
      throw new Error(
        `AtlasCloud returned no prediction ID. Response: ${JSON.stringify(createJson).slice(0, 200)}`,
      );
    }

    // Abortable sleep — responds immediately when the signal fires.
    const abortableSleep = (ms) =>
      new Promise((resolve, reject) => {
        if (signal?.aborted) { reject(Object.assign(new Error("Aborted"), { name: "AbortError" })); return; }
        const timer = setTimeout(resolve, ms);
        signal?.addEventListener("abort", () => {
          clearTimeout(timer);
          reject(Object.assign(new Error("Generation cancelled"), { name: "AbortError" }));
        }, { once: true });
      });

    onStatusUpdate?.("queued", 0);
    const startMs = Date.now();

    const maxAttempts = 72; // 72 × 5 s = 6 minutes max
    let consecutiveErrors = 0;
    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
      await abortableSleep(attempt === 0 ? 3000 : 5000);

      const elapsedSec = Math.round((Date.now() - startMs) / 1000);

      let pollRes;
      try {
        pollRes = await fetch(pollUrl(predictionId), {
          headers: { Authorization: `Bearer ${apiKey}` },
          signal,
        });
      } catch (fetchErr) {
        if (fetchErr?.name === "AbortError") throw fetchErr;
        consecutiveErrors += 1;
        if (consecutiveErrors >= 5) throw new Error("AtlasCloud poll failed repeatedly — network error.");
        continue;
      }

      if (pollRes.status >= 500) {
        consecutiveErrors += 1;
        onStatusUpdate?.("server_error", elapsedSec);
        if (consecutiveErrors >= 3) {
          const errText = await pollRes.text().catch(() => "");
          throw new Error(
            `AtlasCloud job failed on their side (${pollRes.status}). ` +
            `Try: shorter duration (4–5 s), disable audio, or a shorter prompt. ${errText.slice(0, 120)}`,
          );
        }
        continue;
      }
      consecutiveErrors = 0;

      if (!pollRes.ok) continue;
      const pollJson = await pollRes.json().catch(() => null);
      if (!pollJson) continue;

      const rawStatus = String(pollJson?.data?.status || pollJson?.status || "");
      const status = rawStatus.toLowerCase();
      onStatusUpdate?.(rawStatus || "processing", elapsedSec);

      if (status === "failed" || status === "timeout") {
        const rawDetail = pollJson?.data?.error || pollJson?.data?.outputs?.[0] || pollJson?.error || "";
        // Unwrap nested JSON error like {"code":500,"message":"unexpected http status code: 400, body: {\"code\":400,\"message\":\"...\"}"}
        let cleanDetail = String(rawDetail || "").trim();
        try {
          const m = cleanDetail.match(/body:\s*(\{.*\})/);
          if (m) {
            const inner = JSON.parse(m[1]);
            if (inner?.message) cleanDetail = inner.message;
          } else if (cleanDetail.startsWith("{")) {
            const parsed = JSON.parse(cleanDetail);
            cleanDetail = parsed?.message || cleanDetail;
          }
        } catch {}
        // Friendly hints based on common failure modes
        let hint = "Try a shorter duration (4–5 s) or disable audio.";
        const lower = cleanDetail.toLowerCase();
        if (lower.includes("content") || lower.includes("safety") || lower.includes("policy") || lower.includes("violat")) {
          hint = "Seedance flagged the prompt for content safety. Try removing references to people, brands, or specific scenarios (e.g. 'podcast', faces, names).";
        } else if (lower.includes("prompt") && (lower.includes("long") || lower.includes("too"))) {
          hint = "Prompt is too long. Try 1–2 short directive sentences (e.g. 'slow push-in, gentle motion').";
        } else if (lower.includes("image") && (lower.includes("size") || lower.includes("large") || lower.includes("dimension"))) {
          hint = "First-frame image was rejected. Try a smaller or differently-shaped image.";
        }
        throw new Error(`Seedance: ${cleanDetail || "generation failed"}. ${hint}`);
      }
      if (status === "completed" || status === "succeeded") {
        const outputs = pollJson?.data?.outputs || pollJson?.outputs || [];
        const videoUrl = outputs[0];
        if (!videoUrl) throw new Error("AtlasCloud returned no video URL.");
        // Return the CDN URL directly — fetching it as a blob fails due to CORS.
        // The <video> element and anchor download both work fine with a direct URL.
        return {
          videoUrl,
          remoteUrl: videoUrl,
          mimeType: "video/mp4",
        };
      }
    }
    throw new Error("AtlasCloud Seedance timed out after 6 minutes.");
  }

  async generateVideoFromKling({
    prompt,
    modelId = "kling-v3-omni",
    aspectRatio = "16:9",
    durationSeconds = 5,
    mode = "pro",
    sound = "off",
    firstFrameImage = null,
    endFrameImage = null,
    referenceVideoUrl = null,
    onStatusUpdate = null,
    signal,
  }) {
    if (signal?.aborted) {
      const err = new Error("Generation cancelled");
      err.name = "AbortError";
      throw err;
    }
    const apiKey = this.klingApiKey;
    if (!apiKey) throw new Error("Kling API key not configured. Add it in Settings → API Keys.");

    const KLING_BASE = "https://api-singapore.klingai.com";
    const CREATE_URL = `${KLING_BASE}/v1/videos/omni-video`;

    // Build image_list
    const imageList = [];
    if (firstFrameImage) imageList.push({ image_url: firstFrameImage, type: "first_frame" });
    if (endFrameImage) imageList.push({ image_url: endFrameImage, type: "end_frame" });

    // Build video_list (reference video)
    const videoList = referenceVideoUrl
      ? [{ video_url: referenceVideoUrl, refer_type: "feature", keep_original_sound: "yes" }]
      : [];

    // Duration must be a valid Kling enum (3-15s)
    const validDurations = [3,4,5,6,7,8,9,10,11,12,13,14,15];
    const duration = String(validDurations.includes(Number(durationSeconds)) ? durationSeconds : 5);

    // Aspect ratio only supported when not using first-frame mode alone
    const body = {
      model_name: modelId,
      prompt: String(prompt || "").slice(0, 2500) || "Cinematic motion",
      mode,
      duration,
      sound,
      ...(imageList.length > 0 ? { image_list: imageList } : { aspect_ratio: aspectRatio }),
      ...(videoList.length > 0 ? { video_list: videoList } : {}),
    };

    // Create task
    const createRes = await fetch(CREATE_URL, {
      method: "POST",
      headers: { Authorization: `Bearer ${apiKey}`, "Content-Type": "application/json" },
      body: JSON.stringify(body),
      signal,
    });
    if (!createRes.ok) {
      const errText = await createRes.text().catch(() => "");
      throw new Error(`Kling API error ${createRes.status}: ${errText || createRes.statusText}`);
    }
    const createJson = await createRes.json();
    if (createJson.code !== 0) {
      throw new Error(`Kling task creation failed: ${createJson.message || "Unknown error"}`);
    }
    const taskId = createJson?.data?.task_id;
    if (!taskId) throw new Error("Kling did not return a task_id.");

    // Poll for result
    const POLL_URL = `${KLING_BASE}/v1/videos/omni-video/${taskId}`;
    const MAX_ATTEMPTS = 180; // 15 min at 5s intervals
    const POLL_INTERVAL = 5000;
    const pollStart = Date.now();

    for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
      if (signal?.aborted) {
        const err = new Error("Generation cancelled");
        err.name = "AbortError";
        throw err;
      }
      await this.wait(attempt === 0 ? 4000 : POLL_INTERVAL, signal);
      const elapsedSec = Math.round((Date.now() - pollStart) / 1000);

      let pollRes;
      try {
        pollRes = await fetch(POLL_URL, {
          headers: { Authorization: `Bearer ${apiKey}` },
          signal,
        });
      } catch (fetchErr) {
        if (fetchErr?.name === "AbortError") throw fetchErr;
        continue; // network glitch — retry
      }
      if (!pollRes.ok) continue;
      const pollJson = await pollRes.json().catch(() => null);
      if (!pollJson) continue;

      const status = String(pollJson?.data?.task_status || "").toLowerCase();
      const statusMsg = pollJson?.data?.task_status_msg || "";
      onStatusUpdate?.(status || "processing", elapsedSec);

      if (status === "failed") {
        throw new Error(`Kling generation failed: ${statusMsg || "content moderation or invalid input"}`);
      }
      if (status === "succeed") {
        const videos = pollJson?.data?.task_result?.videos || [];
        const videoUrl = videos[0]?.url;
        if (!videoUrl) throw new Error("Kling returned no video URL.");
        return { videoUrl, remoteUrl: videoUrl, mimeType: "video/mp4" };
      }
    }
    throw new Error("Kling generation timed out after 15 minutes.");
  }

  async generateVideoFromSora({
    prompt,
    modelId,
    aspectRatio,
    resolution,
    durationSeconds,
    signal,
  }) {
    if (signal?.aborted) {
      const err = new Error("Generation cancelled");
      err.name = "AbortError";
      throw err;
    }
    const normalizedModel = this.normalizeSoraModel(modelId);
    const seconds = this.resolveSoraSeconds(durationSeconds);
    const size = this.resolveSoraSize(aspectRatio, resolution, normalizedModel);
    const safePrompt = this.truncateText(prompt, 1000);

    let video = await this.callOpenAIVideosCreate(
      {
        model: normalizedModel,
        prompt: safePrompt,
        seconds: String(seconds),
        size,
      },
      signal,
    );

    const maxAttempts = 60;
    const pollStart = Date.now();
    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
      if (signal?.aborted) {
        const err = new Error("Generation cancelled");
        err.name = "AbortError";
        throw err;
      }
      if (!video?.id) break;
      if (
        video.status &&
        video.status !== "queued" &&
        video.status !== "in_progress"
      ) {
        break;
      }
      await new Promise((resolve) => setTimeout(resolve, 2000));
      video = await this.callOpenAIVideosStatus({ id: video.id }, signal);
      if (video?.status === "failed") {
        const errorDetail =
          video?.error?.message ||
          video?.error?.code ||
          (typeof video?.error === "string" ? video.error : "");
        const statusMessage = errorDetail
          ? `Sora generation failed: ${errorDetail}`
          : "Sora generation failed";
        throw new Error(statusMessage);
      }
    }
    if (video?.status && video.status !== "completed") {
      const elapsedSec = Math.round((Date.now() - pollStart) / 1000);
      const progress = Number(video?.progress);
      const progressText = Number.isFinite(progress) ? ` (${progress}%)` : "";
      throw new Error(
        `Sora generation still ${video.status}${progressText} after ${elapsedSec}s`,
      );
    }

    if (signal?.aborted) {
      const err = new Error("Generation cancelled");
      err.name = "AbortError";
      throw err;
    }

    if (!video || video.status !== "completed") {
      const status = video?.status || "unknown";
      const progress = Number(video?.progress);
      const errorDetail =
        video?.error?.message ||
        video?.error?.code ||
        (typeof video?.error === "string" ? video.error : "");
      const progressText = Number.isFinite(progress)
        ? `, progress=${progress}%`
        : "";
      const errorText = errorDetail ? `, error=${errorDetail}` : "";
      throw new Error(
        `Sora generation failed (status=${status}${progressText}${errorText})`,
      );
    }

    const contentData = await this.callOpenAIVideosContent(
      { id: video.id },
      signal,
    );
    if (!contentData?.base64) {
      throw new Error("Sora download did not return video data");
    }
    const blob = await this.base64ToBlob(
      contentData.base64,
      contentData.mimeType || "video/mp4",
    );

    return {
      videoUrl: URL.createObjectURL(blob),
      mimeType: blob.type || contentData.mimeType || "video/mp4",
      blob,
    };
  }

  async generateVideoFromContext(context, knowledge, signal, promptOverride) {
    if (signal?.aborted) {
      const err = new Error("Generation cancelled");
      err.name = "AbortError";
      throw err;
    }
    const prompt = promptOverride || this.buildVideoPrompt(context, knowledge);
    const videoConfig = knowledge?.videoConfig || {};
    const aspectRatio =
      videoConfig.aspectRatio || knowledge?.aspectRatio || "16:9";
    const resolution = videoConfig.resolution || "720p";
    const rawDuration = videoConfig.durationSeconds || 8;
    const durationSeconds = this.snapVideoSceneDuration(
      rawDuration,
      videoConfig.model || DEFAULT_VIDEO_MODEL,
      resolution,
    );
    const baseNegativePrompt =
      "extra arms, extra hands, extra fingers, duplicated limbs, deformed hands, bad anatomy, distorted body, fused fingers, missing fingers";
    const negativePrompt = [
      baseNegativePrompt,
      videoConfig.negativePrompt || "",
    ]
      .map((entry) => String(entry || "").trim())
      .filter(Boolean)
      .join(", ");
    const modelName = videoConfig.model || DEFAULT_VIDEO_MODEL;
    const parsedModel = this.parseVideoModel(modelName);
    if (parsedModel.provider === "seedance") {
      return this.generateVideoFromSeedance({
        prompt,
        modelId: parsedModel.modelId,
        aspectRatio,
        resolution,
        durationSeconds,
        referenceImages: knowledge?.referenceImages || [],
        signal,
      });
    }
    if (parsedModel.provider === "kling") {
      return this.generateVideoFromKling({
        prompt,
        modelId: parsedModel.modelId,
        aspectRatio,
        durationSeconds,
        mode: videoConfig.klingMode || "pro",
        sound: videoConfig.klingSound || "off",
        signal,
      });
    }
    if (parsedModel.provider === "sora") {
      return this.generateVideoFromSora({
        prompt,
        modelId: parsedModel.modelId,
        aspectRatio,
        resolution,
        durationSeconds,
        signal,
      });
    }
    return this.generateVideoFromVeo({
      prompt,
      modelId: parsedModel.modelId,
      aspectRatio,
      durationSeconds,
      negativePrompt,
      referenceImages: knowledge?.referenceImages || [],
      signal,
    });
  }

  async generateVideoFromVeo({
    prompt,
    modelId,
    aspectRatio,
    durationSeconds,
    negativePrompt,
    referenceImages,
    signal,
  }) {
    if (signal?.aborted) {
      const err = new Error("Generation cancelled");
      err.name = "AbortError";
      throw err;
    }
    const safePrompt = this.truncateText(prompt, 3000);
    const normalizeVeoAspectRatio = (ratio) => {
      const map = {
        "16:9": "16:9",
        "9:16": "9:16",
        "1:1": "1:1",
        "4:3": "4:3",
        "3:4": "3:4",
      };
      return map[String(ratio || "").trim()] || "16:9";
    };

    const generateConfig = {
      numberOfVideos: 1,
      durationSeconds: Number(durationSeconds) || 8,
      aspectRatio: normalizeVeoAspectRatio(aspectRatio),
      ...(negativePrompt ? { negativePrompt } : {}),
    };

    const requestBody = {
      contents: [{ parts: [{ text: safePrompt }] }],
      generationConfig: generateConfig,
    };

    const requestBodies = [];
    const hasRefs =
      Array.isArray(referenceImages) && referenceImages.length > 0;
    if (hasRefs) {
      const refParts = [];
      for (const ref of referenceImages.slice(0, 3)) {
        const dataUrl = ref?.dataUrl || ref?.url || ref?.path || "";
        if (!dataUrl) continue;
        const base64Match = String(dataUrl).match(/^data:([^;]+);base64,(.+)$/);
        if (base64Match) {
          refParts.push({
            inlineData: {
              mimeType: base64Match[1],
              data: base64Match[2],
            },
          });
        }
      }
      if (refParts.length > 0) {
        requestBodies.push({
          withReferences: true,
          body: {
            contents: [{ parts: [...refParts, { text: safePrompt }] }],
            generationConfig: generateConfig,
          },
        });
      }
    }
    requestBodies.push({ withReferences: false, body: requestBody });

    let operation = null;
    let operationError = null;
    for (const request of requestBodies) {
      try {
        operation = await this.callGeminiPredictLongRunning(
          modelId,
          request.body,
          signal,
        );
        break;
      } catch (error) {
        operationError = error;
        const lower = String(error?.message || "").toLowerCase();
        const canRetryWithoutRefs =
          request.withReferences &&
          (this.isGeminiUnknownFieldError(error?.message) ||
            this.isPayloadTooLargeError(error?.message) ||
            lower.includes("referenceimage") ||
            lower.includes("reference image") ||
            lower.includes("invalid") ||
            lower.includes("unsupported"));
        if (canRetryWithoutRefs) {
          continue;
        }
        throw error;
      }
    }
    if (!operation) {
      throw operationError || new Error("Veo generation did not start");
    }
    const operationName = operation?.name;
    if (!operationName) {
      throw new Error("Veo generation did not return an operation name");
    }

    let result = null;
    const maxAttempts = 45;
    const pollStart = Date.now();
    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
      if (signal?.aborted) {
        const err = new Error("Generation cancelled");
        err.name = "AbortError";
        throw err;
      }
      await new Promise((resolve) => setTimeout(resolve, 4000));
      const pollData = await this.callGeminiOperation(operationName, signal);
      if (pollData?.done) {
        if (pollData?.error) {
          const errorMessage =
            pollData.error?.message || JSON.stringify(pollData.error);
          throw new Error(errorMessage || "Veo generation failed");
        }
        result = pollData?.response || pollData?.result || pollData;
        break;
      }
    }

    if (!result) {
      const elapsedSec = Math.round((Date.now() - pollStart) / 1000);
      throw new Error(
        `Veo generation timed out after ${elapsedSec}s (operation: ${operationName})`,
      );
    }

    const videoUri =
      result?.generateVideoResponse?.generatedVideos?.[0]?.video?.uri ||
      result?.generatedVideos?.[0]?.video?.uri ||
      result?.generateVideoResponse?.generatedSamples?.[0]?.video?.uri ||
      result?.generatedSamples?.[0]?.video?.uri ||
      result?.video?.uri ||
      result?.uri;
    if (!videoUri) {
      throw new Error("No video URI returned from Veo");
    }

    const contentData = await this.callAiServer(
      "gemini.fileContent",
      { fileUri: videoUri },
      { signal },
    );
    if (!contentData?.base64) {
      throw new Error("Veo video download failed — no data returned");
    }
    const blob = await this.base64ToBlob(
      contentData.base64,
      contentData.mimeType || "video/mp4",
    );
    return {
      videoUrl: URL.createObjectURL(blob),
      mimeType: blob.type || "video/mp4",
      blob,
    };
  }

  async generateImageFromContext(context, knowledge, signal, promptOverride) {
    const prompt = promptOverride || this.buildImagePrompt(context, knowledge);
    const safePrompt = this.truncateText(prompt, 4000);
    const imageModel = knowledge?.imageModel || DEFAULT_IMAGE_MODEL;
    const aspectRatio =
      knowledge?.aspectRatio || this.getMasterAspectRatio() || "1:1";
    const knowledgeWithReferences = {
      ...knowledge,
      referenceImages: this.collectImageReferenceImages(knowledge),
    };
    if (
      this.shouldUseReferenceAwareImageRenderer(
        imageModel,
        knowledgeWithReferences,
        aspectRatio,
      )
    ) {
      return await this.generateImageFromContextWithGemini(
        safePrompt,
        knowledgeWithReferences,
      );
    }

    if (imageModel.startsWith("gpt")) {
      const size = this.getOpenAISizeForAspect(aspectRatio);
      // Product photos plus any user-pasted style references are the only
      // pixels image generation ever sees. The analyzed source ad stays out —
      // its learnings arrive as text via the concept — so the source's copy
      // and layout can't ghost into the render.
      const productRefs = this.collectImageReferenceImages(knowledge, {
        maxProductImages: 3,
        maxAnalysisImages: 0,
        maxBrandAssets: 0,
        maxStyleImages: 2,
      });
      const referenceImages = await this.buildOpenAIReferenceImages(productRefs);
      const toImageResult = async (base64) => {
        if (!base64) {
          throw new Error("No image data returned from OpenAI");
        }
        return await this.enforceAspectRatioOnDataUrl(
          `data:image/png;base64,${base64}`,
          aspectRatio,
          "image/png",
        );
      };
      try {
        // Regular chat, opened fresh for this one image: the chat model reads
        // the brief, studies the attached product photos, then renders through
        // the image tool. The server forces store:false, so nothing persists
        // between generations.
        const response = await this.callOpenAIResponses(
          {
            model: OPENAI_LATEST_MODEL,
            input: [
              {
                role: "user",
                content: [
                  { type: "input_text", text: safePrompt },
                  ...referenceImages.map((img) => ({
                    type: "input_image",
                    image_url: `data:${img.mimeType || "image/png"};base64,${img.b64}`,
                    detail: "auto",
                  })),
                ],
              },
            ],
            tools: [
              {
                type: "image_generation",
                model: OPENAI_IMAGE_MODEL,
                size,
                quality: "high",
                input_fidelity: "high",
                moderation: "low",
              },
            ],
            tool_choice: { type: "image_generation" },
          },
          signal,
        );
        const imageCall = (response?.output || []).find(
          (item) => item?.type === "image_generation_call" && item?.result,
        );
        return await toImageResult(imageCall?.result || "");
      } catch (gptErr) {
        if (gptErr?.name === "AbortError") throw gptErr;
        // eslint-disable-next-line no-console
        console.warn(
          "Fresh-chat image generation failed, falling back to the images API:",
          gptErr?.message,
        );
        const response = await this.callOpenAIImages(
          {
            model: OPENAI_IMAGE_MODEL,
            prompt: safePrompt,
            size,
            ...(referenceImages.length ? { images: referenceImages } : {}),
          },
          signal,
        );
        return await toImageResult(response?.data?.[0]?.b64_json || "");
      }
    }

    // Non-GPT model selection → Gemini directly
    return await this.generateImageFromContextWithGemini(
      safePrompt,
      knowledge,
      imageModel,
    );
  }

  async generateImageFromContextWithGemini(
    prompt,
    knowledge,
    imageModelOverride,
  ) {
    const imageModel = imageModelOverride || DEFAULT_IMAGE_MODEL;
    const aspectRatio =
      knowledge?.aspectRatio || this.getMasterAspectRatio() || "1:1";
    const creativityValue = Number(knowledge?.creativity);
    const imageTemperature =
      Number.isFinite(creativityValue) && creativityValue > 0
        ? Math.min(1, Math.max(0.15, 0.15 + creativityValue * 0.65))
        : 0.35;
    const referenceImages = this.collectImageReferenceImages(knowledge, {
      maxProductImages: 4,
      maxBrandAssets: 1,
      maxStyleImages: 2,
    });
    const productOnlyReferenceImages = this.collectImageReferenceImages(
      knowledge,
      {
        maxProductImages: 2,
        maxBrandAssets: 0,
      },
    );
    let imageParts = await this.buildGeminiReferenceImageParts(referenceImages);

    const parseGeminiImageResponse = async (data) => {
      const candidates = Array.isArray(data?.candidates) ? data.candidates : [];
      const allParts = candidates.flatMap(
        (candidate) => candidate?.content?.parts || [],
      );

      const filePart = allParts.find(
        (part) => part.fileData?.fileUri || part.file_data?.file_uri,
      );
      if (filePart) {
        const fileUri =
          filePart.fileData?.fileUri || filePart.file_data?.file_uri;
        const mimeType =
          filePart.fileData?.mimeType ||
          filePart.file_data?.mime_type ||
          "image/png";
        return await this.resolveGeminiFileToObjectUrl(
          fileUri,
          mimeType,
          aspectRatio,
        );
      }

      const imagePart = allParts.find(
        (part) => part.inlineData?.data || part.inline_data?.data,
      );
      if (!imagePart) {
        const reasons = candidates
          .map(
            (candidate) => candidate?.finishReason || candidate?.finish_reason,
          )
          .filter(Boolean);
        const reasonLine =
          reasons.length > 0 ? ` (finish reason: ${reasons.join(", ")})` : "";
        throw new Error(`No image data returned from Gemini${reasonLine}`);
      }

      const mimeType =
        imagePart.inlineData?.mimeType ||
        imagePart.inline_data?.mime_type ||
        "image/png";
      const base64 = imagePart.inlineData?.data || imagePart.inline_data?.data;
      const blob = await this.base64ToBlob(base64, mimeType);
      return {
        imageUrl: `data:${mimeType};base64,${base64}`,
        mimeType,
        blob,
      };
    };

    const maxAttempts = 3;
    let lastError = null;
    let retryNote = "";
    let reducedToProductOnly = false;
    let trimmedReferences = false;
    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
      try {
        const attemptPrompt = retryNote
          ? `${prompt}\n\nRetry requirement: ${retryNote}`
          : prompt;
        const data = await this.requestGeminiImageResponse(
          imageModel,
          imageParts,
          attemptPrompt,
          aspectRatio,
          imageTemperature,
        );
        const generated = await parseGeminiImageResponse(data);
        const compositionCheck = await this.validateGeneratedImageComposition(
          generated.imageUrl,
          aspectRatio,
        );
        if (!compositionCheck.ok) {
          if (String(generated.imageUrl || "").startsWith("blob:")) {
            URL.revokeObjectURL(generated.imageUrl);
          }
          throw new Error(
            compositionCheck.reason || "Invalid image composition",
          );
        }
        return generated;
      } catch (error) {
        lastError = error;
        const errorMessage = String(error?.message || "");
        if (
          imageParts.length > 0 &&
          this.isPayloadTooLargeError(errorMessage)
        ) {
          if (
            !reducedToProductOnly &&
            productOnlyReferenceImages.length > 0 &&
            productOnlyReferenceImages.length < referenceImages.length
          ) {
            reducedToProductOnly = true;
            imageParts = await this.buildGeminiReferenceImageParts(
              productOnlyReferenceImages,
              {
                maxImages: 2,
                maxTotalBytes: 750_000,
              },
            );
            retryNote =
              "Regenerate using only the strongest product references. Prioritize exact product identity over extra brand assets.";
            continue;
          }
          if (!trimmedReferences) {
            trimmedReferences = true;
            imageParts = [];
            retryNote =
              "Regenerate without attached reference images, but keep the exact same product silhouette, materials, and branding from the earlier instructions.";
            continue;
          }
          continue;
        }
        if (errorMessage.startsWith("Gemini image generation failed:")) {
          throw error;
        }
        retryNote =
          aspectRatio === "1:1"
            ? "Recompose natively in 1:1. Use full canvas utilization. Keep subject/headline/logo/CTA inside the center column and safe area. Do not create a poster inside a square."
            : `Recompose natively in ${aspectRatio}. Keep all critical elements inside the frame with no bars or clipping.`;
      }
    }

    throw lastError || new Error("No image data returned from Gemini");
  }

  getQcThresholds(qcLevel, mediaType = "image") {
    const isVideo = mediaType === "video";
    if (qcLevel === "strict") {
      return {
        product_similarity: isVideo ? 86 : 88,
        text_legibility: isVideo ? 84 : 90,
        crop_safety: isVideo ? 88 : 90,
        aspect_ratio: 96,
        json_alignment: isVideo ? 84 : 86,
        post_readiness: 90,
        technical_quality: isVideo ? 86 : 84,
      };
    }
    return {
      product_similarity: isVideo ? 60 : 62,
      text_legibility: isVideo ? 58 : 66,
      crop_safety: isVideo ? 64 : 66,
      aspect_ratio: 88,
      json_alignment: isVideo ? 40 : 45,
      post_readiness: 55,
      technical_quality: isVideo ? 60 : 55,
    };
  }

  getQcPassPolicy(qcLevel) {
    if (qcLevel === "strict") {
      return {
        requiredCriteria: [
          "product_similarity",
          "text_legibility",
          "crop_safety",
          "aspect_ratio",
          "json_alignment",
          "post_readiness",
          "technical_quality",
        ],
        minJsonAlignmentFloor: 86,
      };
    }
    return {
      requiredCriteria: [
        "product_similarity",
        "text_legibility",
        "crop_safety",
        "aspect_ratio",
      ],
      minJsonAlignmentFloor: 0,
    };
  }

  buildQualityControlPrompt(
    context,
    qcLevel,
    mediaType,
    thresholds,
    expectedAspectRatio,
  ) {
    const contextJson = JSON.stringify(context || {});
    const isVideo = mediaType === "video";
    return `You are a production quality-control checker for ad creatives.
You must NOT critique style, taste, creativity, branding preference, or virality.
Only check deployment-readiness.

Primary requirement:
- The product shown in the generated creative should closely match product identity from the provided knowledge-base reference images.

Score each criterion from 0-100:
1) product_similarity: similarity to knowledge-base reference product look.
2) text_legibility: visible words are readable and not cut off.
3) crop_safety: critical elements are not clipped (product/logo/main subject/headline/CTA).
4) aspect_ratio: output frame matches required aspect ratio exactly (within minor rendering tolerance).
5) json_alignment: adherence to factual JSON constraints only (product type, core offer/CTA presence, key required claims, required language/format constraints). Do NOT penalize creative variation in tone/style wording if factual intent is still met.
6) post_readiness: ready-to-post without blocking issues.
7) technical_quality: no severe blur/compression/artifacts that block posting.

Hard fail guidance:
- If key text is truncated at any edge, set text_legibility <= 40 and crop_safety <= 40.
- If logo/product/main subject is clipped by the frame edge, set crop_safety <= 40.
- Basic mode should be lenient on copy/style nuance. Only block for clear readiness failures.
- If the image places a smaller wide banner/poster inside a square canvas, set aspect_ratio <= 30.
- If background does not reach all four edges or solid-color bars are present, set aspect_ratio <= 35 and crop_safety <= 35.
${
  isVideo
    ? `
Video-specific checks:
- Ensure actor identity, wardrobe, grooming, and body type stay consistent across frames.
- Penalize visible flicker, stutter, temporal artifacts, or sudden lighting shifts.
- If continuity breaks or the actor changes between frames, reduce post_readiness and technical_quality.
- If motion looks implausible or jittery, reduce technical_quality.
`
    : ""
}

Media type: ${mediaType}
QC mode: ${qcLevel}
Required aspect ratio: ${expectedAspectRatio || "none provided"}
Thresholds (minimum score required):
${JSON.stringify(thresholds)}

Source JSON intent:
${contextJson}

Return ONLY JSON in this exact shape:
{
  "summary": "",
  "issues": [""],
  "scores": {
    "product_similarity": 0,
    "text_legibility": 0,
    "crop_safety": 0,
    "aspect_ratio": 0,
    "json_alignment": 0,
    "post_readiness": 0,
    "technical_quality": 0
  }
}`;
  }

  async loadVideoFromUrl(src) {
    return new Promise((resolve, reject) => {
      const video = document.createElement("video");
      video.preload = "metadata";
      video.onloadedmetadata = () => resolve(video);
      video.onerror = reject;
      video.src = src;
    });
  }

  async measureMediaAspectRatio(mediaUrl, mediaType) {
    if (!mediaUrl) return null;
    if (mediaType === "video") {
      const video = await this.loadVideoFromUrl(mediaUrl);
      const width = Number(video.videoWidth || 0);
      const height = Number(video.videoHeight || 0);
      if (!width || !height) return null;
      return { width, height, ratio: width / height };
    }
    const image = await this.loadImageFromUrl(mediaUrl);
    const width = Number(image.naturalWidth || image.width || 0);
    const height = Number(image.naturalHeight || image.height || 0);
    if (!width || !height) return null;
    return { width, height, ratio: width / height };
  }

  scoreAspectRatio(expectedAspectRatio, measuredAspect) {
    const expected = this.parseAspectRatio(expectedAspectRatio);
    if (!expected) {
      return {
        score: 100,
        issue: "",
      };
    }
    if (!measuredAspect?.ratio) {
      return {
        score: 0,
        issue: "Unable to verify output aspect ratio.",
      };
    }
    const delta = Math.abs(measuredAspect.ratio - expected.ratio);
    let score = 0;
    if (delta <= 0.005) score = 100;
    else if (delta <= 0.01) score = 98;
    else if (delta <= 0.02) score = 95;
    else if (delta <= 0.04) score = 80;
    else if (delta <= 0.08) score = 50;
    else score = 0;
    const issue =
      score < 92
        ? `Aspect ratio mismatch. Expected ${expectedAspectRatio}, got ${measuredAspect.width}x${measuredAspect.height}.`
        : "";
    return { score, issue };
  }

  normalizeQualityControlResult(raw, qcLevel, thresholdsOverride) {
    const thresholds = thresholdsOverride || this.getQcThresholds(qcLevel);
    const policy = this.getQcPassPolicy(qcLevel);
    const issues = Array.isArray(raw?.issues)
      ? raw.issues.map((item) => String(item || "").trim()).filter(Boolean)
      : [];
    const rawScores =
      raw?.scores && typeof raw.scores === "object" ? raw.scores : {};
    const scores = Object.keys(thresholds).reduce((acc, key) => {
      const next = Number(rawScores[key]);
      const clamped = Number.isFinite(next)
        ? Math.max(0, Math.min(100, next))
        : 0;
      acc[key] = clamped;
      return acc;
    }, {});
    const failingCriteria = Object.keys(thresholds).filter(
      (key) => (scores[key] || 0) < thresholds[key],
    );
    const failingRequired = policy.requiredCriteria.filter(
      (key) => (scores[key] || 0) < (thresholds[key] || 0),
    );
    const jsonAlignmentScore = scores.json_alignment || 0;
    const jsonFloorFailed = jsonAlignmentScore < policy.minJsonAlignmentFloor;
    const pass = failingRequired.length === 0 && !jsonFloorFailed;
    return {
      pass,
      summary: String(raw?.summary || "").trim(),
      issues,
      scores,
      thresholds,
      failingCriteria,
      failingRequired,
      jsonFloorFailed,
    };
  }

  async buildGeminiInlinePartsFromImages(images = []) {
    const limited = this.dedupeReferenceImages(images).slice(0, 8);
    const parts = await Promise.all(
      limited.map(async (img) => {
        if (img?.dataUrl) {
          const match = String(img.dataUrl).match(/^data:(.+);base64,(.*)$/);
          if (!match) return null;
          return {
            inline_data: {
              mime_type: match[1],
              data: match[2],
            },
          };
        }
        if (img?.blob) {
          const base64 = await this.blobToBase64(img.blob);
          return {
            inline_data: {
              mime_type: img.mimeType || img.blob.type || "image/png",
              data: base64,
            },
          };
        }
        if (img?.url) {
          const parsed = this.parseDataUrl(img.url);
          if (parsed?.base64) {
            return {
              inline_data: {
                mime_type: parsed.mimeType || "image/png",
                data: parsed.base64,
              },
            };
          }
          const cacheKey = img?.path || img.url;
          if (this.geminiFileCache.has(cacheKey)) {
            const cached = this.geminiFileCache.get(cacheKey);
            if (cached?.uri) {
              return {
                file_data: {
                  mime_type: cached.mimeType || "image/jpeg",
                  file_uri: cached.uri,
                },
              };
            }
          }
          try {
            const uploaded = await this.uploadGeminiUrl(
              img.url,
              img.mimeType || "image/jpeg",
              img.name || "reference-image",
            );
            if (uploaded?.uri) {
              this.geminiFileCache.set(cacheKey, uploaded);
              return {
                file_data: {
                  mime_type: uploaded.mimeType || "image/jpeg",
                  file_uri: uploaded.uri,
                },
              };
            }
            return null;
          } catch (error) {
            return null;
          }
        }
        return null;
      }),
    );
    return parts.filter(Boolean);
  }

  async runCreativeQualityControl({
    mediaUrl,
    mediaType = "image",
    mimeType,
    context,
    qcLevel = "off",
    expectedAspectRatio,
    referenceImages = [],
  }) {
    if (qcLevel === "off") {
      return {
        pass: true,
        summary: "QC skipped",
        issues: [],
        scores: {},
        thresholds: {},
        failingCriteria: [],
      };
    }
    if (!mediaUrl) {
      throw new Error("Media URL missing for quality control");
    }
    const baseThresholds = this.getQcThresholds(qcLevel, mediaType);
    const hasProductReferences =
      Array.isArray(referenceImages) && referenceImages.length > 0;
    let thresholds = {
      ...baseThresholds,
      product_similarity: hasProductReferences
        ? baseThresholds.product_similarity
        : 0,
    };
    if (qcLevel === "basic") {
      thresholds = {
        ...thresholds,
        text_legibility: 0,
        json_alignment: 0,
        post_readiness: 0,
        technical_quality: 0,
      };
    }
    const shouldCheckAspect = Boolean(
      this.parseAspectRatio(expectedAspectRatio),
    );
    const preMeasuredAspect = shouldCheckAspect
      ? await this.measureMediaAspectRatio(mediaUrl, mediaType)
      : null;
    const preRatioScore = shouldCheckAspect
      ? this.scoreAspectRatio(expectedAspectRatio, preMeasuredAspect)
      : { score: 100, issue: "" };
    if (qcLevel === "basic" && thresholds.product_similarity === 0) {
      const scores = Object.keys(thresholds).reduce((acc, key) => {
        acc[key] = 100;
        return acc;
      }, {});
      const issues = [];
      if (shouldCheckAspect) {
        scores.aspect_ratio = preRatioScore.score;
        if (preRatioScore.issue) {
          issues.push(preRatioScore.issue);
        }
      }
      if (mediaType === "image" && shouldCheckAspect) {
        const letterboxCheck = await this.detectLetterboxing(mediaUrl);
        if (letterboxCheck.letterboxDetected) {
          scores.aspect_ratio = Math.min(scores.aspect_ratio || 100, 25);
          scores.crop_safety = Math.min(scores.crop_safety || 100, 35);
          issues.push(
            "Canvas utilization failed: detected edge bars / poster-in-frame composition.",
          );
        }
      }
      const summary = issues[0] || "Basic QC passed.";
      return this.normalizeQualityControlResult(
        {
          summary,
          issues,
          scores,
        },
        qcLevel,
        thresholds,
      );
    }
    if (shouldCheckAspect && preRatioScore.score < thresholds.aspect_ratio) {
      const scores = Object.keys(thresholds).reduce((acc, key) => {
        acc[key] = key === "aspect_ratio" ? preRatioScore.score : 100;
        return acc;
      }, {});
      const issue = preRatioScore.issue || "Aspect ratio mismatch.";
      return this.normalizeQualityControlResult(
        {
          summary: issue,
          issues: [issue],
          scores,
        },
        qcLevel,
        thresholds,
      );
    }
    if (mediaType === "image" && shouldCheckAspect) {
      const letterboxCheck = await this.detectLetterboxing(mediaUrl);
      if (letterboxCheck.letterboxDetected) {
        const scores = Object.keys(thresholds).reduce((acc, key) => {
          acc[key] = 100;
          return acc;
        }, {});
        scores.aspect_ratio = Math.min(scores.aspect_ratio || 100, 25);
        scores.crop_safety = Math.min(scores.crop_safety || 100, 35);
        const issue =
          "Canvas utilization failed: detected edge bars / poster-in-frame composition.";
        return this.normalizeQualityControlResult(
          {
            summary: issue,
            issues: [issue],
            scores,
          },
          qcLevel,
          thresholds,
        );
      }
    }

    const referenceParts =
      await this.buildGeminiInlinePartsFromImages(referenceImages);
    thresholds = {
      ...thresholds,
      product_similarity:
        referenceParts.length > 0 ? baseThresholds.product_similarity : 0,
    };
    const prompt = this.buildQualityControlPrompt(
      context,
      qcLevel,
      mediaType,
      thresholds,
      expectedAspectRatio,
    );
    const qcModel = this.getQcModel(qcLevel);
    const fallbackQcModel = GEMINI_VISION_MODEL;
    const referencePromptPrefix =
      referenceParts.length > 0
        ? "Reference images from knowledge base are provided first; use them for product similarity scoring."
        : "No reference images were provided; score product similarity using available context only.";

    if (mediaType === "video") {
      const { blob, mimeType: resolvedMime } =
        await this.fetchMediaBlob(mediaUrl);
      const file = new File([blob], "creative-qc.mp4", {
        type: mimeType || resolvedMime || blob.type || "video/mp4",
      });
      const videoParts = await this.getGeminiVideoParts(file);
      let parsed;
      try {
        parsed = await this.requestGeminiVideoAnalysis(
          [...referenceParts, ...videoParts.parts],
          `${referencePromptPrefix}\n${prompt}`,
          qcModel,
          undefined,
          true,
        );
      } catch (error) {
        if (qcModel !== fallbackQcModel) {
          parsed = await this.requestGeminiVideoAnalysis(
            [...referenceParts, ...videoParts.parts],
            `${referencePromptPrefix}\n${prompt}`,
            fallbackQcModel,
            undefined,
            true,
          );
        } else {
          throw error;
        }
      }
      const measuredAspect =
        preMeasuredAspect ||
        (await this.measureMediaAspectRatio(mediaUrl, "video"));
      const ratioScore = shouldCheckAspect
        ? preRatioScore
        : this.scoreAspectRatio(expectedAspectRatio, measuredAspect);
      parsed.scores = {
        ...(parsed.scores || {}),
        aspect_ratio: ratioScore.score,
      };
      if (ratioScore.issue) {
        parsed.issues = [...(parsed.issues || []), ratioScore.issue];
      }
      return this.normalizeQualityControlResult(parsed, qcLevel, thresholds);
    }

    const { blob } = await this.fetchMediaBlob(mediaUrl);
    const { base64, mimeType: imageMime } = await this.prepareQcImage(
      blob,
      qcLevel,
    );
    const runQc = async (modelName) => {
      const qcData = await this.callGeminiGenerate(modelName, {
        contents: [
          {
            parts: [
              ...referenceParts,
              {
                inline_data: {
                  mime_type: imageMime,
                  data: base64,
                },
              },
              {
                text: `${referencePromptPrefix}\n${prompt}\nReturn ONLY JSON.`,
              },
            ],
          },
        ],
      });
      const qcText = qcData?.candidates?.[0]?.content?.parts
        ?.map((part) => part.text)
        .filter(Boolean)
        .join("\n");
      return this.extractJSON(qcText);
    };
    let parsed;
    try {
      parsed = await runQc(qcModel);
    } catch (error) {
      if (qcModel !== fallbackQcModel) {
        parsed = await runQc(fallbackQcModel);
      } else {
        throw error;
      }
    }
    const measuredAspect =
      preMeasuredAspect ||
      (await this.measureMediaAspectRatio(mediaUrl, "image"));
    const ratioScore = shouldCheckAspect
      ? preRatioScore
      : this.scoreAspectRatio(expectedAspectRatio, measuredAspect);
    parsed.scores = {
      ...(parsed.scores || {}),
      aspect_ratio: ratioScore.score,
    };
    if (ratioScore.issue) {
      parsed.issues = [...(parsed.issues || []), ratioScore.issue];
    }
    return this.normalizeQualityControlResult(parsed, qcLevel, thresholds);
  }

  async analyzeImageWithGPT(file, signal, modelName) {
    const gptModel =
      modelName && String(modelName).startsWith("gpt")
        ? modelName
        : OPENAI_LATEST_MODEL;
    try {
      const maxBytes = 1_800_000;
      const attempts = [
        { maxDim: 1280, quality: 0.78 },
        { maxDim: 1024, quality: 0.72 },
        { maxDim: 768, quality: 0.66 },
        { maxDim: 512, quality: 0.6 },
        { maxDim: 384, quality: 0.55 },
      ];
      let prepared = null;
      for (const attempt of attempts) {
        const candidate = await this.prepareOpenAIImage(file, attempt);
        if (!candidate?.base64) continue;
        prepared = candidate;
        const bytes = this.estimateBase64Bytes(candidate.base64);
        if (bytes <= maxBytes) break;
      }
      const base64 = prepared?.base64 || (await this.fileToBase64(file));
      const mimeType = prepared?.mimeType || file.type || "image/jpeg";
      const prompt = `You are an expert creative strategist. Analyze this image ad in extreme detail.
Provide a comprehensive JSON analysis including a deep psychological "why_it_works" section.
Be highly specific about emotions, camera lens, distance, mood, vibe, and subtle details.
Avoid nulls. Use empty strings/arrays if needed. Return ONLY JSON.
${this.buildAiGuidance()}${this.buildAnalysisGuidance()}

Structure:
{
  "why_it_works": "2-3 rich paragraphs on psychological triggers, persuasion, and attention mechanics",
  "emotional_response": {
    "primary_emotion": "",
    "secondary_emotions": [],
    "tension_level": "",
    "energy_level": "",
    "mood": "",
    "vibe_keywords": []
  },
  "visual_elements": {
    "primary_hook": "",
    "color_palette": [],
    "composition": "",
    "key_visuals": [],
    "text_overlays": "",
    "texture_and_materials": "",
    "visual_metaphors": []
  },
  "visual_dynamics": {
    "camera_lens": "",
    "camera_distance": "",
    "camera_angle": "",
    "depth_of_field": "",
    "focal_points": [],
    "lighting_style": "",
    "lighting_direction": "",
    "contrast_level": "",
    "motion_cues": ""
  },
  "environment": {
    "setting": "",
    "time_of_day": "",
    "background_elements": [],
    "foreground_elements": [],
    "weather_or_atmosphere": ""
  },
  "design_details": {
    "typography_style": "",
    "hierarchy": "",
    "alignment": "",
    "whitespace": "",
    "iconography": ""
  },
  "messaging": {
    "value_proposition": "",
    "emotional_tone": "",
    "cta": "",
    "copy_style": "",
    "key_claims": []
  },
  "brand_details": {
    "logo_placement": "",
    "brand_colors_used": [],
    "product_positioning": "",
    "packaging_visibility": "",
    "trust_signals": []
  },
  "target_audience": {
    "demographics": "",
    "psychographics": "",
    "pain_points": [],
    "aspirations": []
  },
  "performance_indicators": {
    "strengths": [],
    "weaknesses": [],
    "scroll_stopping_factors": [],
    "recommendations": []
  },
  "micro_details": {
    "subtle_elements": [],
    "quality_cues": [],
    "social_proof_signals": []
  }
}
Return ONLY the JSON object.`;

      if (signal?.aborted) {
        throw new Error("Analysis cancelled");
      }

      const response = await this.callOpenAIChat(
        {
          model: gptModel,
          messages: [
            {
              role: "user",
              content: [
                {
                  type: "image_url",
                  image_url: {
                    url: `data:${mimeType};base64,${base64}`,
                    detail: "high",
                  },
                },
                { type: "text", text: prompt },
              ],
            },
          ],
          max_completion_tokens: 3000,
        },
        signal,
      );

      const content = this.getOpenAIChatContentCandidatesOrThrow(response);
      return this.extractJSONFromCandidates(content);
    } catch (error) {
      throw new Error(`GPT analysis failed: ${error.message}`);
    }
  }

  async analyzeImageWithGemini(file, signal, modelName = GEMINI_VISION_MODEL) {
    try {
      const mimeType = file.type || "image/jpeg";
      const uploaded = await this.uploadGeminiFile(file, mimeType, signal);
      await this.waitForGeminiFileActive(uploaded.name, signal);

      const prompt = `You are an expert creative strategist. Analyze this image ad in extreme detail.
Provide a comprehensive JSON analysis including a deep psychological "why_it_works" section.
Be highly specific about emotions, camera lens, distance, mood, vibe, and subtle details.
Avoid nulls. Use empty strings/arrays if needed. Return ONLY JSON.
${this.buildAiGuidance()}${this.buildAnalysisGuidance()}

Structure:
{
  "why_it_works": "2-3 rich paragraphs on psychological triggers, persuasion, and attention mechanics",
  "emotional_response": {
    "primary_emotion": "",
    "secondary_emotions": [],
    "tension_level": "",
    "energy_level": "",
    "mood": "",
    "vibe_keywords": []
  },
  "visual_elements": {
    "primary_hook": "",
    "color_palette": [],
    "composition": "",
    "key_visuals": [],
    "text_overlays": "",
    "texture_and_materials": "",
    "visual_metaphors": []
  },
  "visual_dynamics": {
    "camera_lens": "",
    "camera_distance": "",
    "camera_angle": "",
    "depth_of_field": "",
    "focal_points": [],
    "lighting_style": "",
    "lighting_direction": "",
    "contrast_level": "",
    "motion_cues": ""
  },
  "environment": {
    "setting": "",
    "time_of_day": "",
    "background_elements": [],
    "foreground_elements": [],
    "weather_or_atmosphere": ""
  },
  "design_details": {
    "typography_style": "",
    "hierarchy": "",
    "alignment": "",
    "whitespace": "",
    "iconography": ""
  },
  "messaging": {
    "value_proposition": "",
    "emotional_tone": "",
    "cta": "",
    "copy_style": "",
    "key_claims": []
  },
  "brand_details": {
    "logo_placement": "",
    "brand_colors_used": [],
    "product_positioning": "",
    "packaging_visibility": "",
    "trust_signals": []
  },
  "target_audience": {
    "demographics": "",
    "psychographics": "",
    "pain_points": [],
    "aspirations": []
  },
  "performance_indicators": {
    "strengths": [],
    "weaknesses": [],
    "scroll_stopping_factors": [],
    "recommendations": []
  },
  "micro_details": {
    "subtle_elements": [],
    "quality_cues": [],
    "social_proof_signals": []
  }
}
Return ONLY the JSON object.`;

      const data = await this.callGeminiGenerate(
        modelName,
        {
          contents: [
            {
              parts: [
                {
                  file_data: {
                    mime_type: uploaded.mimeType || mimeType,
                    file_uri: uploaded.uri,
                  },
                },
                { text: prompt },
              ],
            },
          ],
        },
        signal,
      );
      const text = data?.candidates?.[0]?.content?.parts
        ?.map((part) => part.text)
        .filter(Boolean)
        .join("\n");

      return this.extractJSON(text);
    } catch (error) {
      throw new Error(`Gemini analysis failed: ${error.message}`);
    }
  }

  async reviewCreativeScore(file, signal) {
    const prompt = `You are a world-class performance creative director who has reviewed thousands of paid social ads.
Score this creative and provide precise improvement guidance. Return ONLY valid JSON with no markdown.
${this.buildAiGuidance()}${this.buildAnalysisGuidance()}

{
  "score": <number 1-10, one decimal place>,
  "assessment": "<2-3 sentence honest overall assessment of this creative's effectiveness>",
  "strengths": ["<specific strength>", "<specific strength>"],
  "suggestions": [
    {
      "area": "<what to change — e.g. 'Headline copy', 'Background contrast', 'CTA button'>",
      "issue": "<exactly what is weak or missing and why it hurts performance>",
      "fix": "<concrete, actionable instruction a designer can execute immediately>"
    }
  ],
  "ten_out_of_ten": "<A precise creative brief describing what the 10/10 version looks like — describe the visual composition, copy direction, emotional hook, and specific design changes that would maximise scroll-stop power and conversion. Be specific enough that a designer could rebuild it from your description alone.>"
}

Rules:
- Score honestly. 7+ means it's genuinely good. Most ads score 4-7. 9+ is exceptional.
- suggestions: minimum 3, maximum 6. Each must be specific to THIS creative, not generic advice.
- ten_out_of_ten: 3-5 sentences. Be specific about colors, copy, layout, imagery, emotion. No platitudes.
Return ONLY the JSON object.`;

    const tryOpenAI = async () => {
      const maxBytes = 1_800_000;
      const sizingAttempts = [
        { maxDim: 1280, quality: 0.78 },
        { maxDim: 1024, quality: 0.72 },
        { maxDim: 768, quality: 0.66 },
      ];
      let prepared = null;
      for (const attempt of sizingAttempts) {
        const candidate = await this.prepareOpenAIImage(file, attempt);
        if (!candidate?.base64) continue;
        prepared = candidate;
        if (this.estimateBase64Bytes(candidate.base64) <= maxBytes) break;
      }
      const base64 = prepared?.base64 || (await this.fileToBase64(file));
      const mimeType = prepared?.mimeType || file.type || "image/jpeg";
      if (signal?.aborted) throw new Error("Review cancelled");
      const response = await this.callOpenAIChat(
        {
          model: OPENAI_LATEST_MODEL,
          messages: [
            {
              role: "user",
              content: [
                { type: "image_url", image_url: { url: `data:${mimeType};base64,${base64}`, detail: "high" } },
                { type: "text", text: prompt },
              ],
            },
          ],
        },
        signal,
      );
      const candidates = this.getOpenAIChatContentCandidatesOrThrow(response);
      return this.extractJSON(candidates[0] || "");
    };

    // Fall back to Claude
    const tryClaude = async () => {
      const base64 = await this.fileToBase64(file);
      if (signal?.aborted) throw new Error("Review cancelled");
      const text = await this.callClaude({
        model: CLAUDE_DEFAULT_MODEL,
        max_tokens: 1200,
        temperature: 0.3,
        messages: [
          {
            role: "user",
            content: [
              { type: "image", source: { type: "base64", media_type: file.type || "image/jpeg", data: base64 } },
              { type: "text", text: prompt },
            ],
          },
        ],
      });
      return this.extractJSON(text);
    };

    try {
      return await tryClaude();
    } catch (err) {
      if (err?.name === "AbortError") throw err;
      return await tryOpenAI();
    }
  }

  /**
   * Score a freshly GENERATED creative (image or the video's poster frame) with
   * the creative-director scorer and persist the result to creative_analyses so
   * scores survive reload and can rank variants. Returns the review object
   * ({ score, assessment, strengths, suggestions, ten_out_of_ten }) or null.
   * Best-effort: swallows failures so it never blocks the generation UI.
   */
  async scoreGeneratedCreative({ blob, dataUrl, mediaType = "image", signal } = {}) {
    try {
      let file = blob || null;
      if (!file && dataUrl) {
        const resp = await fetch(dataUrl);
        file = await resp.blob();
      }
      if (!file) return null;
      const review = await this.reviewCreativeScore(file, signal);
      const score = Number(review?.score);
      if (!Number.isFinite(score)) return review || null;

      if (this.workspaceId) {
        const contentHash = await hashFile(file);
        if (contentHash) {
          await saveCreativeScore({
            workspaceId: this.workspaceId,
            contentHash,
            model: CLAUDE_DEFAULT_MODEL,
            mediaKind: mediaType === "video" ? "video" : "image",
            score,
            detail: { ...review, model: CLAUDE_DEFAULT_MODEL },
          });
        }
      }
      return review;
    } catch (error) {
      if (error?.name === "AbortError") return null;
      return null;
    }
  }

  /* ------------------------------------------------------------------ */
  /*  Script extraction + variation brief (The Web actions)             */
  /* ------------------------------------------------------------------ */

  normalizeScript(parsed) {
    const p = parsed && typeof parsed === "object" ? parsed : {};
    const scenes = Array.isArray(p.scenes)
      ? p.scenes.slice(0, 24).map((s) => ({
          timeLabel: String(s?.timeLabel || s?.time || ""),
          visual: String(s?.visual || ""),
          spoken: String(s?.spoken || ""),
          onScreen: String(s?.onScreen || s?.on_screen || ""),
        }))
      : [];
    return {
      transcript: typeof p.transcript === "string" ? p.transcript.trim() : "",
      onScreenText: Array.isArray(p.onScreenText)
        ? p.onScreenText.filter(Boolean).map(String)
        : [],
      hook: typeof p.hook === "string" ? p.hook.trim() : "",
      scenes,
    };
  }

  /**
   * extractScript — pull the delivered script from a creative: spoken
   * voiceover (verbatim), on-screen text, the opening hook, and a beat-by-beat
   * scene breakdown. Video uses Gemini multimodal; images (and video fallback)
   * read on-screen copy via a vision model. Requires the raw bytes (Blob).
   */
  async extractScript(file, { isVideo = false, signal } = {}) {
    if (!(file instanceof Blob)) {
      return { transcript: "", onScreenText: [], hook: "", scenes: [] };
    }
    const schemaHint = `Return ONLY valid JSON (no markdown, no commentary) in this exact shape:
{
  "transcript": "<full spoken words / voiceover, verbatim, in order. Empty string if the ad is silent.>",
  "onScreenText": ["<each distinct on-screen text or caption, in order>"],
  "hook": "<the opening line or first on-screen text — the scroll-stopper>",
  "scenes": [
    { "timeLabel": "<e.g. '0-3s' or 'opening'>", "visual": "<what is shown>", "spoken": "<words in this beat>", "onScreen": "<text overlay in this beat>" }
  ]
}`;

    if (isVideo) {
      try {
        const prompt = `You are a transcription + creative-analysis engine. Watch this ad video and extract its script exactly as delivered — spoken words verbatim, all on-screen text, and a beat-by-beat scene breakdown.\n\n${schemaHint}`;
        const videoParts = await this.getGeminiVideoParts(file, signal);
        const parsed = await this.requestGeminiVideoAnalysis(
          videoParts.parts,
          prompt,
          GEMINI_VISION_MODEL,
          signal,
          true,
        );
        return this.normalizeScript(parsed);
      } catch (error) {
        if (signal?.aborted || error?.name === "AbortError") throw error;
        // Fall through to still-frame read below.
      }
    }

    const prompt = `You are a creative analyst. Read every piece of text in this ad and infer its script/messaging.\n\n${schemaHint}`;
    try {
      const base64 = await this.fileToBase64(file);
      const mimeType = file.type || "image/jpeg";
      try {
        const text = await this.callClaude({
          model: CLAUDE_DEFAULT_MODEL,
          max_tokens: 1200,
          temperature: 0.2,
          messages: [
            {
              role: "user",
              content: [
                { type: "image", source: { type: "base64", media_type: mimeType, data: base64 } },
                { type: "text", text: prompt },
              ],
            },
          ],
        });
        return this.normalizeScript(this.extractJSON(text));
      } catch (err) {
        if (err?.name === "AbortError") throw err;
        const response = await this.callOpenAIChat(
          {
            model: OPENAI_LATEST_MODEL,
            messages: [
              {
                role: "user",
                content: [
                  { type: "image_url", image_url: { url: `data:${mimeType};base64,${base64}`, detail: "high" } },
                  { type: "text", text: prompt },
                ],
              },
            ],
          },
          signal,
        );
        const candidates = this.getOpenAIChatContentCandidatesOrThrow(response);
        return this.normalizeScript(this.extractJSON(candidates[0] || ""));
      }
    } catch (error) {
      if (signal?.aborted || error?.name === "AbortError") throw error;
      return { transcript: "", onScreenText: [], hook: "", scenes: [] };
    }
  }

  buildRetentionPromptBlock(retention) {
    if (!retention) {
      return "No watch-through data is available for this creative (static image or retention not tracked). Base drop-off reasoning on the hook and pacing of the script instead.";
    }
    const curve = (retention.curve || [])
      .map((p) => `  ${p.label}: ${Math.round((p.pct || 0) * 100)}% of viewers still watching`)
      .join("\n");
    const drop = retention.dropOff
      ? `Sharpest drop-off: between ${retention.dropOff.from} and ${retention.dropOff.to}, losing ${Math.round(
          (retention.dropOff.dropPct || 0) * 100,
        )}% of viewers.`
      : "";
    return [
      `Video plays: ${retention.plays ?? "n/a"}`,
      retention.avgWatchSec ? `Avg watch time: ${retention.avgWatchSec}s` : "",
      retention.completionRate != null
        ? `Completion rate: ${Math.round(retention.completionRate * 100)}%`
        : "",
      "Retention curve:",
      curve,
      drop,
    ]
      .filter(Boolean)
      .join("\n");
  }

  scriptToText(script) {
    if (!script) return "";
    const parts = [];
    if (script.hook) parts.push(`HOOK: ${script.hook}`);
    if (script.transcript) parts.push(`VOICEOVER / SPOKEN:\n${script.transcript}`);
    if (Array.isArray(script.onScreenText) && script.onScreenText.length) {
      parts.push(`ON-SCREEN TEXT:\n- ${script.onScreenText.join("\n- ")}`);
    }
    if (Array.isArray(script.scenes) && script.scenes.length) {
      const beats = script.scenes
        .map(
          (s, i) =>
            `${i + 1}. [${s.timeLabel || `beat ${i + 1}`}] ${[s.visual, s.spoken && `“${s.spoken}”`, s.onScreen && `(text: ${s.onScreen})`]
              .filter(Boolean)
              .join(" — ")}`,
        )
        .join("\n");
      parts.push(`SCENE BREAKDOWN:\n${beats}`);
    }
    return parts.join("\n\n");
  }

  /**
   * generateVariationBrief — the brains behind "Generate variation brief".
   * Given a creative's script + retention/drop-off + performance, it: analyzes
   * where and why viewers dropped, rewrites the script to fix those beats, and
   * emits a full brief. KB chunks are injected so the model can cite the
   * workspace's own learnings. Returns a structured object; render markdown
   * with buildBriefMarkdown.
   */
  async generateVariationBrief({ node = {}, script = null, retention = null, model, signal } = {}) {
    const modelName =
      String(model || "").trim() || DEFAULT_ANALYSIS_MODEL || GEMINI_VISION_MODEL;
    const kb = this.workspaceId ? await this.loadActiveKnowledge().catch(() => []) : [];
    const knowledgeBlock = this.buildKnowledgeContext({
      topic: `${node.label || ""} ${node.angle || ""} ${node.format || ""}`,
      maxTokens: 4000,
    });

    const perf = [
      node.spend != null ? `Spend: $${Number(node.spend).toLocaleString()}` : "",
      node.roas != null ? `ROAS: ${Number(node.roas).toFixed(2)}` : "",
      node.ctr != null ? `CTR: ${Number(node.ctr).toFixed(2)}%` : "",
      node.hookRate != null ? `Hook rate: ${Math.round((node.hookRate || 0) * 100)}%` : "",
    ]
      .filter(Boolean)
      .join(" · ");

    const scriptText = this.scriptToText(script) || node.transcript || "(script unavailable — infer from performance and format)";

    const prompt = `You are a senior direct-response creative strategist writing a variation brief for a new ad based on an existing one.

CREATIVE: ${node.label || "Untitled"}
FORMAT: ${(node.format || "unknown").toString().replace(/_/g, " ")} · FUNNEL: ${node.funnel || "untagged"} · ANGLE: ${node.angle || "untagged"}
PERFORMANCE: ${perf || "n/a"}

CURRENT SCRIPT:
${scriptText}

WATCH-THROUGH / DROP-OFF:
${this.buildRetentionPromptBlock(retention)}
${knowledgeBlock}

TASK:
1. Diagnose exactly where and why viewers drop off, tying each drop to a specific beat/line of the current script.
2. Identify what is already working and must be preserved.
3. Rewrite the script to fix the drop-off points — tighten or replace the beats that lose viewers, strengthen the hook if early retention is weak. Keep the winning elements.
4. Propose alternative hooks and list elements to avoid.
When you use a knowledge source, cite it and add it to "citations".

Return ONLY valid JSON (no markdown) in this exact shape:
{
  "thesis": "<one-sentence single idea the variation should express>",
  "whatWorked": ["<element to preserve>", "..."],
  "dropOffDiagnosis": "<2-4 sentences: where viewers left and the likely reason, referencing the retention curve>",
  "dropPoints": [ { "at": "<e.g. 25%>", "reason": "<why they left here>", "fix": "<what to change>" } ],
  "scriptAdjustments": ["<specific, actionable change>", "..."],
  "adjustedScript": "<the full rewritten script — hook, voiceover, on-screen text, beat by beat — with the drop-off fixes applied>",
  "hookOptions": ["<hook 1>", "<hook 2>", "<hook 3>"],
  "bannedElements": ["<element to avoid>", "..."],
  "citations": [ { "source": "<title>", "locator": "<locator>", "claim": "<what it supports>" } ]
}`;

    const raw = await this.generateTextWithModel(modelName, prompt, {
      temperature: 0.4,
      maxTokens: 2200,
      signal,
    });
    const parsed = this.extractJSON(raw);
    const arr = (v) => (Array.isArray(v) ? v.filter(Boolean).map(String) : []);
    const brief = {
      thesis: String(parsed?.thesis || ""),
      whatWorked: arr(parsed?.whatWorked),
      dropOffDiagnosis: String(parsed?.dropOffDiagnosis || ""),
      dropPoints: Array.isArray(parsed?.dropPoints)
        ? parsed.dropPoints.map((d) => ({
            at: String(d?.at || ""),
            reason: String(d?.reason || ""),
            fix: String(d?.fix || ""),
          }))
        : [],
      scriptAdjustments: arr(parsed?.scriptAdjustments),
      adjustedScript: String(parsed?.adjustedScript || ""),
      hookOptions: arr(parsed?.hookOptions),
      bannedElements: arr(parsed?.bannedElements),
      citations: Array.isArray(parsed?.citations)
        ? parsed.citations.map((c) => ({
            source: String(c?.source || ""),
            locator: String(c?.locator || ""),
            claim: String(c?.claim || ""),
          }))
        : [],
      model: modelName,
      usedKnowledge: kb.length,
    };
    return brief;
  }

  async analyzeImageWithClaude(file, signal, modelName) {
    try {
      const base64 = await this.fileToBase64(file);
      const prompt = `You are an expert creative strategist. Analyze this image ad in extreme detail.
Provide a comprehensive JSON analysis including a deep psychological "why_it_works" section.
Be highly specific about emotions, camera lens, distance, mood, vibe, and subtle details.
Avoid nulls. Use empty strings/arrays if needed. Return ONLY JSON.
${this.buildAiGuidance()}${this.buildAnalysisGuidance()}

Structure:
{
  "why_it_works": "2-3 rich paragraphs on psychological triggers, persuasion, and attention mechanics",
  "emotional_response": {
    "primary_emotion": "",
    "secondary_emotions": [],
    "tension_level": "",
    "energy_level": "",
    "mood": "",
    "vibe_keywords": []
  },
  "visual_elements": {
    "primary_hook": "",
    "color_palette": [],
    "composition": "",
    "key_visuals": [],
    "text_overlays": "",
    "texture_and_materials": "",
    "visual_metaphors": []
  },
  "visual_dynamics": {
    "camera_lens": "",
    "camera_distance": "",
    "camera_angle": "",
    "depth_of_field": "",
    "focal_points": [],
    "lighting_style": "",
    "lighting_direction": "",
    "contrast_level": "",
    "motion_cues": ""
  },
  "environment": {
    "setting": "",
    "time_of_day": "",
    "background_elements": [],
    "foreground_elements": [],
    "weather_or_atmosphere": ""
  },
  "design_details": {
    "typography_style": "",
    "hierarchy": "",
    "alignment": "",
    "whitespace": "",
    "iconography": ""
  },
  "messaging": {
    "value_proposition": "",
    "emotional_tone": "",
    "cta": "",
    "copy_style": "",
    "key_claims": []
  },
  "brand_details": {
    "logo_placement": "",
    "brand_colors_used": [],
    "product_positioning": "",
    "packaging_visibility": "",
    "trust_signals": []
  },
  "target_audience": {
    "demographics": "",
    "psychographics": "",
    "pain_points": [],
    "aspirations": []
  },
  "performance_indicators": {
    "strengths": [],
    "weaknesses": [],
    "scroll_stopping_factors": [],
    "recommendations": []
  },
  "micro_details": {
    "subtle_elements": [],
    "quality_cues": [],
    "social_proof_signals": []
  }
}
Return ONLY the JSON object.`;

      if (signal?.aborted) {
        throw new Error("Analysis cancelled");
      }

      const text = await this.callClaude({
        model: modelName,
        // The full analysis schema (11 sections + multi-paragraph why_it_works)
        // overran 3000 tokens and truncated the JSON ("Incomplete JSON"). 8000
        // gives headroom while staying under the non-streaming HTTP timeout.
        max_tokens: 8000,
        temperature: 0.2,
        messages: [
          {
            role: "user",
            content: [
              {
                type: "image",
                source: {
                  type: "base64",
                  media_type: file.type || "image/jpeg",
                  data: base64,
                },
              },
              { type: "text", text: prompt },
            ],
          },
        ],
      });

      return this.extractJSON(text);
    } catch (error) {
      throw new Error(`Claude analysis failed: ${error.message}`);
    }
  }

  async refineImagePromptWithClaude(prompt, modelName) {
    const text = await this.callClaude({
      model: modelName,
      max_tokens: 800,
      temperature: 0.2,
      messages: [
        {
          role: "user",
          content: [
            {
              type: "text",
              text:
                "Rewrite the following image prompt to be clearer and more direct. " +
                "Preserve all constraints. Return ONLY the rewritten prompt.",
            },
            { type: "text", text: prompt },
          ],
        },
      ],
    });

    return (text || "").trim() || prompt;
  }
}
